import { regrowStage } from '../shared/dig'
import { FILL_TICKS } from '../shared/types'
import { Fish, GameState, Monk, orRamasse, Runner } from '../shared/state'
import { BREATH_TICKS, COLS, ItemKind, REGROW_TICKS, ROWS, SUB, T, colOf, rowOf } from '../shared/types'
import { isSolid } from '../shared/physics'
import { peindreCascade } from './cascade-papier'
import { DASH_CD_TICKS, GEANT_TICKS, MAILLET_CD, MOMIE_COUPS, PATATE_JIT, PATATE_MIN, estImmerge } from '../shared/sim'
const FANTOME_FONDU = 15 // la demi-seconde de réapparition en fondu (rendu pur : rien dans la sim)
import { DEFAULT_WORLD, WORLDS, type WorldKey } from '../shared/worlds'
import { BIG_DECOS, BIG_FRAMES, bigOfCode, DECO_BY_CODE, DECOS, TRESOR_CODE_MIN, type BigDecoKind, type DecoKind, type DecorGrid } from '../shared/decor'
import { cameraOffset } from './camera'
import { formatTime } from './format-time'
import { ghostHudLabel } from './hud-ghost'
import { drawParticles } from './particles'
import { currentEmote, drawEmoteBubble } from './emote-ui'
import { equipped } from './loadout'

/** Horloge murale des emotes. Isolée ici pour que le rendu reste testable sans navigateur. */
const nowMsForEmote = (): number => (typeof performance !== 'undefined' ? performance.now() : 0)
import { creusageDuLoot, tempsCreusage } from './creusage'
import { petDessinable, resetPetHistory, updateAndDrawPet } from './pet'
import { dashVariant } from './dash-variant'
import { dashFxHideBody, dashFxTrack, drawDashFxBack, drawDashFxFront } from './dash-fx'
import { bouncePose, bounceVariant } from './bounce-variant'
import { poseDuSaut } from './saut-papier'
import { drapeauPapier } from './drapeau-papier'
import { toilePapier, TOILE_CHOISIE } from './toile-papier'
import { mailletVariant } from './maillet-variant'
import { FRAPPES_EN_JEU, peintEffetFrappe, tempsFrappe, tempsRetour } from './frappe-maillet'
import { MAILLET_CHOISI, mailletPapier, PRISE_CHOISIE, priseRepos } from './maillet-papier'
import { PORTE_CHOISIE, PORTEURS } from './porte-maillet'
import { blitPet } from './pet-papier'
import { tenueDuJeu } from './baroudeur-papier'
import { avatarHue, cameraZoom, crtPref, skinId, torchPref } from './prefs'
import { RUNNER_CELL, hueIndex, iceRef, runnerRef, skinIndex, smearRef } from '../shared/skins'
import { SHAKE_TICKS, shakeAmplitudeAt } from './shake'
import { BOSS_GOLD_REWARD, monkMood } from '../shared/monks'
import { teleCooldownRatio } from '../shared/teleport'
import {
  bigDecoSrc, BOMB_ITEM, DECO_SPRITES, decorSheet, DESERT_DECO_BY_CODE, DESERT_SPIKES, GOLD, greySprite, JACK_ITEM, KEY_ITEM, MONK_FRAMES, monkSprite, PET_SPRITES,
  RUNNER_POSE, ROPE_ITEM, SpriteRef, TAZER_ITEM, TRAMP_POSE, WORM_SLEEP, WORM_WAKE, FISH_FRAMES, runnersSheet, worldTilesSheet, HD_TILE, HD_BIOMES,
} from './sprites'
import { drawBaroudeur, type PoseName } from './baroudeur'
import { drawBaroudeurPose } from './baroudeur-papier'
import { terrainHash } from './terrain-cache'
import { bruit, rgba, setDetail } from './papier'
import { BIOMES, tuilePapier } from './terrain-papier'
import { dessinFossile } from './emmure-papier'
import { fondPapier } from './fond-papier'
import { blitMoine } from './moine-papier'
import { blitDeco } from './deco-papier'
import { blitFish, blitGrandDeco, blitObjet } from './objet-papier'
import { sameMask, shelterMask } from './shelter'
import { computeWaterBg } from './water-bg'

// Table code de déco -> sprite (index = code ; trous laissés à undefined). Construite une fois
// depuis DECOS + DECO_SPRITES. Grille déco vide (que des 0) -> aucun blit (rendu byte-identique).
const EMPTY_DECOR_GRID: DecorGrid = new Uint8Array(ROWS * COLS)
const DECO_SPRITES_BY_CODE: (SpriteRef | undefined)[] = (() => {
  const t: (SpriteRef | undefined)[] = []
  for (const k of Object.keys(DECOS) as DecoKind[]) t[DECOS[k].code] = DECO_SPRITES[k]
  return t
})()
// Codes des décos ANIMÉES (overlay au tick) : torche/bougie = flamme, valve = éclat tournant.
const DECO_ANIMATED_CODES = new Set(
  (Object.keys(DECOS) as DecoKind[]).filter((k) => DECOS[k].animated).map((k) => DECOS[k].code),
)
// Codes des décos de PREMIER PLAN (cascades) : dessinées APRÈS les entités, en semi-transparence
// (le coureur passe DERRIÈRE le rideau). Exclues de la passe déco normale et de drawDecoAnim.
const DECO_FRONT_CODES = new Set(
  (Object.keys(DECOS) as DecoKind[]).filter((k) => DECOS[k].front).map((k) => DECOS[k].code),
)

const BRAKE_POSE_TICKS = 5 // pose « freinage talons » après le burst du dash (cristal incarné 18/07)
const SCALE = 3
export const TS = 16 * SCALE   // 48 px : une case terrain à l'écran
const HUD_H = TS               // bandeau du bas = une case de haut
const PX_PER_SUB = TS / SUB    // 6 px par sous-unité (les entités bougent en sous-unités)
const HUD_BG = '#1a1633'
const HUD_FG = '#f0e9d2'
const ACCENT = '#ffd23f'       // jaune accent du jeu (HUD tazer)

export interface RenderCtx { ctx: CanvasRenderingContext2D; sheet: HTMLImageElement }
// item : objet porté du coureur LOCAL (16/07 soir) — affiché dans le bandeau (count = charges tazer/corde).
export interface HudInfo {
  lives: number; levelName: string; timeMs: number; muted: boolean; touchMode?: boolean
  item?: { kind: ItemKind; count: number } | null; checkpoint?: boolean
  /** FANTÔME en piste : qui on affronte et en combien de temps. Absent = pas de fantôme.
   *  Sans ça, on voyait un coureur translucide sans savoir qui c'était ni ce qu'il fallait battre. */
  ghost?: { pseudo: string; timeMs: number } | null
}

// Objets ramassables au sol : un sprite par nature (le tazer NE tombe plus dans le
// sprite marteau ; un ItemKind non couvert lèverait à la compilation).
// Les icônes d'objets passent par le rendu PAPIER (blitObjet), pas par la feuille de pixels : la
// refonte graphique a rendu la feuille obsolète pour les objets, et un objet neuf n'a plus à
// attendre qu'on lui grave une case dans un PNG. L'appât (31/07) est le premier dans ce cas.

const toPx = (sub: number) => sub * PX_PER_SUB

// Apparences par coureur en partie EN LIGNE (skins 19/07) : posées par online.ts depuis le
// message start du serveur (anti-jumeaux déjà appliqué), remises à null en solo/local où
// seules les prefs du joueur comptent. skin/hue = indices résolus (skinIdx/hueIdx), prêts pour
// runnerRef. pet/title (Lot C7) = loot_id ÉQUIPÉS du joueur : `pet` sert à dessiner le familier
// DE CHAQUE coureur (boucle plus bas) ; `title` est transporté par le même canal (affiché hors jeu,
// écran de fin) — jamais de label au-dessus des coureurs. Tout est cosmétique, hors sim/hash.
let runnerLooks: { skin: number; hue: number; pet?: string; title?: string; outfit?: string; hat?: string; pickaxe?: string }[] | null = null
export function setRunnerLooks(looks: { skin: number; hue: number; pet?: string; title?: string; outfit?: string; hat?: string; pickaxe?: string }[] | null): void {
  runnerLooks = looks
}
// Accès sûr à une paire de frames (0/1) sans buter sur noUncheckedIndexedAccess.
const pick2 = <T,>(pair: readonly [T, T], parity: number): T => (parity & 1 ? pair[1] : pair[0])

// Avatar : sprite du coureur LOCAL teinté par hue-rotate, mis en cache par (sprite, teinte) pour ne
// pas refiltrer chaque frame. -1 = couleur d'origine. Dégradation propre si ctx.filter n'est pas géré.
const tintCache = new Map<string, HTMLCanvasElement>()
function drawTinted(rc: RenderCtx, sr: SpriteRef, x: number, y: number, mirror: boolean, hue: number): void {
  const key = sr.sx + '_' + sr.sy + '_' + hue
  let cv = tintCache.get(key)
  if (!cv) {
    cv = document.createElement('canvas'); cv.width = 16; cv.height = 16
    const c = cv.getContext('2d')!
    c.filter = `hue-rotate(${hue}deg) saturate(1.5)`
    c.drawImage(rc.sheet, sr.sx, sr.sy, 16, 16, 0, 0, 16, 16)
    tintCache.set(key, cv)
  }
  const { ctx } = rc
  if (mirror) { ctx.save(); ctx.translate(x + TS, y); ctx.scale(-1, 1); ctx.drawImage(cv, 0, 0, 16, 16, 0, 0, TS, TS); ctx.restore() }
  else ctx.drawImage(cv, 0, 0, 16, 16, x, y, TS, TS)
}
// Le CORPS du coureur ne blitte plus runners.png : il est dessiné par le module procédural
// « Le Baroudeur » (client/baroudeur.ts, cf. drawRunner). Les anciens helpers de blit de
// cellule/pose/glace ont été retirés ; runners.png ne sert plus qu'au vestiaire hors jeu.
function blit(rc: RenderCtx, sr: SpriteRef, x: number, y: number, mirror = false): void {
  const { ctx, sheet } = rc
  if (!mirror) { ctx.drawImage(sheet, sr.sx, sr.sy, 16, 16, x, y, TS, TS); return }
  ctx.save()
  ctx.translate(x + TS, y)
  ctx.scale(-1, 1)
  ctx.drawImage(sheet, sr.sx, sr.sy, 16, 16, 0, 0, TS, TS)
  ctx.restore()
}

// Position ÉCRAN (px) d'une entité, interpolée entre le tick précédent et le courant.
// Snap si le saut dépasse une case (téléport / réapparition) pour éviter un glissé plein écran.
function lerpPos(prev: { x: number; y: number } | null, cur: { x: number; y: number }, alpha: number) {
  if (prev && Math.abs(cur.x - prev.x) <= SUB && Math.abs(cur.y - prev.y) <= SUB) {
    return { px: toPx(prev.x * (1 - alpha) + cur.x * alpha), py: toPx(prev.y * (1 - alpha) + cur.y * alpha) }
  }
  return { px: toPx(cur.x), py: toPx(cur.y) }
}

// Marqueur « étourdi » (tazer) : trois points jaunes tournoyant au-dessus de la tête de la
// cible (moine ou coureur). L'animation suit `tick` (état courant), pas l'horloge murale,
// pour rester cohérente entre solo et rejeu réseau. (sx, sy) = coin haut-gauche du sprite.
function drawStun(ctx: CanvasRenderingContext2D, sx: number, sy: number, tick: number): void {
  const cx = sx + TS / 2, cy = sy + 4
  ctx.save()
  ctx.fillStyle = '#ffe36b'
  for (let i = 0; i < 3; i++) {
    const a = tick * 0.25 + (i * Math.PI * 2) / 3
    ctx.beginPath()
    ctx.arc(cx + Math.cos(a) * (TS * 0.24), cy + Math.sin(a) * 3, 2.5, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.restore()
}

// Durée (ticks) de la frame d'anim de creusage après un event digAnim. Le client pose
// digUntil[runner] = tick + DIG_ANIM_TICKS et affiche `dig` tant que digUntil > tick (fenêtre
// exprimée en TICKS de simulation -> durée du geste stable quelle que soit la cadence d'affichage).
export const DIG_ANIM_TICKS = 6

// Frame du coureur selon son état (l'animation de marche/corde suit la position écran,
// pas l'horloge : elle « colle » au déplacement réel). `digging` = ce coureur vient de creuser
// (fenêtre digAnim, cosmétique) -> frame `dig`, MAIS chute/échelle/corde gardent la priorité :
// le geste de creusage ne doit jamais masquer une chute ou une grimpe. En revanche `digging`
// bat la marche/idle (on veut voir le geste même si une direction reste enfoncée après le creusage).
export function runnerFrame(r: Runner, screenX: number, moving: boolean, digging: boolean, immerge: boolean): number {
  // NAGE (EAU 16/07) : bras étendus (frames corde, lisibles sans nouveau sprite).
  // ⚠️ L'IMMERSION EST DONNÉE PAR L'APPELANT (06/08), elle n'est plus déduite du souffle. L'ancien
  // raccourci « breath < plein ⟺ immergé » évitait d'accéder à la grille, mais il mentait sur les
  // SOURCES DE BULLES, qui rechargent le souffle jusqu'au plein : le nageur posé sur une source y
  // reprenait la pose de marche, au fond de l'eau — tout le décor de « De bulle en bulle ».
  if (immerge) return pick2(RUNNER_POSE.rope, (r.x + r.y) >> 2)
  if (r.falling) return RUNNER_POSE.fall
  if (r.onLadder) return pick2(RUNNER_POSE.climb, (r.x + r.y) >> 2)
  if (r.onRope) return pick2(RUNNER_POSE.rope, Math.floor(screenX / 8))
  if (digging) return RUNNER_POSE.dig
  if (moving) return pick2(RUNNER_POSE.run, Math.floor(screenX / 8))
  return RUNNER_POSE.idle
}

// --- Le Baroudeur (client/baroudeur.ts) : traduction de l'INDICE de pose (RUNNER_POSE /
// TRAMP_POSE, choisi par la simulation via runnerFrame + la logique dash/tramp de drawRunner)
// vers le PoseName du module, plus la PHASE d'animation 0..1. run/climb/rope/dash sont animés
// par la phase (leurs deux sous-frames -> même PoseName) ; les autres poses sont figées. ---
const POSE_NAME_BY_INDEX: readonly PoseName[] = (() => {
  const t: PoseName[] = []
  t[RUNNER_POSE.idle] = 'idle'
  t[RUNNER_POSE.run[0]] = 'run'; t[RUNNER_POSE.run[1]] = 'run'
  t[RUNNER_POSE.climb[0]] = 'climb'; t[RUNNER_POSE.climb[1]] = 'climb'
  t[RUNNER_POSE.rope[0]] = 'rope'; t[RUNNER_POSE.rope[1]] = 'rope'
  t[RUNNER_POSE.fall] = 'fall'
  t[RUNNER_POSE.dig] = 'dig'
  t[RUNNER_POSE.crouch] = 'crouch'
  t[RUNNER_POSE.dash[0]] = 'dash'; t[RUNNER_POSE.dash[1]] = 'dash'
  t[RUNNER_POSE.brake] = 'brake'
  t[RUNNER_POSE.pickUp] = 'pickUp'
  t[RUNNER_POSE.pickHit] = 'pickHit'
  t[TRAMP_POSE[0]] = 'tramp0'; t[TRAMP_POSE[1]] = 'tramp1'; t[TRAMP_POSE[2]] = 'tramp2'
  return t
})()

const fract = (v: number): number => ((v % 1) + 1) % 1
// Phase 0..1 par pose, cohérente avec l'ancienne alternance de sous-frames :
//  · run/rope  suivent la position ÉCRAN horizontale (comme pick2 sur screenX aujourd'hui) ;
//  · climb     suit la progression écran (px+py, comme l'ancien (x+y)>>2 — échelle = vertical) ;
//  · fall/idle battent au tick (lisses et bouclants) ;
//  · dash      suit la fenêtre de BURST (fin de charge -> dashUntil), pour tenir la fente.
function baroudeurPhase(name: PoseName, r: Runner, px: number, py: number, tick: number, alpha: number): number {
  switch (name) {
    case 'run': return fract(px / 16)            // cycle de marche = 16 px (2 sous-frames de 8 px)
    case 'rope': return fract(px / 16)           // mains sur mains : avance avec le déplacement horizontal
    case 'climb': return fract((px + py) / 24)   // grimpe : suit la progression (px+py)
    case 'fall': return fract((tick + alpha) * 0.09) // battement de bras au tick
    case 'idle': return fract((tick + alpha) * 0.03) // respiration lente
    case 'dash': { const p = Math.max(0, Math.min(1, ((tick + alpha) - r.dashChargeUntil) / 6)); return 0.22 + p * 0.20 } // burst (DASH_TICKS=6) : ANTIC -> LUNGE tenue
    default: return 0 // crouch/brake/pickUp/pickHit/tramp* : poses figées (phase ignorée)
  }
}

// Objet porté : petite icône pixel-art au-dessus de la tête, pour TOUT coureur qui tient un
// objet, TOUS les joueurs, TOUS les modes (lit r.item déjà synchronisé -> zéro état ajouté,
// purement cosmétique). `count` > 0 (charges du tazer) = petit chiffre ambre discret en bas à
// droite. Pastille sombre translucide derrière pour rester lisible sur un terrain chargé.
const CARRY_ICON = 22 // px (~0.46 case) : lisible mais discret, pixel-art ×~1.4
function drawCarriedItem(rc: RenderCtx, item: ItemKind, sx: number, sy: number, count: number): void {
  const { ctx, sheet } = rc
  const ix = sx + (TS - CARRY_ICON) / 2         // centré horizontalement au-dessus de la tête
  const iy = Math.max(1, sy - CARRY_ICON / 2)   // moitié au-dessus du sprite ; clamp anti-débord haut d'écran
  ctx.save()
  ctx.fillStyle = 'rgba(26, 22, 51, 0.62)'      // pastille = teinte HUD, translucide (lisibilité)
  ctx.fillRect(ix - 3, iy - 3, CARRY_ICON + 6, CARRY_ICON + 6)
  blitObjet(ctx, item, CARRY_ICON, ix, iy)
  if (count > 0) {
    const txt = String(count)
    ctx.font = 'bold 13px ui-monospace, Menlo, monospace'
    ctx.textAlign = 'right'
    ctx.textBaseline = 'alphabetic'
    ctx.fillStyle = 'rgba(0, 0, 0, 0.85)'       // ombre pour détacher le chiffre du fond
    ctx.fillText(txt, ix + CARRY_ICON + 3, iy + CARRY_ICON + 5)
    ctx.fillStyle = ACCENT                       // jaune accent (reprend l'ancien HUD « TAZER n »)
    ctx.fillText(txt, ix + CARRY_ICON + 2, iy + CARRY_ICON + 4)
  }
  ctx.restore()
}

// FANTÔME DU RECORDMAN (25/07). Transparence : assez présent pour se suivre du coin de l'œil,
// assez discret pour ne jamais se confondre avec soi-même. Teinte 5 (bleu) = la seule qui se lise
// d'emblée comme « pas moi » à côté de l'ocre du Baroudeur. Index d'effets 7 : hors des index de
// coureurs réels (0 à 2), pour que sa traînée de dash ait son propre emplacement.
const GHOST_ALPHA = 0.42
/** Teinte du fantôme dans le bandeau : la même famille que le coureur translucide. */
const GHOST_HUD_COLOR = '#7fb8ff'
const HUD_GAP = 16 // blanc minimal entre le flux de gauche et le nom du niveau collé à droite
const GHOST_HUE_IDX = 5
const GHOST_FX_IDX = 7

// LE COCON DE LA MOMIE (05/08, objet versus n°5) : plus de coureur du tout — un fuseau de
// bandes de papier qui SAUTILLE sur place (il se débat), deux yeux ronds paniqués au sommet,
// et un tressaillement à chaque coup martelé. Les bandes suivent la teinte de sa tenue au
// liseré : même en cocon, on sait qui on regarde.
// `hueIdx = -1` : le cocon d'un MOINE (liseré violet de moine au lieu de la teinte d'un joueur).
function drawCocon(ctx: CanvasRenderingContext2D, x: number, y: number, tick: number, hueIdx: number, coups: number): void {
  const bond = Math.abs(Math.sin(tick * 0.35)) * 5           // le sautillement sur place
  const tremble = Math.sin(tick * 1.9) * (1 - coups / 6) * 1.6 // plus il se libère, plus il tremble
  const cx = x + TS / 2 + tremble, base = y + TS - bond
  ctx.save()
  ctx.fillStyle = 'rgba(46, 34, 20, 0.3)'                     // l'ombre portée, franche comme tout le reste
  ctx.beginPath(); ctx.ellipse(cx + 3, base + 2, TS * 0.3, TS * 0.11, 0, 0, Math.PI * 2); ctx.fill()
  ctx.fillStyle = '#efe6d2'                                   // le fuseau de bandes
  ctx.beginPath()
  ctx.moveTo(cx - TS * 0.27, base)
  ctx.quadraticCurveTo(cx - TS * 0.34, base - TS * 0.5, cx, base - TS * 0.86)
  ctx.quadraticCurveTo(cx + TS * 0.34, base - TS * 0.5, cx + TS * 0.27, base)
  ctx.closePath(); ctx.fill()
  ctx.strokeStyle = 'rgba(150, 132, 100, 0.75)'; ctx.lineWidth = 2 // les bandes croisées
  for (let i = 0; i < 5; i++) {
    const yy = base - 4 - i * TS * 0.16
    ctx.beginPath()
    ctx.moveTo(cx - TS * 0.26 + i, yy)
    ctx.lineTo(cx + TS * 0.26 - i, yy - 4 + (i % 2) * 8)
    ctx.stroke()
  }
  ctx.strokeStyle = hueIdx < 0 ? '#8b4fd8' : tenueDuJeu(hueIdx, '').torse; ctx.lineWidth = 3 // le liseré d'identité
  ctx.beginPath(); ctx.moveTo(cx - TS * 0.24, base - TS * 0.3); ctx.lineTo(cx + TS * 0.24, base - TS * 0.36); ctx.stroke()
  ctx.fillStyle = '#2e2214'                                   // les deux yeux paniqués
  ctx.beginPath(); ctx.arc(cx - 5, base - TS * 0.62, 3.4, 0, Math.PI * 2); ctx.fill()
  ctx.beginPath(); ctx.arc(cx + 5, base - TS * 0.62, 3.4, 0, Math.PI * 2); ctx.fill()
  ctx.fillStyle = '#f6efe0'
  ctx.beginPath(); ctx.arc(cx - 6, base - TS * 0.64, 1.2, 0, Math.PI * 2); ctx.fill()
  ctx.beginPath(); ctx.arc(cx + 4, base - TS * 0.64, 1.2, 0, Math.PI * 2); ctx.fill()
  ctx.restore()
}

// L'ÉCHANGE D'OUTIL (06/08, retour Claude-Alain : « la prise du maillet ou de la pioche se fait
// automatiquement mais il faut une animation visuelle qui montre bien le changement d'outil,
// actuellement beaucoup trop discret »). Une seconde entière de cérémonie : l'ANCIEN outil part
// en tournoyant vers le haut pendant que le NOUVEAU tombe du ciel en tournant à l'envers, une
// couronne d'éclats s'ouvre au moment du croisement, et une bannière annonce l'arme.
// 100 % rendu : `depuis` est le tick de l'événement (malletPris / malletRendu).
const SWAP_TICKS = 30

function drawEchangeOutil(ctx: CanvasRenderingContext2D, x: number, y: number, ecoule: number, versMaillet: boolean): void {
  const p = Math.max(0, Math.min(1, ecoule / SWAP_TICKS))
  const cx = x + TS / 2, cy = y + TS * 0.45
  ctx.save()
  // 1. la couronne d'éclats, au croisement (le milieu de l'échange)
  const kEclat = 1 - Math.abs(p - 0.42) / 0.42
  if (kEclat > 0) {
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + p * 1.2
      const rr = 10 + (1 - kEclat) * 34
      ctx.globalAlpha = 0.75 * kEclat
      ctx.fillStyle = i % 2 ? '#ffd23f' : '#fff3c4'
      const ex = cx + Math.cos(a) * rr, ey = cy + Math.sin(a) * rr * 0.85
      ctx.fillRect(ex - 1.5, ey - 5, 3, 10); ctx.fillRect(ex - 5, ey - 1.5, 10, 3)
    }
    ctx.restore()
  }
  // 2. l'ANCIEN outil s'envole en tournoyant (il sort par le haut), le NOUVEAU tombe dessus
  const sortant = versMaillet ? 'pioche' : 'maillet'
  const entrant = versMaillet ? 'maillet' : 'pioche'
  const dessine = (quoi: string, oy: number, ang: number, a: number): void => {
    if (a <= 0.02) return
    ctx.save()
    ctx.globalAlpha = a
    ctx.translate(cx, cy + oy)
    ctx.rotate(ang)
    if (quoi === 'maillet') mailletPapier(ctx, TS * 0.6, MAILLET_CHOISI)
    else dessinePioche(ctx, TS * 0.6)
    ctx.restore()
  }
  dessine(sortant, -p * TS * 1.5, p * 7, 1 - p)                       // il part vers le haut
  dessine(entrant, (1 - p) * TS * 1.5 - TS * 0.1, -(1 - p) * 7, p)    // il descend à sa place
  // 3. la bannière : on nomme l'arme, en toutes lettres
  if (p > 0.3) {
    const a = Math.min(1, (p - 0.3) / 0.2) * (p > 0.85 ? (1 - p) / 0.15 : 1)
    ctx.globalAlpha = a
    const txt = versMaillet ? 'MAILLET !' : 'PIOCHE !'
    ctx.font = 'bold 13px ui-monospace, Menlo, monospace'
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
    const w = ctx.measureText(txt).width + 18
    const by = y - TS * 0.3 - p * 8
    ctx.fillStyle = 'rgba(46, 34, 20, 0.34)'; ctx.fillRect(cx - w / 2 + 3, by - 8 + 3, w, 22)
    ctx.fillStyle = '#f6efe0'; ctx.fillRect(cx - w / 2, by - 8, w, 22)
    ctx.fillStyle = versMaillet ? '#8b2f2f' : '#5a4a2a'
    ctx.fillText(txt, cx, by + 3)
    ctx.globalAlpha = 1
  }
  ctx.restore()
}

/** La pioche, dessinée dans la même convention que le maillet (poigne en 0,0, tête en haut) :
 *  c'est elle qui part quand le maillet arrive, et l'inverse. */
function dessinePioche(ctx: CanvasRenderingContext2D, S: number): void {
  const k = S / 48
  ctx.save()
  ctx.fillStyle = '#a06a34'
  ctx.fillRect(-2.5 * k, -34 * k, 5 * k, 34 * k)
  ctx.strokeStyle = '#9aa8b4'; ctx.lineWidth = 5 * k; ctx.lineCap = 'round'
  ctx.beginPath(); ctx.arc(0, -34 * k, 13 * k, Math.PI * 1.12, Math.PI * 1.88); ctx.stroke()
  ctx.strokeStyle = 'rgba(200, 210, 218, 0.7)'; ctx.lineWidth = 2 * k
  ctx.beginPath(); ctx.arc(0, -35 * k, 13 * k, Math.PI * 1.2, Math.PI * 1.6); ctx.stroke()
  ctx.restore()
}

// LE MODE D'EMPLOI DE LA MOMIE (06/08, sa demande : « il faut un message fun pour indiquer au
// joueur comment s'en sortir »). Une pancarte de papier au-dessus de SON cocon, à lui seul :
// le mot d'ordre, les deux flèches dont la PROCHAINE ATTENDUE s'allume et grossit, et les huit
// perles du compte à rebours. On ne lit pas un message quand on panique — on suit une flèche
// qui clignote. Purement cosmétique, dérivé de l'état (aucune horloge murale).
const MOMIE_CRIS = ['GIGOTE !', 'DÉBATS-TOI !', 'ÇA COLLE !', 'REMUE !'] as const

function drawMessageMomie(ctx: CanvasRenderingContext2D, x: number, y: number, tick: number, coups: number, cote: number, total: number): void {
  const cri = MOMIE_CRIS[Math.floor(tick / 45) % MOMIE_CRIS.length]!
  const suivant = cote === -1 ? 1 : cote === 1 ? -1 : 0 // l'alternance impose l'autre côté ; 0 = les deux vont
  const W = 112, H = 42
  // La pancarte ne sort JAMAIS par le haut : momifié tout en haut de la carte, le mode d'emploi
  // serait hors écran — donc invisible pile quand on en a besoin. Elle bascule alors sous le cocon.
  const cx = x + TS / 2
  const haut = y - TS * 0.36 + Math.sin(tick * 0.18) * 2
  const by = haut - H < 2 ? y + TS + H + 2 : haut
  ctx.save()
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  // la pancarte : ombre franche + papier crème, comme tout le reste du jeu
  ctx.fillStyle = 'rgba(46, 34, 20, 0.34)'
  ctx.fillRect(cx - W / 2 + 3, by - H + 4, W, H)
  ctx.fillStyle = '#f6efe0'
  ctx.fillRect(cx - W / 2, by - H, W, H)
  ctx.fillStyle = '#c9b48d'                   // le bord déchiré du bas
  for (let i = 0; i < W; i += 6) ctx.fillRect(cx - W / 2 + i, by - 2, 4, 2 + (i % 12 === 0 ? 2 : 0))
  // le cri
  ctx.fillStyle = '#8b2f2f'
  ctx.font = 'bold 13px ui-monospace, Menlo, monospace'
  ctx.fillText(cri, cx, by - H + 12)
  // les deux flèches : la prochaine attendue est GRANDE et dorée, l'autre pâle
  for (const s of [-1, 1] as const) {
    const attendue = suivant === 0 || s === suivant
    const k = attendue ? 1 + 0.14 * Math.sin(tick * 0.4) : 0.78
    ctx.save()
    ctx.translate(cx + s * 27, by - 19)
    ctx.scale(-s * k, k) // la flèche de GAUCHE pointe à gauche : le dessin natif pointe déjà à gauche
    ctx.fillStyle = attendue ? '#e8a020' : 'rgba(120, 108, 88, 0.5)'
    ctx.beginPath()
    ctx.moveTo(9, -8); ctx.lineTo(-4, 0); ctx.lineTo(9, 8); ctx.lineTo(9, 3); ctx.lineTo(2, 0); ctx.lineTo(9, -3)
    ctx.closePath(); ctx.fill()
    ctx.restore()
  }
  // les perles du compte à rebours : une par coup déjà porté
  const faits = total - coups
  for (let i = 0; i < total; i++) {
    ctx.fillStyle = i < faits ? '#e8a020' : 'rgba(120, 108, 88, 0.32)'
    ctx.beginPath(); ctx.arc(cx - (total - 1) * 5.5 / 2 + i * 5.5, by - 7, 2.1, 0, Math.PI * 2); ctx.fill()
  }
  ctx.restore()
}

// LA PATATE CHAUDE (05/08) : éteinte, c'est l'objet au repos ; ALLUMÉE, elle bat de plus en
// plus vite et rougeoie — mais le battement suit une horloge FIXE, jamais l'échéance : la mèche
// reste secrète, exactement comme demandé (« le sifflement ment un peu »).
function drawPatate(ctx: CanvasRenderingContext2D, x: number, y: number, tick: number, allumee: boolean, depuis: number): void {
  if (!allumee) { blitObjet(ctx, 'patate', TS, x, y); return }
  const bat = 1 + 0.14 * Math.abs(Math.sin(tick * (0.22 + Math.min(0.5, depuis * 0.0035))))
  drawBloom(ctx, x, y, '255,120,60', TS * 0.75, tick)
  ctx.save()
  ctx.translate(x + TS / 2, y + TS / 2)
  ctx.scale(bat, bat)
  ctx.translate(-(x + TS / 2), -(y + TS / 2))
  blitObjet(ctx, 'patate', TS, x, y)
  ctx.globalCompositeOperation = 'lighter'                    // la chauffe : elle rougeoie de l'intérieur
  ctx.fillStyle = `rgba(255, 90, 40, ${(0.2 + 0.2 * Math.sin(tick * 0.4)).toFixed(3)})`
  ctx.beginPath(); ctx.ellipse(x + TS / 2, y + TS / 2 + 4, 15, 13, 0.12, 0, Math.PI * 2); ctx.fill()
  ctx.restore()
  // l'étincelle au bout de la mèche, qui court
  ctx.save()
  ctx.fillStyle = '#ffd23f'
  const sx = x + TS / 2 + 6, sy = y + TS / 2 - 23 + Math.sin(tick * 0.9) * 1.5
  ctx.beginPath(); ctx.arc(sx, sy, 3 + Math.sin(tick * 0.7), 0, Math.PI * 2); ctx.fill()
  ctx.restore()
}

// LA PIOCHE PLANTÉE (05/08, l'échange du maillet) : le manche en biais fiché au sol, la tête
// d'acier en l'air, et le FOULARD du propriétaire noué au manche — c'est lui qui dit « à qui ».
function drawPiochePlantee(ctx: CanvasRenderingContext2D, x: number, y: number, hueIdx: number, tick: number): void {
  const cx = x + TS / 2, sol = y + TS
  ctx.save()
  ctx.lineCap = 'round'
  ctx.strokeStyle = '#a06a34'; ctx.lineWidth = 4                                  // le manche, planté en biais
  ctx.beginPath(); ctx.moveTo(cx + 6, sol - 2); ctx.lineTo(cx - 6, sol - TS * 0.72); ctx.stroke()
  ctx.strokeStyle = '#9aa8b4'; ctx.lineWidth = 5                                  // la tête d'acier, en croissant
  ctx.beginPath(); ctx.arc(cx - 6, sol - TS * 0.66, 11, Math.PI * 0.15, Math.PI * 0.95); ctx.stroke()
  const flotte = Math.sin(tick * 0.12 + x * 0.05) * 1.5                            // le foulard respire
  ctx.fillStyle = tenueDuJeu(hueIdx, '').torse
  ctx.beginPath()
  ctx.moveTo(cx - 1, sol - TS * 0.5)
  ctx.lineTo(cx + 12, sol - TS * 0.56 + flotte)
  ctx.lineTo(cx + 10, sol - TS * 0.42 + flotte)
  ctx.lineTo(cx - 1, sol - TS * 0.42)
  ctx.closePath(); ctx.fill()
  ctx.restore()
}

// LE DRAPEAU-CRAPAUD (05/08) : le fanion baladeur, incliné dans le sens de son vol. La capture
// le TÉLÉPORTE chez lui (sa case d'éditeur) : lerpPos snappe déjà les sauts de plus d'une
// sous-unité, aucun filage à l'écran. Le claquement suit le tick partagé (déterministe).
function drawDrapeau(rc: RenderCtx, prev: { x: number; y: number } | null, d: { x: number; y: number; dx: number }, alpha: number, tick: number): void {
  const { px, py } = lerpPos(prev, d, alpha)
  drawBloom(rc.ctx, px, py, '110,205,130', TS * 0.62, tick) // halo de collectible : on le repère de loin
  rc.ctx.save()
  const cx = px + TS / 2, cy = py + TS / 2
  rc.ctx.translate(cx, cy)
  rc.ctx.rotate(d.dx * 0.09)                       // penché dans le sens du vol : on lit sa route
  if (d.dx < 0) rc.ctx.scale(-1, 1)                // le fanion claque toujours vers l'arrière
  rc.ctx.translate(-cx, -cy)
  drapeauPapier(rc.ctx, px, py, TS, tick + alpha)
  rc.ctx.restore()
}

function drawRunner(rc: RenderCtx, prev: Runner | null, r: Runner, idx: number, alpha: number, tick: number, digLeft: number, hueIdx: number, bounce?: readonly [number, number], outfitId = '', emoteBob = 0, hatId = '', immerge = false): void {
  const { px, py } = lerpPos(prev, r, alpha)
  // FORME CRAPAUD (05/08, objet versus n°1). Le coureur EST le crapaud de pet-papier, monté à
  // l'échelle du héros, avec un foulard à la teinte de SA tenue (l'identité en multi). L'arc de
  // ses bonds est celui du sauteur (poseDuSaut sur r.hop) : un seul saut dans tout le jeu.
  if (r.crapaud) {
    drawGroundShadow(rc.ctx, px, py)
    const saut = poseDuSaut(prev?.hop ?? 0, r.hop, alpha, 2 * SUB)
    const pyS = saut ? py - Math.round(saut.dy * TS) : py
    rc.ctx.save()
    if (r.face < 0) { rc.ctx.translate(px + TS / 2, 0); rc.ctx.scale(-1, 1); rc.ctx.translate(-(px + TS / 2), 0) }
    if (saut) {
      const cx = px + TS / 2, cy = pyS + TS * 0.6
      rc.ctx.translate(cx, cy)
      if (r.stunUntil > tick) rc.ctx.rotate(saut.t * Math.PI * 4) // crapaud FRAPPÉ : il vrille aussi
      else { rc.ctx.rotate(saut.lean); rc.ctx.scale(1 / saut.sy, saut.sy) }
      rc.ctx.translate(-cx, -cy)
    }
    // Allure : petit dandinement de marche quand il se déplace (les mêmes 2 pas que les pets).
    const bouge = prev ? prev.x !== r.x || prev.y !== r.y : false
    rc.ctx.translate(px + TS * 0.06, pyS + TS * 0.1)
    blitPet(rc.ctx, 'crapaud', TS * 0.88, 0, bouge && r.hop === 0 ? 1 + (tick >> 2) % 2 : 0)
    rc.ctx.translate(-(px + TS * 0.06), -(pyS + TS * 0.1))
    // Le foulard : un bandeau noué à la teinte de la tenue du joueur, l'identité qui reste.
    rc.ctx.fillStyle = tenueDuJeu(hueIdx, outfitId).torse
    rc.ctx.fillRect(px + TS * 0.22, pyS + TS * 0.62, TS * 0.5, TS * 0.11)
    rc.ctx.beginPath()
    rc.ctx.moveTo(px + TS * 0.24, pyS + TS * 0.7)
    rc.ctx.lineTo(px + TS * 0.12, pyS + TS * 0.88)
    rc.ctx.lineTo(px + TS * 0.28, pyS + TS * 0.76)
    rc.ctx.closePath(); rc.ctx.fill()
    rc.ctx.restore()
    if (r.stunUntil > tick) drawStun(rc.ctx, px, pyS, tick)
    return
  }
  // MOMIFIÉ (05/08, objet versus n°5) : on ne voit plus le coureur du tout, juste un cocon de
  // bandes qui sautille — le pet aussi disparaît (masqué au dessin des pets).
  if (r.momieUntil > tick) {
    drawCocon(rc.ctx, px, py, tick + alpha, hueIdx, r.momieCoups)
    return
  }
  // FAUX MOINE (05/08, objet versus n°3) : sur TOUS les écrans, le déguisé EST un moine normal —
  // même silhouette, même échelle, aucune bulle d'humeur, pas de pet (masqué au dessin des pets),
  // aucune traînée de tenue. Le seul indice qui reste est le MOUVEMENT : un moine qui dashe,
  // c'est la demi-seconde de terreur voulue par l'objet. Le joueur déguisé se retrouve, lui,
  // parce que la caméra le suit et que ses commandes répondent.
  if (r.deguiseUntil > tick) {
    drawGroundShadow(rc.ctx, px, py)
    rc.ctx.save()
    if (r.face < 0) { rc.ctx.translate(px + TS / 2, 0); rc.ctx.scale(-1, 1); rc.ctx.translate(-(px + TS / 2), 0) }
    blitMoine(rc.ctx, 'normal', TS, px, py)
    rc.ctx.restore()
    if (r.stunUntil > tick) drawStun(rc.ctx, px, py, tick)
    return
  }
  const moving = prev ? prev.x !== r.x : false
  // La pioche du joueur `idx` : en ligne elle vient du serveur (on voit le geste des autres),
  // en solo de l'équipement local. `null` = aucune pioche à style, geste historique.
  const styleCreusage = creusageDuLoot(runnerLooks ? runnerLooks[idx]?.pickaxe : equipped('pickaxe'))
  let pose = runnerFrame(r, px, moving, digLeft > 0, immerge)
  // COUP DE PIOCHE (18/07, maquette B) : si le geste de creusage a la priorité (runnerFrame a
  // choisi `dig`), on le joue en 2 temps sur la fenêtre existante : impact PLANTÉ (synchro avec
  // l'ouverture du trou) puis la pioche se relève. Aucun tick ajouté nulle part.
  if (pose === RUNNER_POSE.dig) pose = digLeft > DIG_ANIM_TICKS / 2 ? RUNNER_POSE.pickHit : RUNNER_POSE.pickUp
  // DASH (18/07) : poses dédiées par phase. Charge (2 ticks) = accroupi + éclats convergents ;
  // burst = fente lancée (le module dessine la traînée) ; 5 ticks après l'arrêt = freinage talons.
  const charging = r.dashChargeUntil > tick
  const bursting = !charging && r.dashUntil > tick
  const braking = !charging && !bursting && r.dashUntil > 0 && tick >= r.dashUntil && tick < r.dashUntil + BRAKE_POSE_TICKS &&
    !r.falling && !r.onLadder && !r.onRope && r.breath >= BREATH_TICKS
  if (charging) pose = RUNNER_POSE.crouch
  else if (bursting) pose = pick2(RUNNER_POSE.dash, tick >> 1)
  else if (braking) pose = RUNNER_POSE.brake
  // (L'identité visuelle par joueur vient de la TEINTE de tenue (hueIdx) passée au module
  // Baroudeur, qui recolore la seule veste ocre — plus de rangée pré-cuite dans runners.png.)
  // SAUT TRAMPOLINE (18/07, « les 4 sauts en alternance aléatoire ») : habillage 100 % cosmétique
  // de la montée. bounce = [tick de l'event, sous-unités initiales], suivi côté client (pattern
  // digUntil) ; total >= 24 ⟺ vrai trampoline (le bond de surface de l'eau vaut 10). Tirage
  // déterministe PAR rebond (bounce-variant.ts) : 0 physique · 1 acrobate · 2 cartoon · 3 propulseur.
  const [bAt, bTotal] = bounce ?? [0, 0]
  const bRise = bTotal >> 1                 // montée = total/2 ticks (2 su/tick) : 12 ou 20
  const bEl = tick - bAt
  const trampAnim = bAt > 0 && bTotal >= 24 && bEl >= 0 && bEl < bRise + 3 &&
    !charging && !bursting && !braking && digLeft === 0
  const bv = trampAnim ? bounceVariant(bAt, idx) : 0
  let bp = trampAnim ? bouncePose(bv, bEl, bRise, r.falling) : null
  if (bp && bEl < bRise && r.bounce === 0) bp = null // montée coupée (plafond) -> on rend la main net
  if (bp) pose = bp === 'squash' ? TRAMP_POSE[0] : bp === 'apex' ? TRAMP_POSE[2] : TRAMP_POSE[1]
  const pyD = bp === 'squash' ? py + 5 : py // le squash s'enfonce dans la toile (5 px, visuel pur)
  // GÉANT (05/08, objet versus n°2) : deux cases de haut. Croissance sur ~6 ticks, rétrécissement
  // sur les 6 derniers — le facteur est une fonction PURE du temps restant, rien à stocker.
  const gRest = r.geantUntil - (tick + alpha)
  const kGeant = r.geantUntil > tick ? 1 + 0.85 * Math.max(0, Math.min(1, Math.min((GEANT_TICKS - gRest) / 6, gRest / 6))) : 1
  // VOL PLANÉ (bousculade du géant) : un coureur expédié (r.hop) suit l'arc du bond du crapaud —
  // décalage de rendu pur, comme le sauteur. Le géant, lui, ENJAMBE à plat (grandes jambes).
  const vol = kGeant === 1 ? poseDuSaut(prev?.hop ?? 0, r.hop, alpha, 2 * SUB) : null
  const pyV = vol ? pyD - Math.round(vol.dy * TS) : pyD
  // BURST du dash : la VARIANTE d'effet (dash-variant.ts, 0..3) est transmise au module, qui dessine
  // lui-même la traversée (0 comète · 1 rémanence · 2 smear · 3 explosion). Le corps, la glace, les
  // échos et la vrille — jadis blittés depuis runners.png — sont désormais rendus par Le Baroudeur.
  const dv = r.dashUntil > 0 ? dashVariant(r.dashUntil, idx) : 0
  // Effets MONDE des 3 variantes (dash-fx) : suivi de la position interpolée pendant charge+burst
  // (départ mémorisé, échos, runes semées sur le trajet réel). 100 % cosmétique client.
  dashFxTrack(idx, r.dashUntil, r.dashChargeUntil, px, pyD, r.face < 0 ? -1 : 1, hueIdx, tick + alpha)
  drawGroundShadow(rc.ctx, px, py)          // ombre au sol (ancre le sprite dans le décor)
  if (kGeant > 1) { // l'ombre s'élargit avec lui : la montagne pèse au sol
    rc.ctx.save(); rc.ctx.fillStyle = 'rgba(0,0,0,0.22)'
    rc.ctx.beginPath(); rc.ctx.ellipse(px + TS / 2, py + TS - 3, TS * 0.33 * kGeant, TS * 0.1 * kGeant, 0, 0, Math.PI * 2); rc.ctx.fill()
    rc.ctx.restore()
  }
  // LE CORPS : module procédural « Le Baroudeur » (client/baroudeur.ts). On traduit l'indice de pose
  // en PoseName + phase 0..1, on passe le sens (r.face) et la TEINTE de tenue du coureur (hueIdx,
  // distincte par joueur en multi). Le squash de trampoline s'enfonce dans la toile via pyD (visuel pur).
  const poseName = POSE_NAME_BY_INDEX[pose] ?? 'idle'
  // ONDE TONNERRE (v2) : vibration de la compression, ÉTIREMENT pendant le burst, ressort au freinage.
  const jx = charging && dv === 2 ? Math.sin((tick + alpha) * 9.7) * 1.3 : 0
  let sxB = 1, syB = 1
  if (bursting && dv === 2) { sxB = 1.32; syB = 0.88 }
  else if (braking && dv === 2) { const e = Math.exp(-(tick + alpha - r.dashUntil) / 3.5) * Math.cos((tick + alpha - r.dashUntil) * 1.35); sxB = 1 + 0.24 * e; syB = 1 - 0.17 * e }
  const stretch = sxB !== 1 || syB !== 1
  // LE PORTEUR DE MAILLET (06/08, sa demande : « il lui faut une nouvelle position du personnage
  // quand il tient le maillet au lieu de la pioche »). Le CORPS change de posture — ce n'est plus
  // un accessoire posé à côté d'un bonhomme inchangé. Réservé aux moments « normaux » : dash,
  // chute, échelle, corde, rebond et frappe gardent leurs poses, qui racontent autre chose.
  // LA FRAPPE (06/08) : sa phase se calcule ICI, avant le corps — depuis qu'elle a ses propres
  // postures (frappe-maillet.ts), c'est elle qui décide de la pose du personnage.
  // Le cycle COMPLET d'un coup, sans trou : la première moitié du rechargement joue la frappe,
  // la seconde ramène le corps à la garde (06/08 — avant, le corps sautait d'un coup à la posture
  // de repos à mi-cycle, et c'est CE saut qu'on voyait comme une saccade).
  const resteFr = r.mailletReadyAt - (tick + alpha)
  const phaseFr = r.maillet && r.mailletReadyAt > 0 && resteFr > 0 && r.geantUntil <= tick
    ? (MAILLET_CD - resteFr) / MAILLET_CD : -1
  const frFrappe = phaseFr >= 0 ? FRAPPES_EN_JEU[mailletVariant(r.mailletReadyAt, idx) % FRAPPES_EN_JEU.length]! : 0
  const frappeEnCours = phaseFr < 0 ? null
    : phaseFr < .5 ? tempsFrappe(frFrappe, phaseFr * 2)
      : tempsRetour(frFrappe, (phaseFr - .5) * 2, PORTEURS[PORTE_CHOISIE]!)
  const swFr = phaseFr
  const porteur = !frappeEnCours && r.maillet && r.geantUntil <= tick && !r.crapaud && !charging && !bursting && !braking
    && !r.falling && !r.onLadder && !r.onRope && r.bounce === 0 && digLeft === 0 && !bp
    ? PORTEURS[PORTE_CHOISIE]! : null
  if (!dashFxHideBody(idx, r.dashUntil, tick + alpha)) { // ÉCLAIR D'ARCANE (v1) : le corps EST la foudre pendant le burst
    if (kGeant > 1) { rc.ctx.save(); rc.ctx.translate(px + TS / 2, py + TS); rc.ctx.scale(kGeant, kGeant); rc.ctx.translate(-(px + TS / 2), -(py + TS)) } // GÉANT : pivot aux pieds, il pousse vers le HAUT
    if (stretch) { rc.ctx.save(); rc.ctx.translate(px + TS / 2, pyD + TS); rc.ctx.scale(sxB, syB); rc.ctx.translate(-(px + TS / 2), -(pyD + TS)) }
    // VOL PLANÉ ENCAISSÉ (05/08, « une animation plus parlante ») : maillet ou bousculade du
    // géant, le coureur part en VRILLE — deux tours complets sur la durée du vol, dans le sens
    // de l'expulsion. C'est la vrille qui dit « il s'est fait avoir », à n'importe quelle échelle.
    if (vol) { rc.ctx.save(); rc.ctx.translate(px + TS / 2, pyV + TS * 0.55); rc.ctx.rotate(r.face * vol.t * Math.PI * 4); rc.ctx.translate(-(px + TS / 2), -(pyV + TS * 0.55)) }
    if (frappeEnCours) {
      // 31 poses distinctes par cycle de coup : à 30 Hz le cycle dure 10 ticks, soit 20 images
      // à 60 Hz — une pose neuve à chaque image, avec de la marge pour les écrans plus rapides.
      const cleFr = `fr${frFrappe}|${Math.round(phaseFr * 30)}`
      drawBaroudeurPose(rc.ctx, frappeEnCours.corps, cleFr, r.face < 0 ? -1 : 1, px + jx, pyV + emoteBob, TS, { hue: hueIdx, outfit: outfitId, hat: hatId, hatTime: tick + alpha })
    } else if (digLeft > 0 && styleCreusage !== null) {
      // LE CREUSAGE A UN STYLE (13/08). Sans pioche à style, on garde le geste historique (les
      // deux poses figées, plus bas). Avec, la pose se calcule en continu sur la fenêtre du geste :
      // le tailleur donne trois petits coups là où le bûcheron n'en donne qu'un, très lourd.
      const phCr = 1 - digLeft / DIG_ANIM_TICKS
      const tc = tempsCreusage(styleCreusage, phCr)
      drawBaroudeurPose(rc.ctx, tc.pose, `cr${styleCreusage}|${Math.round(phCr * 20)}`, r.face < 0 ? -1 : 1, px + jx, pyV + emoteBob, TS, { hue: hueIdx, outfit: outfitId, hat: hatId, hatTime: tick + alpha })
    } else if (porteur) {
      // marche ou arrêt : le cycle de jambes du jeu, le haut du corps du porteur.
      const bouge = prev ? prev.x !== r.x : false
      const f = bouge ? Math.floor(px / 6) % porteur.marche.length : -1
      const po = f < 0 ? porteur.debout : porteur.marche[f]!
      drawBaroudeurPose(rc.ctx, po, `p${PORTE_CHOISIE}|${f}`, r.face < 0 ? -1 : 1, px + jx, pyV + emoteBob, TS, { hue: hueIdx, outfit: outfitId, hat: hatId, hatTime: tick + alpha })
    } else {
      drawBaroudeur(rc.ctx, poseName, baroudeurPhase(poseName, r, px, py, tick, alpha), r.face < 0 ? -1 : 1, px + jx, pyV + emoteBob, TS, { dashVariant: dv, hue: hueIdx, outfit: outfitId, hat: hatId, hatTime: tick + alpha })
    }
    if (vol) rc.ctx.restore()
    if (stretch) rc.ctx.restore()
    if (kGeant > 1) rc.ctx.restore()
  }
  // LE MAILLET EN MAIN (05/08) : posé sur l'épaule au repos, grand arc vers l'avant pendant la
  // frappe — la fenêtre du swing est une fonction PURE de mailletReadyAt (posé à tick+CD par la
  // sim), rien à suivre côté client. Rangé pendant une forme (le géant ne le montre pas).
  if (r.maillet && r.geantUntil <= tick) {
    // Le maillet suit SA frappe si un coup est en cours (corps et arme sur la même horloge),
    // sinon l'ancrage de la posture de porteur, sinon la prise neutre (chute, échelle...).
    const pp = PORTEURS[PORTE_CHOISIE]!
    const T0 = pp.taille * TS
    const anc = frappeEnCours
      ? { dx: frappeEnCours.dx * TS, dy: frappeEnCours.dy * TS }
      : porteur ? { dx: pp.dx * TS, dy: pp.dy * TS } : { dx: priseRepos(PRISE_CHOISIE, TS).dx, dy: priseRepos(PRISE_CHOISIE, TS).dy }
    rc.ctx.save()
    rc.ctx.translate(px + TS / 2 + r.face * anc.dx, pyV + anc.dy)
    if (r.face < 0) rc.ctx.scale(-1, 1)
    let ang = frappeEnCours ? frappeEnCours.ang : porteur ? pp.ang : priseRepos(PRISE_CHOISIE, TS).ang
    if (frappeEnCours) {
      peintEffetFrappe(rc.ctx, frappeEnCours, T0) // chaque frappe a SON effet visuel
    } else if (porteur) {
      // L'INERTIE DE LA MASSE : la tête RETARDE sur le corps (bat en marche, respire à l'arrêt).
      const bouge = prev ? prev.x !== r.x : false
      ang += bouge ? Math.sin(px * 0.26) * pp.balanceMarche : Math.sin((tick + alpha) * 0.055) * pp.balanceRepos
    }
    rc.ctx.rotate(ang)
    mailletPapier(rc.ctx, frappeEnCours || porteur ? T0 : priseRepos(PRISE_CHOISIE, TS).taille, MAILLET_CHOISI)
    rc.ctx.restore()
  }
  if (bp === 'apex') { // acrobate : 2 étincelles qui scintillent à l'apex (l'instant de grâce)
    const f2 = (tick >> 1) & 1
    rc.ctx.save(); rc.ctx.globalAlpha = 0.9; rc.ctx.fillStyle = '#fff3c4'
    const ax = px + (f2 ? 7 : TS - 9), ay = py + 5 + f2 * 5
    rc.ctx.fillRect(ax, ay - 3, 2, 8); rc.ctx.fillRect(ax - 3, ay, 8, 2)
    rc.ctx.restore()
  }
  if (trampAnim && bv === 3 && bEl < bRise) { // propulseur : onde de choc HORIZONTALE au pad
    const e = bEl + alpha
    if (e >= 1 && e < 7) {
      const kk = (e - 1) / 6
      const padY = py + e * 2 * PX_PER_SUB + TS // le pad reste fixe : on redescend d'autant que la montée
      rc.ctx.save(); rc.ctx.globalAlpha = 0.5 * (1 - kk); rc.ctx.strokeStyle = '#f6f8fa'; rc.ctx.lineWidth = 2
      rc.ctx.beginPath(); rc.ctx.ellipse(px + TS / 2, padY, 6 + kk * 44, (6 + kk * 44) * 0.32, 0, 0, Math.PI * 2); rc.ctx.stroke()
      rc.ctx.restore()
    }
  }
  if (charging) { // 5 éclats convergent pendant l'accroupi (le flash de charge, 66 ms)
    const kk = Math.min(1, Math.max(0, 1 - (r.dashChargeUntil - (tick + alpha)) / 2))
    rc.ctx.save()
    for (let i = 0; i < 5; i++) {
      const ang = i * (Math.PI * 2 / 5) - Math.PI / 2, rr = 30 * (1 - kk) + 8
      const ex = Math.round(px + TS / 2 + Math.cos(ang) * rr), ey = Math.round(py + 20 + Math.sin(ang) * rr * 0.8)
      rc.ctx.globalAlpha = 0.45 + 0.55 * kk
      rc.ctx.fillStyle = i % 2 ? '#6afcff' : '#cfeaff'
      rc.ctx.fillRect(ex, ey - 3, 2, 8); rc.ctx.fillRect(ex - 3, ey, 8, 2)
    }
    rc.ctx.restore()
  }
  // Objet porté : PLUS d'icône au-dessus de la tête du coureur (retour Claude-Alain 16/07 soir) —
  // le bouton tactile OBJET l'affiche déjà, et le bandeau du bas la montre au clavier (renderHud).
  // Les MOINES armés gardent la leur (drawMonk) : c'est une info de DANGER, pas un rappel.
  // LA COURONNE MAUDITE (05/08) sur la tête : elle brille, on sait toujours qui est riche et
  // maudit. Elle flotte d'un cheveu (elle pèse, mais elle est vivante).
  if (r.couronne) {
    const cy = pyV - TS * 0.22 + Math.sin((tick + alpha) * 0.14) * 1.5
    drawBloom(rc.ctx, px, cy, '255,198,31', TS * 0.5, tick)
    blitObjet(rc.ctx, 'couronne', TS * 0.62, px + TS * 0.19, cy)
  }
  if (r.stunUntil > tick) drawStun(rc.ctx, px, py, tick)
  // Jauge de SOUFFLE (EAU 16/07, agrandie sur retour Claude-Alain : « trop discrète ») : visible
  // UNIQUEMENT immergé (breath < plein ⟺ sous l'eau). Barre LARGE au-dessus de la tête, liseré
  // sombre, 3 couleurs (bleu > 50 % > orange > 25 % > rouge) et CLIGNOTE en zone critique.
  if (r.breath < BREATH_TICKS) {
    const frac = r.breath / BREATH_TICKS
    const BW = 32, BH = 5
    const bx = px + TS / 2 - BW / 2, by = py - 10
    const ctx2 = rc.ctx
    ctx2.save()
    if (frac < 0.25 && (tick >> 2) % 2 === 0) ctx2.globalAlpha = 0.45 // clignote (4 ticks on/off)
    // REFONTE (29/07) : c'était un rectangle à liseré net, du langage de l'ancien
    // HUD, posé à l'endroit le plus regardé de l'écran — juste au-dessus de la
    // tête. Elle devient une BANDE DE PAPIER : ombre portée franche (comme tout
    // ce qui compte), bord déchiré, aucun contour. Les trois couleurs et le
    // clignotement critique ne bougent pas, ils disent la règle.
    ctx2.fillStyle = 'rgba(46, 34, 20, 0.34)'
    ctx2.fillRect(bx + 2, by + 3, BW, BH)                              // l'ombre, décalée
    ctx2.fillStyle = 'rgba(242, 228, 204, 0.9)'
    ctx2.fillRect(bx - 1, by - 1, BW + 2, BH + 2)                      // la bande nue
    const col = frac < 0.25 ? '#ff5f57' : frac < 0.5 ? '#ffb13d' : '#7ec4ef'
    const w = Math.max(2, Math.round(BW * frac))
    ctx2.fillStyle = col
    ctx2.fillRect(bx, by, w, BH)
    // le bord déchiré du remplissage : c'est ce qui le fait lire comme du papier
    // découpé et non comme une jauge. Déterministe (dérivé de x), donc stable.
    for (let i = 0; i < BW; i += 3) {
      if (i > w) break
      ctx2.fillRect(bx + i, by - 1, 3, 1 + ((bruit(bx + i, by) * 2) | 0))
    }
    ctx2.restore()
  }
}

// `near` = un coureur vivant est à portée (≤ 2 cases) du moine ; calculé dans render() à partir
// des positions courantes (lecture seule, cosmétique). Quand il nargue, le moine alterne « bras
// levés » (frame narguer) et frame de marche au rythme de `tick` (horloge de SIMULATION, lue
// seule -> geste cohérent en solo comme en rejeu réseau, sans état d'animation ajouté). La
// narquerie ne remplace QUE la marche au sol : trappé / porté / chute / échelle priment.
function monkFrame(prev: Monk | null, m: Monk, screenX: number, near: boolean, tick: number): SpriteRef {
  if (m.trappedCell >= 0) return MONK_FRAMES.trapped
  if (m.carried) return MONK_FRAMES.carried
  if (m.falling) return pick2(MONK_FRAMES.run, Math.floor(screenX / 8)) // pas de frame de chute dédiée
  if (prev && prev.y !== m.y) return pick2(MONK_FRAMES.climb, (m.x + m.y) >> 2)
  if (near && tick % 8 < 4) return MONK_FRAMES.narguer // bras levés 1 frame sur 2 -> petit geste de narquerie
  return pick2(MONK_FRAMES.run, Math.floor(screenX / 8))
}

// Teinte du BÂTISSEUR (17/07) : violet du moine normal (~275°) + 120° -> robe OCRE de chantier.
// Appliquée au rendu (drawTinted, mécanisme des avatars) : aucune ligne de feuille consommée.
const BATISSEUR_HUE = 120

function drawMonk(rc: RenderCtx, prev: Monk | null, m: Monk, alpha: number, tick: number, near: boolean, mood: 'alert' | 'search' | 'idle' | null = null, monkPanique = false): void {
  if (m.respawnAt !== 0) return // mort / en attente de réapparition : invisible
  const { px, py } = lerpPos(prev, m, alpha)
  // EMMURÉ (31/07) : il est VIVANT dans la brique. On ne dessine plus le moine du tout — juste
  // deux gros yeux qui roulent dans le mur. Pas d'ombre au sol : il n'est plus posé sur rien.
  if (m.emmure) { dessinFossile(rc.ctx, px, py, tick, TS); return }
  // MOINE ENROULÉ (06/08, « tout le monde doit se prendre dans la toile ») : le même cocon que
  // les joueurs, en teinte de moine. Il se débat cinq secondes, tout seul.
  if (m.momieUntil > tick) { drawCocon(rc.ctx, px, py, tick + alpha, -1, 3); return }
  // SPECTRE DE LA MEUTE (05/08) : le même moine, TRANSLUCIDE et nimbé de rouge — on le
  // reconnaît en une image comme « pas d'ici, et là pour quelqu'un ». Il pâlit quand son
  // échéance approche (les deux dernières secondes) : sa fin se voit venir.
  if (m.spectre) {
    const reste = m.expireAt - (tick + alpha)
    const a = 0.42 + 0.12 * Math.sin((tick + alpha) * 0.22) - (reste < 60 ? (1 - reste / 60) * 0.22 : 0)
    drawBloom(rc.ctx, px, py, '200,60,60', TS * 0.6, tick)
    rc.ctx.save()
    rc.ctx.globalAlpha = Math.max(0.12, a)
    if (m.face < 0) { rc.ctx.translate(px + TS / 2, 0); rc.ctx.scale(-1, 1); rc.ctx.translate(-(px + TS / 2), 0) }
    blitMoine(rc.ctx, 'normal', TS, px, py - Math.abs(Math.sin((tick + alpha) * 0.13)) * 3) // il flotte
    rc.ctx.restore()
    return
  }
  drawGroundShadow(rc.ctx, px, py) // ombre au sol — elle ne saute PAS : l'écart donne la hauteur
  // SAUT DU SAUTEUR (05/08, retour Claude-Alain : « on dirait qu'ils flottent dessus le trou »).
  // La sim glisse x en ligne droite (m.hop) ; l'arc est un habillage de rendu pur dérivé de hop.
  const saut = poseDuSaut(prev?.hop ?? 0, m.hop, alpha, 2 * SUB)
  const pyS = saut ? py - Math.round(saut.dy * TS) : py
  const frame = monkSprite(monkFrame(prev, m, px, near, tick), m.kind)
  // REFONTE « papier découpé » (lot 5) : les cinq moines partagent une silhouette,
  // la capuche, et ne se distinguent que par la couleur et UN attribut (ressort,
  // sac, couronne, truelle). C'est ce qui permet de les reconnaître en fuyant.
  if (m.dormant) {
    // STATUE (gong d'or) : le même moine, en pierre, sans ornement.
    blitMoine(rc.ctx, 'statue', TS, px, py)
    return
  }
  rc.ctx.save()
  if (m.face < 0) { rc.ctx.translate(px + TS / 2, 0); rc.ctx.scale(-1, 1); rc.ctx.translate(-(px + TS / 2), 0) }
  if (saut) {
    const cx = px + TS / 2, cy = pyS + TS * 0.55
    rc.ctx.translate(cx, cy)
    if (m.stunUntil > tick) {
      // BUMP DE MAILLET (05/08, « une animation plus parlante et spectaculaire ») : un vol
      // AVEC étourdissement est un coup encaissé — le moine part en VRILLE, deux tours
      // complets sur la durée du vol. Un saut de sauteur (sans stun) garde son arc penché.
      rc.ctx.rotate(saut.t * Math.PI * 4)
    } else {
      // Penché vers l'avant + étiré dans le sens du mouvement. Appliqué APRÈS le flip : le
      // même signe de rotation « penche vers l'avant » des deux côtés, le miroir fait le reste.
      rc.ctx.rotate(saut.lean)
      rc.ctx.scale(1 / saut.sy, saut.sy)
    }
    rc.ctx.translate(-cx, -cy)
  }
  blitMoine(rc.ctx, m.kind, TS, px, pyS)
  rc.ctx.restore()
  // MOINES ARMÉS (16/07) : l'objet ramassé s'affiche au-dessus de sa tête (même icône que le
  // coureur) -> on VOIT qu'il est dangereux (bombe/tazer) ou insensible aux trous (marteau).
  // (pyS : pendant un saut, l'objet et la pièce serrée suivent le corps dans l'arc.)
  if (m.item) drawCarriedItem(rc, m.item, px, pyS, 0)
  if (m.carried) drawMonkGold(rc.ctx, px, pyS, m.face, tick) // LE VOLEUR : il serre ta pièce
  if (m.stunUntil > tick) drawStun(rc.ctx, px, pyS, tick)
  if (m.work > 0) drawWork(rc.ctx, px, py, m.face, tick) // BÂTISSEUR au chantier : coups de truelle
  if (m.kind === 'boss') {
    drawBossVies(rc.ctx, px, py, m.hits, tick) // jauge segmentée + butin, au-dessus de la couronne
    // IL NARGUE quand on l'a coincé — et seulement là (cf. drawBossBulle). Un emmuré ne parle pas :
    // il n'est plus qu'une paire d'yeux dans la brique, et la branche `emmure` sort bien avant ici.
    if (m.trappedCell >= 0 || m.stunUntil > tick) drawBossBulle(rc.ctx, px, py, m.spawnCell, tick)
  }
  if (m.trappedCell >= 0) drawCaptif(rc.ctx, px, py, tick, monkPanique) // il gigote, et panique si on rebouche
  if (mood) drawMood(rc.ctx, px, py, mood, tick) // ÉMOTIONS (smartMonks) : !, ?, z au-dessus de la capuche
}

/** LE TROU QU'ON REBOUCHE (31/07). La terre monte du bas, bord déchiré comme tout le reste du
 *  papier découpé, et la surface tremble au rythme des coups. `avance` va de 0 à 1. */
function drawRebouchage(ctx: CanvasRenderingContext2D, x: number, y: number, avance: number, t: number): void {
  const h = Math.max(2, Math.round(TS * Math.min(1, avance)))
  const top = y + TS - h
  ctx.save()
  ctx.fillStyle = '#7d5a3a'                                   // la terre remise
  ctx.fillRect(x, top, TS, h)
  ctx.fillStyle = '#9a7048'                                   // la crête, plus claire
  for (let i = 0; i < TS; i += 4) {
    const bosse = ((i * 7 + Math.floor(t * 0.4)) % 3) - 1     // tremble au rythme des coups
    ctx.fillRect(x + i, top + bosse, 4, 3)
  }
  ctx.restore()
}

/* LE MOINE EMMURÉ (31/07) : son dessin vit dans `client/emmure-papier.ts` avec les trois autres
   candidates, pour que la planche /pages/emmure.html peigne avec le MOTEUR et non une imitation.
   Claude-Alain a tranché sur cette planche le 31/07 : c'est LE FOSSILE, sa silhouette
   encapuchonnée imprimée en creux dans la brique. Les trois autres restent sur la planche.
   ⚠️ `dessinFossile` est livré EXACTEMENT tel qu'il l'a validé : ne pas le « peaufiner » après
   coup, ce serait livrer autre chose que ce qui a été choisi. */

/** LE MOINE CAPTIF (31/07, demande Claude-Alain « une animation fun du moine captif »).
 *  Il ne peut plus rien faire : il gigote. Deux mains qui agrippent le rebord et lâchent prise en
 *  alternance, la capuche qui tressaute, et des gouttes d'effort qui giclent. Tout est dérivé du
 *  tick de SIMULATION, donc identique sur les trois écrans d'une partie en ligne. */
export function drawCaptif(ctx: CanvasRenderingContext2D, px: number, py: number, t: number, panique = false): void {
  // PANIQUE (31/07) : pendant que le joueur rebouche, il s'agite deux fois plus vite et transpire
  // deux fois plus. C'est ce qui rend les quatre coups de pioche lisibles côté moine.
  const phase = Math.sin(t * (panique ? 0.8 : 0.35)) * (panique ? 1.8 : 1)
  ctx.save()
  // les mains, posées sur le rebord du trou, qui glissent alternativement
  ctx.fillStyle = '#f2e4cc'
  const mg = py - 2 + phase * 2, md = py - 2 - phase * 2
  ctx.fillRect(px + 5, mg, 7, 5)
  ctx.fillRect(px + TS - 12, md, 7, 5)
  ctx.fillStyle = 'rgba(46, 34, 20, 0.34)'
  ctx.fillRect(px + 5, mg + 5, 7, 2)
  ctx.fillRect(px + TS - 12, md + 5, 7, 2)
  // les gouttes d'effort : deux, en opposition de phase, qui montent et s'effacent
  for (let i = 0; i < (panique ? 4 : 2); i++) {
    const k = ((t * (panique ? 0.13 : 0.06) + i * 0.25) % 1)
    ctx.globalAlpha = 1 - k
    ctx.fillStyle = '#bfe4ff'
    const dx = px + 3 + ((i * 11) % (TS - 8)), dy = py - 4 - k * 12
    ctx.fillRect(dx, dy, 3, 4)
  }
  ctx.restore()
}

/** LES VIES DU BOSS (31/07, demande Claude-Alain « un boss à plusieurs vies, ça s'affiche »).
 *  LA JAUGE ET LE BUTIN (choix Claude-Alain 01/08, maquette /pages/boss-hud.html, solution 2).
 *  Une barre SEGMENTÉE au-dessus de la tête — trois segments, pas un dégradé : on doit pouvoir
 *  COMPTER ce qui reste — et, accolé, le butin annoncé en clair : une pièce et « ×3 ».
 *
 *  ⚠️ Ça remplace les trois lanternes de la veille, qui faisaient 4 px de rayon : à bout de bras
 *  sur un téléphone, on ne les comptait pas. Et surtout rien n'annonçait l'or : le boss lâche trois
 *  pièces à sa dernière vie, une récompense que personne ne pouvait deviner, donc qui ne motivait
 *  aucun détour. Un trésor qu'on ignore n'est pas un trésor.
 *
 *  Un segment perdu reste VISIBLE (creux clair) : sinon on ne sait pas combien il en avait. */
export function drawBossVies(ctx: CanvasRenderingContext2D, px: number, py: number, vies: number, tick: number, butin = BOSS_GOLD_REWARD): void {
  const w = 46, h = 7, x0 = px + TS / 2 - w / 2, y0 = py - 20
  ctx.save()
  ctx.fillStyle = 'rgba(20, 14, 10, 0.55)'                      // le fourreau, qui décolle du décor
  ctx.beginPath(); ctx.roundRect(x0 - 2, y0 - 2, w + 4, h + 4, 4); ctx.fill()
  const sw = (w - 4) / 3
  for (let i = 0; i < 3; i++) {
    const sx = x0 + i * (sw + 2)
    if (i < vies) {
      const derniere = vies === 1
      ctx.globalAlpha = derniere ? 0.72 + 0.28 * Math.sin(tick * 0.24) : 1
      ctx.fillStyle = derniere ? '#e0605a' : '#ffb13d'          // la dernière vire au rouge ET palpite
    } else {
      ctx.globalAlpha = 1
      ctx.fillStyle = 'rgba(242, 228, 204, 0.22)'               // perdu, mais toujours compté
    }
    ctx.beginPath(); ctx.roundRect(sx, y0, sw, h, 2); ctx.fill()
  }
  ctx.globalAlpha = 1
  // LE BUTIN : une pièce et son compte. C'est l'indication qui manquait complètement.
  const bx = x0 + w + 11, by = y0 + h / 2
  ctx.fillStyle = 'rgba(46, 34, 20, 0.34)'
  ctx.beginPath(); ctx.arc(bx + 1, by + 2, 5.5, 0, Math.PI * 2); ctx.fill()
  ctx.fillStyle = '#ffcf3f'
  ctx.beginPath(); ctx.arc(bx, by, 5.5, 0, Math.PI * 2); ctx.fill()
  ctx.fillStyle = '#ffe9a8'
  ctx.beginPath(); ctx.arc(bx - 1.4, by - 1.7, 2.3, 0, Math.PI * 2); ctx.fill()
  ctx.fillStyle = '#ffe9a8'
  ctx.font = '700 11px ui-monospace, Menlo, monospace'
  ctx.textAlign = 'left'; ctx.textBaseline = 'middle'
  ctx.fillText('\u00d7' + butin, bx + 8, by + 1)
  ctx.restore()
}

/** LES PROVOCATIONS DU BOSS (01/08, demande Claude-Alain : « je veux que le boss ait des animations
 *  provocantes qui montrent qu'il a des pièces, pourquoi pas des bulles de texte. Quand il est
 *  enfermé ou bloqué il encourage le joueur à venir chercher le butin »).
 *
 *  ⚠️ PUREMENT COSMÉTIQUE. Rien ici ne touche à l'état simulé : le texte est choisi à partir du
 *  TICK et de la CASE du boss, jamais d'un tirage — les trois écrans d'une partie en ligne lisent
 *  donc la même réplique au même moment, sans qu'un seul octet d'état soit ajouté.
 *
 *  ⚠️ Il ne parle QUE bloqué (piégé dans un trou, emmuré, étourdi). Un boss qui jacasse en
 *  permanence devient un décor sonore qu'on cesse de lire ; un boss qui nargue précisément quand
 *  on l'a coincé, ça se remarque — et c'est le moment où l'on décide d'aller le chercher ou non. */
const NARGUES = ['Viens le chercher !', 'J\u2019ai ton or !', 'Meme pas cap !', 'Tout ca pour moi !', 'Approche donc...']

export function drawBossBulle(ctx: CanvasRenderingContext2D, px: number, py: number, cell: number, tick: number): void {
  // Une réplique change toutes les 4 s environ, et dépend de la case : deux boss coincés côte à
  // côte ne disent pas la même chose au même instant.
  const i = (Math.floor(tick / 120) + cell) % NARGUES.length
  const txt = NARGUES[i]!
  // Le petit sursaut : la bulle respire, sinon elle se lit comme une étiquette collée.
  const bob = Math.sin(tick * 0.14) * 1.4
  ctx.save()
  ctx.font = '700 10px ui-monospace, Menlo, monospace'
  const w = ctx.measureText(txt).width + 14, h = 18
  // ⚠️ 52 px au-dessus, pas 40 : la jauge de vie occupe la bande py-22..py-13, et à 40 la bulle
  // s'asseyait dessus — vu sur capture, sa queue traversait les segments.
  const x0 = px + TS / 2 - w / 2, y0 = py - 52 + bob
  ctx.fillStyle = 'rgba(20, 14, 10, 0.34)'                      // l'ombre, comme tout le papier
  ctx.beginPath(); ctx.roundRect(x0 + 2, y0 + 3, w, h, 6); ctx.fill()
  ctx.fillStyle = '#f6efe0'
  ctx.beginPath(); ctx.roundRect(x0, y0, w, h, 6); ctx.fill()
  ctx.beginPath()                                               // la queue de la bulle, vers sa tête
  ctx.moveTo(px + TS / 2 - 4, y0 + h - 1)
  ctx.lineTo(px + TS / 2 + 1, y0 + h + 6)
  ctx.lineTo(px + TS / 2 + 5, y0 + h - 1)
  ctx.closePath(); ctx.fill()
  ctx.fillStyle = '#3a2c1e'
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
  ctx.fillText(txt, px + TS / 2, y0 + h / 2 + 1)
  ctx.restore()
}

// LE MOINE QUI T'A PIQUÉ UNE PIÈCE (31/07, demande Claude-Alain : « les moines qui ont une pièce
// doivent avoir un visuel qui le montre, quelque chose de fun »).
//
// Trois signes, du plus lointain au plus proche, pour qu'on repère le voleur d'un bout à l'autre
// de l'écran et qu'on comprenne pourquoi le compteur ne bouge plus :
//   · un HALO doré qui bat autour de lui — visible même quand le moine est minuscule au loin ;
//   · la PIÈCE serrée devant lui, côté marche, qui rebondit comme s'il courait avec ;
//   · une TRAÎNÉE de paillettes derrière lui : il se sauve avec, et ça se voit.
//
// ⚠️ Tout est piloté par le TICK de simulation, jamais par l'horloge murale : même règle que
// drawMood. Sinon le rejeu, le fantôme et le multi ne montreraient pas la même image.
// ⚠️ Appelé APRÈS le ctx.restore() du miroir : on est en espace NON retourné, d'où le `face`
// explicite. Dessiné à la main plutôt qu'avec objetPapier() : la pièce au sol fait 34 px de
// diamètre, bien trop pour tenir dans les mains d'un moine sans le masquer.
function drawMonkGold(ctx: CanvasRenderingContext2D, sx: number, sy: number, face: -1 | 1, tick: number): void {
  const cx = sx + TS / 2, cy = sy + TS / 2
  const bat = (Math.sin(tick * 0.16) + 1) / 2            // 0..1, respiration lente du halo

  ctx.save()
  // ⚠️ MODE ADDITIF pour tout ce qui brille. Premier essai en peinture normale : du jaune à 20 %
  // d'opacité sur un fond violet sombre donne un anneau BRUN BOUEUX qui se lit comme une ombre,
  // pas comme une lueur. `lighter` ajoute la lumière au lieu de la mélanger — c'est ce qui fait
  // qu'un halo ressemble à un halo.
  ctx.globalCompositeOperation = 'lighter'

  // 1. LES ÉCLATS — six rayons courts qui tournent lentement autour du voleur. Repérable d'un
  // bout à l'autre de l'écran, là où une pièce de 16 px se perdrait dans le décor.
  const rot = tick * 0.025
  for (let k = 0; k < 6; k++) {
    const a = rot + (k * Math.PI) / 3
    const r0 = TS * 0.40 + bat * 2, r1 = r0 + 6 + bat * 4
    ctx.strokeStyle = rgbaHex('#ffd23f', 0.30 + bat * 0.30)
    ctx.lineWidth = 3; ctx.lineCap = 'round'
    ctx.beginPath()
    ctx.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0)
    ctx.lineTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1)
    ctx.stroke()
  }
  // Et l'anneau qui bat, fin et franc plutôt que large et pâle.
  ctx.strokeStyle = rgbaHex('#ffdf6a', 0.26 + bat * 0.26)
  ctx.lineWidth = 2
  ctx.beginPath(); ctx.arc(cx, cy, TS * 0.38 + bat * 1.5, 0, 7); ctx.stroke()

  // 2. LA TRAÎNÉE — quatre paillettes derrière lui, qui s'effacent en s'éloignant : il se sauve
  // avec ta pièce, et ça doit se voir même de dos.
  for (let k = 0; k < 4; k++) {
    const phase = ((tick * 0.07) + k * 0.25) % 1
    const d = TS * (0.32 + phase * 0.62)
    ctx.fillStyle = rgbaHex('#ffd23f', (1 - phase) * 0.75)
    const t = 6 - phase * 4
    ctx.fillRect(cx - face * d - t / 2, cy - 6 + Math.sin((tick + k * 9) * 0.3) * 6, t, t)
  }
  ctx.globalCompositeOperation = 'source-over'   // la pièce, elle, se peint normalement

  // 3. LA PIÈCE, serrée devant lui, qui rebondit au rythme de sa course.
  const px = cx + face * 15
  const py = cy + 3 + Math.sin(tick * 0.26) * 2.5
  ctx.fillStyle = '#2e2214'                              // le trait d'encre du style papier
  ctx.beginPath(); ctx.arc(px, py, 9.5, 0, 7); ctx.fill()
  ctx.fillStyle = '#ffc61f'
  ctx.beginPath(); ctx.arc(px, py, 8, 0, 7); ctx.fill()
  ctx.fillStyle = rgbaHex('#fff8d8', 0.92)               // le même reflet que la pièce au sol
  ctx.beginPath(); ctx.arc(px - 2.5, py - 2.5, 2.8, 0, 7); ctx.fill()
  ctx.restore()
}

/** `#rrggbb` + alpha -> `rgba(...)`. Local : papier.ts ne l'exporte pas et render.ts n'en dépend pas. */
function rgbaHex(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
}

// Coups de truelle du BÂTISSEUR (17/07) : étincelles dorées rythmées au bord du trou qu'il
// rebouche (devant lui, au niveau du sol). Pur affichage, piloté par le tick de simulation.
function drawWork(ctx: CanvasRenderingContext2D, sx: number, sy: number, face: -1 | 1, tick: number): void {
  if (Math.floor(tick / 4) % 2 === 0) return // frappe rythmée (~4 coups/s)
  const bx = sx + TS / 2 + face * TS * 0.62, by = sy + TS - 6
  ctx.fillStyle = '#ffd23f'
  ctx.fillRect(bx - 2, by - 2, 5, 5)
  ctx.fillStyle = '#ff9a3d'
  ctx.fillRect(bx + 5, by - 7, 3, 3)
  ctx.fillRect(bx - 8, by - 6, 3, 3)
}

// ÉMOTIONS au-dessus des capuches (17/07, IA maligne seulement) : « ! » l'instant où il te voit,
// « ? » quand il enquête sur ta dernière position, « z » quand il patrouille sans but. Double
// fillText (ombre puis couleur) pour rester lisible sur tous les fonds de biome ; léger flottement
// au tick (jamais l'horloge murale : identique en solo, rejeu et multi).
function drawMood(ctx: CanvasRenderingContext2D, sx: number, sy: number, mood: 'alert' | 'search' | 'idle', tick: number): void {
  const bob = Math.sin(tick * 0.18) * 2
  const cx = sx + TS / 2, cy = sy - 8 + bob
  ctx.save()
  ctx.textAlign = 'center'
  ctx.textBaseline = 'alphabetic'
  const put = (ch: string, size: number, color: string): void => {
    ctx.font = `bold ${size}px ui-monospace, Menlo, monospace`
    ctx.fillStyle = '#14102a'
    ctx.fillText(ch, cx + 2, cy + 2)
    ctx.fillStyle = color
    ctx.fillText(ch, cx, cy)
  }
  if (mood === 'alert') put('!', 30, '#ffe45c')
  else if (mood === 'search') put('?', 26, '#f0e9d2')
  else put('z', 19, 'rgba(203, 196, 224, 0.7)')
  ctx.restore()
}

// ENNEMI AQUATIQUE (16/07 soir) : 2 frames au tick (nage/pulsation). Le piranha regarde à
// GAUCHE dans la feuille -> flip quand il va à droite ; la méduse ondule légèrement en x
// (cosmétique, dérivé du tick). Étourdi = halo drawStun ; en respawn = invisible.
function drawFish(rc: RenderCtx, prev: Fish | null, f: Fish, alpha: number, tick: number): void {
  if (f.respawnAt !== 0) return
  const { px, py } = lerpPos(prev, f, alpha)
  const frames = FISH_FRAMES[f.kind]
  const frame = pick2(frames, (tick >> 3) & 1)
  const wob = f.kind === 'meduse' ? Math.sin((tick + alpha) * 0.08 + f.homeCell) * 1.2 : 0
  rc.ctx.save()
  if (f.kind === 'piranha' && f.face > 0) { rc.ctx.translate(px + TS / 2, 0); rc.ctx.scale(-1, 1); rc.ctx.translate(-(px + TS / 2), 0) }
  blitFish(rc.ctx, f.kind, TS, px + wob, py)
  rc.ctx.restore()
  if (f.stunUntil > tick) drawStun(rc.ctx, px, py, tick)
}

// --- Fond par MONDE (thème) : dégradé de ciel + motif décoratif, construit UNE fois par monde
// dans un canvas hors écran (cache) puis blit chaque frame derrière le terrain. Purement
// cosmétique (aucun impact sur la simulation). Taille = aire de jeu (hors bandeau HUD). ---
const bgCache = new Map<WorldKey, HTMLCanvasElement>()

// Soleil (désert), en fractions de l'aire : partagé par le disque STATIQUE (drawDunes, cache) et
// son halo ANIMÉ (drawWorldDecor, redessiné au tick).
const SUN_X = 0.78, SUN_Y = 0.3
// Cristaux luminescents de la grotte (fractions W/H + teinte rgb) : gemmes + halo STATIQUES et
// scintillement ANIMÉ dessinés au MÊME endroit (fond en cache / overlay au tick).
const CAVE_GEMS: [number, number, string][] = [[0.13, 0.56, '95,222,255'], [0.86, 0.5, '186,140,255']]
// Chalet enneigé (fraction W/H) : fenêtre chaude STATIQUE + fumée ANIMÉE partent d'ici.
const CABIN_X = 0.12, CABIN_Y = 0.66
// Abysses : la SURFACE est le plafond du monde (fraction H). Les rayons STATIQUES en partent, et
// les bulles ANIMÉES y remontent -> une seule constante pour que les deux racontent la même scène.
const SEA_TOP = 0.06
// Manoir : lune (fraction W/H) — disque + halo STATIQUES, nuages qui la traversent au tick ; et
// fenêtres allumées, dont le vacillement ANIMÉ doit tomber PILE sur les carreaux peints.
const MOON_X = 0.76, MOON_Y = 0.16
const MANOR_X = 0.3, MANOR_Y = 0.44
const MANOR_WINDOWS: [number, number][] = [[-52, 22], [-16, 22], [22, 22], [-52, 66], [22, 66], [2, 100]]

// --- Boîte à outils des FONDS premium : tout est STATIQUE (peint UNE fois dans le cache de
// worldBackground), donc aucun coût par frame et aucun impact sur la simulation. ---

/** PRNG déterministe (mulberry32) : décors de fond reproductibles (étoiles, dunes, fenêtres…). */
function rng(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}
/** Dégradé vertical multi-arrêts peint sur toute l'aire : ciel de base riche d'un biome. */
function skyGrad(g: CanvasRenderingContext2D, W: number, H: number, stops: [number, string][]): void {
  const grad = g.createLinearGradient(0, 0, 0, H)
  for (const [o, c] of stops) grad.addColorStop(o, c)
  g.fillStyle = grad; g.fillRect(0, 0, W, H)
}
/** Halo radial doux : cœur `inner` -> bord transparent `outer` (soleil, lune, cristal, néon). */
function glow(g: CanvasRenderingContext2D, x: number, y: number, r: number, inner: string, outer: string): void {
  const rad = g.createRadialGradient(x, y, 0, x, y, r)
  rad.addColorStop(0, inner); rad.addColorStop(1, outer)
  g.save(); g.fillStyle = rad; g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill(); g.restore()
}
/** Remplit une silhouette (colline/dune/montagne) sous le profil y(x), jusqu'au bas de l'aire. */
function fillProfile(g: CanvasRenderingContext2D, W: number, H: number, color: string, yAt: (x: number) => number): void {
  g.fillStyle = color
  g.beginPath(); g.moveTo(0, H); g.lineTo(0, yAt(0))
  for (let x = 6; x <= W; x += 6) g.lineTo(x, yAt(x))
  g.lineTo(W, H); g.closePath(); g.fill()
}
/** Profil de collines douces : base - somme de sinus (couches lisses et lointaines). */
function hills(baseY: number, waves: [number, number, number][]): (x: number) => number {
  return (x) => { let y = baseY; for (const [a, f, p] of waves) y -= a * Math.sin(x * f + p); return y }
}
/** Profil de crête dentelée (montagnes / mesas) : pics linéaires interpolés, semés par `rand`. */
function peaks(W: number, baseY: number, minH: number, maxH: number, n: number, rand: () => number): (x: number) => number {
  const ys: number[] = []
  for (let i = 0; i < n; i++) ys.push(baseY - (minH + rand() * (maxH - minH)))
  return (x) => {
    const s = Math.max(0, Math.min(n - 1.001, (x / W) * (n - 1)))
    const i = Math.floor(s), f = s - i
    return (ys[i] ?? baseY) * (1 - f) + (ys[i + 1] ?? baseY) * f
  }
}
/** Champ d'étoiles PRNG dans le haut de l'aire : petits points d'éclat varié. */
function starfield(g: CanvasRenderingContext2D, W: number, H: number, count: number, seed: number, maxYFrac: number): void {
  const R = rng(seed)
  for (let i = 0; i < count; i++) {
    const x = R() * W, y = R() * H * maxYFrac, s = R() < 0.12 ? 2 : 1
    g.fillStyle = `rgba(255,255,255,${0.25 + R() * 0.7})`
    g.fillRect(x, y, s, s)
  }
}
/** Vignette radiale : assombrit doucement les bords pour concentrer le regard (profondeur). */
function vignette(g: CanvasRenderingContext2D, W: number, H: number, alpha: number): void {
  const vg = g.createRadialGradient(W / 2, H * 0.45, H * 0.3, W / 2, H * 0.5, H * 0.95)
  vg.addColorStop(0, 'rgba(0,0,0,0)'); vg.addColorStop(1, `rgba(0,0,0,${alpha})`)
  g.fillStyle = vg; g.fillRect(0, 0, W, H)
}
/** Rangée de sapins enneigés (profondeur) : cône à étages + cape de neige, semée par `rand`. */
function drawFirRow(g: CanvasRenderingContext2D, W: number, baseY: number, n: number, minH: number, maxH: number, color: string, snowAlpha: number, rand: () => number): void {
  for (let i = 0; i < n; i++) {
    const cx = (i + 0.5) * (W / n) + (rand() - 0.5) * 10
    const h = minH + rand() * (maxH - minH), w = h * 0.72
    g.fillStyle = color
    g.beginPath(); g.moveTo(cx, baseY - h); g.lineTo(cx - w / 2, baseY); g.lineTo(cx + w / 2, baseY); g.closePath(); g.fill()
    g.beginPath(); g.moveTo(cx, baseY - h * 0.62); g.lineTo(cx - w * 0.4, baseY - h * 0.16); g.lineTo(cx + w * 0.4, baseY - h * 0.16); g.closePath(); g.fill()
    g.fillStyle = `rgba(255,255,255,${snowAlpha})`
    g.beginPath(); g.moveTo(cx, baseY - h); g.lineTo(cx - w * 0.22, baseY - h * 0.62); g.lineTo(cx + w * 0.22, baseY - h * 0.62); g.closePath(); g.fill()
  }
}
/** Roue dentée (usine) : dents rayonnantes + moyeu ; un éclat tourne dessus au tick (overlay). */
function drawGear(g: CanvasRenderingContext2D, cx: number, cy: number, r: number, teeth: number, body: string, edge: string): void {
  g.save(); g.translate(cx, cy); g.fillStyle = body
  for (let i = 0; i < teeth; i++) { g.rotate((Math.PI * 2) / teeth); g.fillRect(-6, -r - 8, 12, 14) }
  g.beginPath(); g.arc(0, 0, r, 0, Math.PI * 2); g.fill()
  g.fillStyle = edge; g.beginPath(); g.arc(0, 0, r * 0.5, 0, Math.PI * 2); g.fill()
  g.fillStyle = body; g.beginPath(); g.arc(0, 0, r * 0.26, 0, Math.PI * 2); g.fill()
  g.restore()
}

// GROTTE — caverne : voûte sombre, strates de roche à bord irrégulier, stalactites/stalagmites
// incurvées, cristaux luminescents (halo froid) + mousse bioluminescente. `color` = concrétions.
function drawStalactites(g: CanvasRenderingContext2D, W: number, H: number, color: string): void {
  skyGrad(g, W, H, [[0, '#080611'], [0.55, '#120f20'], [1, '#1b1428']])
  glow(g, W * 0.5, H * 0.66, W * 0.55, 'rgba(70,52,104,0.30)', 'rgba(70,52,104,0)') // lueur diffuse
  const R = rng(1337)
  const strata = ['#1d1730', '#241b31', '#191325', '#271d38'] // strates de roche à bord irrégulier
  for (let s = 0; s < 5; s++) {
    const y = H * (0.26 + s * 0.12)
    g.fillStyle = strata[s % strata.length] ?? '#1d1730'
    g.beginPath(); g.moveTo(0, y)
    for (let x = 0; x <= W; x += 44) g.lineTo(x, y + Math.sin(x * 0.011 + s * 1.7) * 9 + (R() - 0.5) * 10)
    g.lineTo(W, y + 42); g.lineTo(0, y + 42); g.closePath(); g.fill()
  }
  vignette(g, W, H, 0.5)
  const nT = 22 // stalactites (haut) : cônes incurvés, largeurs/hauteurs semées, arête claire à gauche
  for (let i = 0; i < nT; i++) {
    const x = (i + 0.5) * (W / nT) + (R() - 0.5) * 14
    const w = (W / nT) * (0.5 + R() * 0.6), h = 28 + R() * 78
    g.fillStyle = color
    g.beginPath(); g.moveTo(x - w / 2, 0); g.quadraticCurveTo(x - w * 0.12, h * 0.55, x, h); g.quadraticCurveTo(x + w * 0.12, h * 0.55, x + w / 2, 0); g.closePath(); g.fill()
    g.fillStyle = 'rgba(255,255,255,0.06)'
    g.beginPath(); g.moveTo(x - w * 0.28, 0); g.quadraticCurveTo(x - w * 0.05, h * 0.6, x, h); g.lineTo(x - w * 0.05, 0); g.closePath(); g.fill()
  }
  const nS = 18 // stalagmites (bas), décalées et plus trapues
  for (let i = 0; i < nS; i++) {
    const x = (i + 0.9) * (W / nS) + (R() - 0.5) * 12
    const w = (W / nS) * (0.45 + R() * 0.5), h = 22 + R() * 52
    g.fillStyle = color
    g.beginPath(); g.moveTo(x - w / 2, H); g.quadraticCurveTo(x - w * 0.1, H - h * 0.55, x, H - h); g.quadraticCurveTo(x + w * 0.1, H - h * 0.55, x + w / 2, H); g.closePath(); g.fill()
  }
  for (const [fx, fy, rgb] of CAVE_GEMS) { // cristaux luminescents : halo + éclats facettés
    const gx = fx * W, gy = fy * H
    glow(g, gx, gy, 78, `rgba(${rgb},0.34)`, `rgba(${rgb},0)`)
    for (let k = 0; k < 3; k++) {
      const ox = (k - 1) * 13, hh = 24 + (k % 2) * 12
      g.fillStyle = `rgba(${rgb},0.9)`
      g.beginPath(); g.moveTo(gx + ox, gy - hh); g.lineTo(gx + ox - 7, gy); g.lineTo(gx + ox, gy + 7); g.lineTo(gx + ox + 7, gy); g.closePath(); g.fill()
      g.fillStyle = 'rgba(255,255,255,0.4)'
      g.beginPath(); g.moveTo(gx + ox, gy - hh); g.lineTo(gx + ox + 2, gy); g.lineTo(gx + ox + 7, gy); g.closePath(); g.fill()
    }
  }
  for (let i = 0; i < 6; i++) glow(g, R() * W, H * (0.5 + R() * 0.45), 24, 'rgba(120,220,150,0.13)', 'rgba(120,220,150,0)') // mousse
}

// PRAIRIE — pastorale ensoleillée : ciel dégradé, soleil chaud à rayons, montagnes brumeuses,
// 3 couches de collines (perspective atmosphérique), arbre isolé, fleurs. `color` = colline proche.
function drawClouds(g: CanvasRenderingContext2D, W: number, H: number, color: string): void {
  skyGrad(g, W, H, [[0, '#5aa7e6'], [0.45, '#9fd0f0'], [0.72, '#d9eef6'], [1, '#eef7e2']])
  const R = rng(77)
  const sx = W * 0.2, sy = H * 0.16 // soleil chaud + rayons doux (god rays) + halo
  glow(g, sx, sy, 190, 'rgba(255,246,208,0.55)', 'rgba(255,246,208,0)')
  g.save(); g.globalAlpha = 0.1; g.fillStyle = '#fff6cf'
  for (let a = 0; a < 9; a++) { const ang = a * 0.5 + 0.2; g.beginPath(); g.moveTo(sx, sy); g.lineTo(sx + Math.cos(ang) * W, sy + Math.sin(ang) * W); g.lineTo(sx + Math.cos(ang + 0.09) * W, sy + Math.sin(ang + 0.09) * W); g.closePath(); g.fill() }
  g.restore()
  g.fillStyle = '#fff3c4'; g.beginPath(); g.arc(sx, sy, 34, 0, Math.PI * 2); g.fill()
  fillProfile(g, W, H, '#a9c6d8', peaks(W, H * 0.6, 40, 150, 9, rng(5))) // montagnes lointaines brumeuses
  fillProfile(g, W, H, '#8fb98a', hills(H * 0.66, [[26, 0.004, 0.6], [12, 0.011, 2]])) // colline lointaine
  fillProfile(g, W, H, '#79ad57', hills(H * 0.74, [[30, 0.005, 1.9], [14, 0.013, 0.4]])) // colline médiane
  fillProfile(g, W, H, color, hills(H * 0.82, [[26, 0.006, 3.1], [12, 0.02, 1.2]])) // colline proche
  const tx = W * 0.72, ty = H * 0.78 // arbre isolé (tronc + houppier)
  g.fillStyle = '#5b4326'; g.fillRect(tx - 4, ty, 9, 46)
  g.fillStyle = '#4f8f43'; g.beginPath(); g.arc(tx, ty - 6, 30, 0, Math.PI * 2); g.arc(tx - 22, ty + 6, 20, 0, Math.PI * 2); g.arc(tx + 22, ty + 4, 22, 0, Math.PI * 2); g.fill()
  for (let i = 0; i < 40; i++) { const fx = R() * W, fy = H * (0.82 + R() * 0.16); g.fillStyle = ['#f7d54a', '#ff7a9c', '#ffffff', '#b98bff'][i % 4] ?? '#fff'; g.beginPath(); g.arc(fx, fy, 2.4, 0, Math.PI * 2); g.fill() } // fleurs
  for (const [cx, cy, r] of [[W * 0.5, H * 0.14, 42], [W * 0.83, H * 0.22, 34]] as [number, number, number][]) { // nuages ombrés
    g.fillStyle = '#ffffff'; g.beginPath(); g.arc(cx, cy, r, 0, Math.PI * 2); g.arc(cx + r * 0.8, cy + 6, r * 0.66, 0, Math.PI * 2); g.arc(cx - r * 0.8, cy + 8, r * 0.6, 0, Math.PI * 2); g.fill()
    g.fillStyle = 'rgba(200,220,235,0.6)'; g.beginPath(); g.arc(cx + r * 0.2, cy + r * 0.4, r * 0.7, 0, Math.PI); g.fill()
  }
}

// DÉSERT — coucher de soleil : ciel ambre dégradé, grand soleil bas + halo, mesas lointaines,
// 4 couches de dunes (pâle -> chaude) à crête éclairée, rides de sable, cactus. `color` = dune proche.
function drawDunes(g: CanvasRenderingContext2D, W: number, H: number, color: string): void {
  skyGrad(g, W, H, [[0, '#d97a24'], [0.4, '#eb9e3f'], [0.7, '#f6c874'], [1, '#fbe6bb']])
  const cx = W * SUN_X, cy = H * SUN_Y
  glow(g, cx, cy, 260, 'rgba(255,231,150,0.6)', 'rgba(255,231,150,0)') // grand halo chaud
  g.fillStyle = '#fff2cf'; g.beginPath(); g.arc(cx, cy, 46, 0, Math.PI * 2); g.fill() // disque solaire
  g.save(); g.globalAlpha = 0.5; g.fillStyle = '#e6913a' // barres de nuages devant le soleil
  for (const [oy, w] of [[-6, 120], [16, 170], [34, 90]] as [number, number][]) { g.beginPath(); g.ellipse(cx - 6, cy + oy, w, 6, 0, 0, Math.PI * 2); g.fill() }
  g.restore()
  fillProfile(g, W, H, '#c98a4a', peaks(W, H * 0.56, 30, 110, 7, rng(9))) // mesas lointaines
  const layers: [number, string, [number, number, number][]][] = [
    [0.6, '#f0cf98', [[24, 0.004, 0.4]]], [0.68, '#e6bd7f', [[30, 0.005, 1.8]]],
    [0.77, '#d9a869', [[28, 0.006, 3]]], [0.86, color, [[26, 0.007, 1.1]]],
  ]
  for (const [by, col, w] of layers) { // dunes : remplissage + crête éclairée
    const prof = hills(H * by, w)
    fillProfile(g, W, H, col, prof)
    g.strokeStyle = 'rgba(255,255,255,0.14)'; g.lineWidth = 3; g.beginPath(); g.moveTo(0, prof(0))
    for (let x = 6; x <= W; x += 6) g.lineTo(x, prof(x)); g.stroke()
  }
  g.strokeStyle = 'rgba(150,100,50,0.14)'; g.lineWidth = 1 // rides de sable sur la dune proche
  for (let i = 0; i < 6; i++) { const y = H * (0.9 + i * 0.015); g.beginPath(); g.moveTo(0, y); for (let x = 0; x <= W; x += 10) g.lineTo(x, y + Math.sin(x * 0.02 + i) * 3); g.stroke() }
  for (const bx of [W * 0.34, W * 0.6]) { // cactus en silhouette
    const by = hills(H * 0.86, [[26, 0.007, 1.1]])(bx)
    g.fillStyle = '#3f6b3a'; g.fillRect(bx - 4, by - 40, 8, 40)
    g.fillRect(bx - 16, by - 30, 12, 5); g.fillRect(bx - 16, by - 44, 5, 14)
    g.fillRect(bx + 4, by - 24, 12, 5); g.fillRect(bx + 11, by - 40, 5, 16)
  }
}

// NEIGE — crépuscule polaire : ciel bleu-violet dégradé, aurore boréale, lune + étoiles, montagnes
// enneigées dentelées, 2 rangées de sapins, chalet à fenêtre chaude. `color` = collines de neige.
function drawFirs(g: CanvasRenderingContext2D, W: number, H: number, color: string): void {
  skyGrad(g, W, H, [[0, '#243459'], [0.4, '#3f5688'], [0.7, '#9fb6d4'], [1, '#dae8f4']])
  starfield(g, W, H, 60, 21, 0.4)
  glow(g, W * 0.8, H * 0.18, 70, 'rgba(226,236,247,0.5)', 'rgba(226,236,247,0)')
  g.fillStyle = '#eef4fb'; g.beginPath(); g.arc(W * 0.8, H * 0.18, 26, 0, Math.PI * 2); g.fill() // lune
  g.save() // aurore boréale STATIQUE (rubans ondulés) ; le miroitement est ajouté au tick
  for (const [col, baseY, amp] of [['#57e0a8', 0.2, 26], ['#5ac6e6', 0.26, 20], ['#b98bff', 0.32, 16]] as [string, number, number][]) {
    g.globalAlpha = 0.22
    const grad = g.createLinearGradient(0, H * (baseY - 0.06), 0, H * (baseY + 0.14)); grad.addColorStop(0, col); grad.addColorStop(1, 'rgba(0,0,0,0)')
    g.fillStyle = grad; g.beginPath(); g.moveTo(0, H * (baseY + 0.16))
    for (let x = 0; x <= W; x += 12) g.lineTo(x, H * baseY + Math.sin(x * 0.008) * amp)
    g.lineTo(W, H * (baseY + 0.16)); g.closePath(); g.fill()
  }
  g.restore()
  fillProfile(g, W, H, '#8aa0c4', peaks(W, H * 0.6, 60, 190, 8, rng(3))) // montagnes lointaines
  fillProfile(g, W, H, '#b9cbe0', peaks(W, H * 0.66, 30, 90, 12, rng(4))) // contreforts
  fillProfile(g, W, H, color, hills(H * 0.8, [[24, 0.005, 1.2], [10, 0.014, 0.3]])) // collines de neige
  drawFirRow(g, W, H * 0.72, 15, 20, 34, '#3d6b48', 0.7, rng(31)) // sapins lointains
  drawFirRow(g, W, H * 0.82, 10, 38, 58, '#2f5a3a', 1, rng(32)) // sapins proches
  const hx = W * CABIN_X, hy = H * CABIN_Y // chalet chaud (fumée animée au tick)
  g.fillStyle = '#4a3a30'; g.fillRect(hx - 26, hy, 52, 34)
  g.fillStyle = '#e9f1f7'; g.beginPath(); g.moveTo(hx - 32, hy); g.lineTo(hx, hy - 22); g.lineTo(hx + 32, hy); g.closePath(); g.fill()
  glow(g, hx + 6, hy + 14, 26, 'rgba(255,196,92,0.7)', 'rgba(255,196,92,0)'); g.fillStyle = '#ffcf6b'; g.fillRect(hx - 2, hy + 8, 14, 14)
}

// USINE NÉON — nuit industrielle cyberpunk : ciel smog dégradé, skyline d'usine à fenêtres
// allumées, cheminées, tuyaux à brides, roue dentée, flaques réfléchissantes. `color` = tuyaux.
function drawPipes(g: CanvasRenderingContext2D, W: number, H: number, color: string): void {
  skyGrad(g, W, H, [[0, '#080b12'], [0.55, '#111725'], [1, '#1c2536']])
  glow(g, W * 0.5, H * 0.2, W * 0.5, 'rgba(60,90,140,0.18)', 'rgba(60,90,140,0)') // brume smog
  const R = rng(555)
  for (let i = 0; i < 9; i++) { // skyline d'usine lointaine à fenêtres allumées
    const bx = i * (W / 9) + R() * 20, bw = 60 + R() * 70, bh = H * (0.14 + R() * 0.2)
    g.fillStyle = '#0e131f'; g.fillRect(bx, H * 0.52 - bh, bw, bh)
    for (let wx = bx + 8; wx < bx + bw - 6; wx += 14) for (let wy = H * 0.52 - bh + 8; wy < H * 0.5; wy += 16) if (R() < 0.5) { g.fillStyle = R() < 0.5 ? 'rgba(255,200,90,0.5)' : 'rgba(90,200,255,0.4)'; g.fillRect(wx, wy, 5, 7) }
  }
  for (const [bx, bw] of [[W * 0.1, 34], [W * 0.75, 40]] as [number, number][]) { g.fillStyle = '#0c1119'; g.fillRect(bx, H * 0.2, bw, H * 0.33); g.fillStyle = '#161d2b'; g.fillRect(bx, H * 0.2, bw, 6) } // cheminées
  vignette(g, W, H, 0.42)
  const hi = '#3f4a60'
  for (const y of [H * 0.32, H * 0.56, H * 0.78]) { // tuyaux horizontaux à reflet + brides
    g.fillStyle = color; g.fillRect(0, y, W, 14); g.fillStyle = hi; g.fillRect(0, y, W, 3)
    for (let x = W * 0.09; x < W; x += W * 0.19) { g.fillStyle = color; g.fillRect(x - 4, y - 4, 11, 22); g.fillStyle = hi; g.fillRect(x - 4, y - 4, 11, 2) }
  }
  for (const x of [W * 0.19, W * 0.71]) { g.fillStyle = color; g.fillRect(x, H * 0.32, 12, H * 0.46); g.fillStyle = hi; g.fillRect(x, H * 0.32, 3, H * 0.46) } // montants
  drawGear(g, W * 0.87, H * 0.6, 46, 12, '#2a3242', '#3f4a60') // roue dentée (éclat animé au tick)
  g.save(); g.globalAlpha = 0.5 // flaques réfléchissant le néon
  for (const [px, col] of [[W * 0.3, '#3df0ff'], [W * 0.62, '#ff5ad6']] as [number, string][]) { const grad = g.createLinearGradient(0, H - 30, 0, H); grad.addColorStop(0, col); grad.addColorStop(1, 'rgba(0,0,0,0)'); g.fillStyle = grad; g.fillRect(px, H - 24, 130, 24) }
  g.restore()
}

// CLASSIQUE — nuit urbaine profonde : ciel indigo dégradé, voie lactée, champ d'étoiles, lune
// gibbeuse à cratères, silhouette de toits (Lode Runner) à fenêtres allumées. Étoiles scintillantes
// + étoile filante ajoutées au tick (drawWorldDecor). Sans paramètre `color` (motif 'none').
// Une COUCHE de ville (profondeur) : silhouettes de tours à toits variés (antenne/réservoir/plat),
// fenêtres allumées + rares néons colorés, halo de brume basse pour la profondeur atmosphérique.
function cityLayer(g: CanvasRenderingContext2D, W: number, H: number, seed: number, baseY: number, color: string, winChance: number, hazeA: number): void {
  const R = rng(seed), neons = ['rgba(255,80,120,0.85)', 'rgba(90,220,255,0.85)', 'rgba(255,220,90,0.9)', 'rgba(180,120,255,0.85)', 'rgba(120,255,180,0.8)']
  let x = -20
  while (x < W + 20) {
    const w = 28 + R() * 92, h = (0.07 + R() * 0.24) * H, tx = x, ty = H * baseY - h
    g.fillStyle = color; g.fillRect(tx, ty, w, h + H)
    const roof = R() // toit varié
    if (roof < 0.28) { g.fillRect(tx + w * 0.42, ty - 14, 3, 14); g.fillStyle = 'rgba(255,60,60,0.7)'; g.fillRect(tx + w * 0.42 - 1, ty - 15, 5, 3); g.fillStyle = color } // antenne + feu rouge
    else if (roof < 0.5) g.fillRect(tx + w * 0.28, ty - 9, w * 0.44, 9) // réservoir/superstructure
    for (let wx = tx + 6; wx < tx + w - 5; wx += 11) for (let wy = ty + 8; wy < H * baseY; wy += 15) if (R() < winChance) {
      if (R() < 0.09) { g.fillStyle = neons[(R() * neons.length) | 0]!; g.fillRect(wx, wy, 3, 8) }
      else { g.fillStyle = R() < 0.5 ? 'rgba(255,205,120,0.55)' : 'rgba(150,190,255,0.4)'; g.fillRect(wx, wy, 4, 6) }
    }
    x += w + 3 + R() * 16
  }
  if (hazeA > 0) { const hz = g.createLinearGradient(0, H * baseY - H * 0.22, 0, H * baseY + H * 0.08); hz.addColorStop(0, 'rgba(70,52,100,0)'); hz.addColorStop(1, `rgba(70,52,100,${hazeA})`); g.fillStyle = hz; g.fillRect(0, H * baseY - H * 0.22, W, H * 0.32) }
}
// Dirigeable lointain (point d'intérêt du ciel) : enveloppe + nacelle + feux de position + halo.
function drawAirship(g: CanvasRenderingContext2D, x: number, y: number): void {
  glow(g, x, y, 64, 'rgba(120,130,210,0.12)', 'rgba(120,130,210,0)')
  g.save(); g.fillStyle = '#241f3c'; g.beginPath(); g.ellipse(x, y, 46, 14, -0.06, 0, Math.PI * 2); g.fill()
  g.fillStyle = '#38315a'; g.beginPath(); g.ellipse(x - 4, y - 3, 40, 9, -0.06, 0, Math.PI * 2); g.fill()
  g.fillStyle = '#4a4270'; g.beginPath(); g.ellipse(x - 14, y - 5, 16, 4, -0.06, 0, Math.PI * 2); g.fill() // reflet lune
  g.fillStyle = '#16122a'; g.fillRect(x - 9, y + 12, 18, 5)
  g.fillStyle = 'rgba(255,220,130,0.95)'; g.fillRect(x - 7, y + 13, 2, 2); g.fillRect(x + 5, y + 13, 2, 2)
  g.fillStyle = 'rgba(255,70,70,0.95)'; g.fillRect(x + 43, y - 1, 3, 3)
  g.restore()
}
function drawStars(g: CanvasRenderingContext2D, W: number, H: number): void {
  // ciel : nuit profonde -> violet -> lueur de pollution lumineuse (rose/ambre) près de l'horizon urbain
  skyGrad(g, W, H, [[0, '#07051a'], [0.38, '#0c0924'], [0.66, '#170f33'], [0.85, '#2b1a3e'], [1, '#3e2542']])
  g.save(); g.translate(W * 0.55, H * 0.28); g.rotate(-0.5) // voie lactée
  const mw = g.createLinearGradient(0, -95, 0, 95); mw.addColorStop(0, 'rgba(120,120,200,0)'); mw.addColorStop(0.5, 'rgba(155,150,235,0.13)'); mw.addColorStop(1, 'rgba(120,120,200,0)')
  g.fillStyle = mw; g.fillRect(-W, -95, W * 2, 190); g.restore()
  starfield(g, W, H, 190, 99, 0.7)
  const mx = W * 0.82, my = H * 0.17 // lune + halo + god ray oblique
  glow(g, mx, my, 165, 'rgba(240,240,255,0.42)', 'rgba(240,240,255,0)')
  g.save(); g.globalCompositeOperation = 'lighter'
  const ray = g.createLinearGradient(mx, my, mx - W * 0.5, my + H * 0.42); ray.addColorStop(0, 'rgba(228,234,255,0.16)'); ray.addColorStop(1, 'rgba(228,234,255,0)')
  g.fillStyle = ray; g.beginPath(); g.moveTo(mx + 20, my - 20); g.lineTo(mx - W * 0.5, my + H * 0.3); g.lineTo(mx - W * 0.36, my + H * 0.52); g.closePath(); g.fill(); g.restore()
  g.fillStyle = '#eef0ff'; g.beginPath(); g.arc(mx, my, 42, 0, Math.PI * 2); g.fill()
  g.fillStyle = 'rgba(180,185,215,0.5)'
  for (const [ox, oy, r] of [[-12, -8, 7], [10, 6, 9], [-6, 14, 5], [16, -10, 4]] as [number, number, number][]) { g.beginPath(); g.arc(mx + ox, my + oy, r, 0, Math.PI * 2); g.fill() }
  g.fillStyle = 'rgba(10,8,24,0.35)'; g.beginPath(); g.arc(mx + 14, my, 42, -Math.PI / 2, Math.PI / 2); g.fill()
  drawAirship(g, W * 0.27, H * 0.235)
  g.save(); g.globalCompositeOperation = 'lighter' // halo de pollution lumineuse au-dessus de la ville
  const haze = g.createLinearGradient(0, H * 0.46, 0, H); haze.addColorStop(0, 'rgba(255,140,90,0)'); haze.addColorStop(1, 'rgba(255,130,80,0.15)')
  g.fillStyle = haze; g.fillRect(0, H * 0.46, W, H * 0.54); g.restore()
  // VILLE en 3 couches de profondeur (lointaine brumeuse -> proche sombre détaillée)
  cityLayer(g, W, H, 213, 0.52, '#221a38', 0.5, 0.16)
  cityLayer(g, W, H, 224, 0.6, '#171030', 0.5, 0.13)
  cityLayer(g, W, H, 235, 0.7, '#0c0820', 0.46, 0)
  fillProfile(g, W, H, 'rgba(24,16,42,0.5)', hills(H * 0.72, [[9, 0.02, 0]])) // brume basse de premier plan
}

// ABYSSES — grand fond marin : la lumière tombe de la SURFACE (haut) et meurt dans le noir (bas).
// Rayons de soleil obliques, surface ondulée vue de dessous, épave échouée, massifs de coraux et
// algues en silhouette. `color` = récif proche. Poissons / méduses / bulles = animés au tick.
function drawReef(g: CanvasRenderingContext2D, W: number, H: number, color: string): void {
  skyGrad(g, W, H, [[0, '#2ea3cc'], [0.18, '#1273a0'], [0.5, '#0a4467'], [0.78, '#052637'], [1, '#01101c']])
  const top = H * SEA_TOP
  g.save() // SURFACE vue de dessous : ondulation claire + reflets qui filent
  g.fillStyle = 'rgba(190,240,255,0.5)'; g.beginPath(); g.moveTo(0, 0); g.lineTo(0, top)
  for (let x = 0; x <= W; x += 8) g.lineTo(x, top + Math.sin(x * 0.03) * 5 + Math.sin(x * 0.011) * 3)
  g.lineTo(W, 0); g.closePath(); g.fill()
  g.strokeStyle = 'rgba(255,255,255,0.35)'; g.lineWidth = 2
  for (let i = 0; i < 5; i++) { const y = top + 10 + i * 7; g.beginPath(); g.moveTo(0, y); for (let x = 0; x <= W; x += 8) g.lineTo(x, y + Math.sin(x * 0.02 + i) * 3); g.stroke() }
  g.restore()
  g.save() // RAYONS obliques qui percent depuis la surface (les god-rays du désert, mais sous l'eau)
  g.globalCompositeOperation = 'lighter'
  for (const [x0, w, a] of [[0.18, 70, 0.1], [0.34, 110, 0.14], [0.58, 60, 0.09], [0.72, 130, 0.12], [0.9, 50, 0.08]] as [number, number, number][]) {
    const grad = g.createLinearGradient(W * x0, top, W * x0 + 90, H * 0.85)
    grad.addColorStop(0, `rgba(190,240,255,${a})`); grad.addColorStop(1, 'rgba(190,240,255,0)')
    g.fillStyle = grad
    g.beginPath(); g.moveTo(W * x0 - w / 2, top); g.lineTo(W * x0 + w / 2, top)
    g.lineTo(W * x0 + w / 2 + 120, H * 0.9); g.lineTo(W * x0 - w / 2 + 120, H * 0.9); g.closePath(); g.fill()
  }
  g.restore()
  fillProfile(g, W, H, '#073349', peaks(W, H * 0.66, 20, 90, 6, rng(51))) // rochers lointains, presque noyés
  const sx = W * 0.68, sy = H * 0.6 // ÉPAVE : coque inclinée, mât cassé, hublots éteints
  g.save(); g.translate(sx, sy); g.rotate(0.16)
  g.fillStyle = '#0a2e3c'
  g.beginPath(); g.moveTo(-120, -18); g.lineTo(96, -18); g.lineTo(66, 30); g.lineTo(-92, 30); g.closePath(); g.fill()
  g.fillRect(-52, -44, 54, 26)                                       // superstructure
  g.fillRect(-8, -104, 5, 62)                                        // mât
  g.fillRect(-40, -96, 34, 4)                                        // vergue
  g.fillStyle = 'rgba(120,200,220,0.18)'
  for (const hx of [-96, -70, -44, -18, 8, 34, 60]) g.beginPath(), g.arc(hx, 2, 5, 0, Math.PI * 2), g.fill()
  g.restore()
  fillProfile(g, W, H, '#08394c', hills(H * 0.84, [[16, 0.006, 2.2], [7, 0.02, 0.5]])) // pente de sable sombre
  fillProfile(g, W, H, color, hills(H * 0.93, [[10, 0.01, 0.9]]))                       // récif proche
  const R = rng(77) // MASSIFS DE CORAIL en silhouette : gorgones branchues + éventails
  for (let i = 0; i < 9; i++) {
    const bx = R() * W, by = H * (0.9 + R() * 0.06), h = 24 + R() * 44
    g.strokeStyle = ['#0d4f5e', '#124a63', '#0a5a5a'][i % 3]!; g.lineWidth = 3 + R() * 3
    g.beginPath(); g.moveTo(bx, by); g.lineTo(bx, by - h)                    // tronc
    for (const dir of [-1, 1]) { g.moveTo(bx, by - h * 0.5); g.lineTo(bx + dir * (10 + R() * 14), by - h * (0.8 + R() * 0.3)) } // branches
    g.stroke()
  }
  for (const [bx, bh] of [[W * 0.12, 90], [W * 0.46, 70], [W * 0.86, 110]] as [number, number][]) { // ALGUES géantes
    g.strokeStyle = 'rgba(20,110,90,0.55)'; g.lineWidth = 6; g.beginPath(); g.moveTo(bx, H)
    for (let y = 0; y <= bh; y += 8) g.lineTo(bx + Math.sin(y * 0.06) * 10, H - y)
    g.stroke()
  }
  vignette(g, W, H, 0.45) // les bords s'enfoncent dans le noir : on est loin de tout
}

// MANOIR HANTÉ — nuit d'orage : ciel violacé, pleine lune à halo, nuages effilés, silhouette du
// manoir aux fenêtres allumées, arbres morts, grille et pierres tombales. `color` = colline proche.
// Chauves-souris / brume / éclairs / vacillement des fenêtres = animés au tick.
function drawManor(g: CanvasRenderingContext2D, W: number, H: number, color: string): void {
  skyGrad(g, W, H, [[0, '#1a1030'], [0.35, '#33204a'], [0.62, '#4a2b52'], [1, '#160d1c']])
  starfield(g, W, H, 45, 13, 0.45)
  const mx = W * MOON_X, my = H * MOON_Y
  glow(g, mx, my, 150, 'rgba(255,240,205,0.32)', 'rgba(255,240,205,0)') // halo blafard
  g.fillStyle = '#fdf3d0'; g.beginPath(); g.arc(mx, my, 40, 0, Math.PI * 2); g.fill() // pleine lune
  g.fillStyle = 'rgba(214,200,160,0.5)' // cratères
  for (const [dx, dy, r] of [[-12, -8, 7], [10, 6, 5], [-4, 14, 4], [16, -12, 3]] as [number, number, number][]) { g.beginPath(); g.arc(mx + dx, my + dy, r, 0, Math.PI * 2); g.fill() }
  g.save() // nuages : barres FINES et nombreuses qui barrent la lune (des ellipses épaisses feraient
  g.fillStyle = 'rgba(28,18,42,0.8)' // des taches dans un ciel aussi vide ; le désert use du même idiome)
  for (const [cx, cy, rw, rh] of [[0.66, 0.115, 210, 6], [0.72, 0.145, 150, 5], [0.86, 0.2, 120, 4],
    [0.28, 0.08, 190, 5], [0.4, 0.115, 130, 4], [0.12, 0.19, 160, 5], [0.55, 0.26, 230, 6]] as [number, number, number, number][]) {
    g.beginPath(); g.ellipse(W * cx, H * cy, rw, rh, 0, 0, Math.PI * 2); g.fill()
  }
  g.restore()
  fillProfile(g, W, H, '#1c1229', hills(H * 0.52, [[30, 0.0035, 2.6], [12, 0.011, 0.4]])) // crête lointaine
  fillProfile(g, W, H, '#241634', hills(H * 0.64, [[22, 0.005, 1.1]]))                     // colline intermédiaire
  const hx = W * MANOR_X, hy = H * MANOR_Y // MANOIR : corps, ailes, toits pointus, tour
  g.fillStyle = '#120b1c'
  g.fillRect(hx - 86, hy, 172, 118)                                   // corps
  g.fillRect(hx - 118, hy + 34, 34, 84); g.fillRect(hx + 86, hy + 34, 34, 84) // ailes
  g.beginPath(); g.moveTo(hx - 96, hy); g.lineTo(hx, hy - 54); g.lineTo(hx + 96, hy); g.closePath(); g.fill() // toit
  g.fillRect(hx + 44, hy - 96, 34, 60)                                // tour
  g.beginPath(); g.moveTo(hx + 38, hy - 92); g.lineTo(hx + 61, hy - 136); g.lineTo(hx + 84, hy - 92); g.closePath(); g.fill() // flèche
  g.fillRect(hx - 58, hy - 76, 12, 26); g.fillRect(hx + 18, hy - 78, 12, 28) // cheminées
  for (const [wx, wy] of MANOR_WINDOWS) { // fenêtres allumées (le vacillement est ajouté au tick)
    glow(g, hx + wx + 8, hy + wy + 9, 30, 'rgba(255,196,92,0.4)', 'rgba(255,196,92,0)')
    g.fillStyle = '#ffcf6b'; g.fillRect(hx + wx, hy + wy, 16, 18)
    g.fillStyle = '#120b1c'; g.fillRect(hx + wx + 7, hy + wy, 2, 18); g.fillRect(hx + wx, hy + wy + 8, 16, 2) // croisillons
  }
  for (const [tx, sc, lean] of [[W * 0.08, 1, -0.12], [W * 0.9, 0.8, 0.16], [W * 0.55, 0.6, 0.05], [W * 0.68, 0.5, -0.2]] as [number, number, number][]) {
    g.save(); g.translate(tx, H * 0.68); g.rotate(lean); g.scale(sc, sc) // ARBRES MORTS : tronc + branches nues
    g.strokeStyle = '#0d0714'; g.lineCap = 'round'
    g.lineWidth = 9; g.beginPath(); g.moveTo(0, 0); g.lineTo(0, -110); g.stroke()
    g.lineWidth = 5
    for (const [y, dx, dy] of [[-58, -34, -28], [-70, 30, -22], [-92, -22, -30], [-96, 26, -34]] as [number, number, number][]) {
      g.beginPath(); g.moveTo(0, y); g.quadraticCurveTo(dx * 0.6, y + dy * 0.4, dx, y + dy); g.stroke()
    }
    g.restore()
  }
  fillProfile(g, W, H, color, hills(H * 0.86, [[14, 0.007, 1.4]])) // colline proche du cimetière
  const R = rng(66) // CIMETIÈRE : stèles de tailles variées, certaines penchées
  for (let i = 0; i < 11; i++) {
    const bx = R() * W, by = H * (0.88 + R() * 0.07), h = 16 + R() * 16, w = 9 + R() * 6
    g.save(); g.translate(bx, by); g.rotate((R() - 0.5) * 0.3)
    g.fillStyle = '#2c2140'
    g.fillRect(-w / 2, -h, w, h); g.beginPath(); g.arc(0, -h, w / 2, Math.PI, 0); g.fill() // stèle à sommet rond
    g.fillStyle = 'rgba(190,180,215,0.16)'; g.fillRect(-w / 2, -h, 2, h)                    // liseré de lune
    g.restore()
  }
  g.strokeStyle = '#0f0a18'; g.lineWidth = 3 // GRILLE en fer forgé au premier plan
  for (let x = 8; x < W; x += 26) { g.beginPath(); g.moveTo(x, H); g.lineTo(x, H - 34); g.stroke() }
  g.beginPath(); g.moveTo(0, H - 30); g.lineTo(W, H - 30); g.stroke()
  vignette(g, W, H, 0.5) // les bords se perdent dans la nuit
}

export function worldBackground(world: WorldKey): HTMLCanvasElement {
  const cached = bgCache.get(world)
  if (cached) return cached
  const W = COLS * TS, H = ROWS * TS
  const cv = document.createElement('canvas')
  cv.width = W
  cv.height = H
  const g = cv.getContext('2d')!
  // REFONTE « papier découpé » (lot 4). C'est ici que se règle le défaut le plus
  // grave de l'audit : « le fond fait le même bruit que le jeu ». Les anciens
  // fonds étaient du dessin vectoriel lisse et détaillé, avec des fenêtres
  // allumées de la couleur exacte des pièces d'or. Les nouveaux sont des
  // découpes plates, sans ombre portée et plafonnées en clarté : un fond n'a pas
  // à être vu, il a à être là.
  fondPapier(g, W, H, world)
  bgCache.set(world, cv)
  return cv
}

// --- Décors ANIMÉS par MONDE : dessinés à chaque frame DERRIÈRE le terrain, pilotés par le TICK
// de simulation (jamais l'horloge murale : `t` = cur.tick + alpha), donc identiques en solo comme
// en rejeu réseau. Purement cosmétiques (aucun état ajouté ; aucune lecture de Date/performance).
// Les maths de position sont des fonctions PURES (aucun DOM), testées hors navigateur. ---
const wrap = (v: number, m: number): number => ((v % m) + m) % m

/** Un flocon : centre écran (px) + rayon. */
export interface Flake { x: number; y: number; r: number }
/**
 * `count` flocons qui tombent : positions déterministes dérivées du temps `t` (en ticks). Chaque
 * flocon a une colonne, une vitesse, une taille et une oscillation fixes (fonction de son index) ;
 * il descend et boucle sur la hauteur `H`, dérive latéralement en boucle sur `W`. Fonction PURE.
 */
export function snowflakes(count: number, t: number, W: number, H: number): Flake[] {
  const out: Flake[] = []
  for (let i = 0; i < count; i++) {
    const speed = 0.5 + (i % 5) * 0.16                          // px/tick, vitesses variées
    const driftAmp = 5 + (i % 4) * 4                            // amplitude de l'oscillation latérale
    const x = wrap(i * 61.618 + Math.sin(t * 0.02 + i) * driftAmp, W)
    const y = wrap(i * 41.7 + t * speed, H)                     // chute + boucle verticale
    const r = 1 + (i % 3) + (i % 2) * 0.5                       // 1 .. 3.5 px
    out.push({ x, y, r })
  }
  return out
}

/**
 * `count` particules déterministes qui dérivent en boucle sur `[0,W)×[0,H)`, à la vitesse
 * (`speedX`,`speedY`) px/tick, avec une légère oscillation latérale. Base commune des ambiances :
 * nuages (lent, horizontal), braises/vapeur (vertical), lucioles, gouttes. `seed` décorrèle les
 * couches. Fonction PURE (dérivée de `t` = tick, jamais de l'horloge).
 */
export function drift(count: number, t: number, W: number, H: number, speedX: number, speedY: number, seed: number): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = []
  for (let i = 0; i < count; i++) {
    const k = i + seed * 97
    const x = wrap(k * 71.3 + t * speedX + Math.sin(t * 0.015 + k) * 6, W)
    const y = wrap(k * 47.9 + t * speedY, H)
    out.push({ x, y })
  }
  return out
}

/** Braises / poussières qui MONTENT (speedY < 0), rayon variable — ambiance classique/désert. */
export function ember(count: number, t: number, W: number, H: number): Flake[] {
  return drift(count, t, W, H, 0.15, -0.4, 3).map((p, i) => ({ x: p.x, y: p.y, r: 0.8 + (i % 3) * 0.5 }))
}

/** Luminosité [0,1] d'un néon qui « respire », déphasée par `phase`. Fonction PURE du tick. */
export function neonPulse(t: number, phase: number): number {
  return 0.5 + 0.5 * Math.sin(t * 0.12 + phase)
}

/** Bulle qui REMONTE (abysses) : vitesse et zigzag variés par index, boucle du fond vers la surface.
 *  PURE (aucune horloge, aucun aléa) : identique en solo, en rejeu et sur les 3 écrans d'un multi. */
export function bubbleUp(count: number, t: number, W: number, H: number, seed: number): Flake[] {
  const out: Flake[] = []
  for (let i = 0; i < count; i++) {
    const k = i + seed * 31
    const speed = 0.35 + (i % 4) * 0.18
    const x = wrap(k * 67.1 + Math.sin(t * 0.03 + k) * (4 + (i % 3) * 3), W)
    const y = H - wrap(k * 53.3 + t * speed, H) // remonte : le sens du monde, à l'envers de la neige
    out.push({ x, y, r: 1 + (i % 4) * 0.9 })
  }
  return out
}

/** ÉCLAIR (manoir) : intensité [0,1] d'un flash d'orage. Zéro la plupart du temps ; un coup double
 *  bref toutes les ~14 s de jeu (period ticks). PURE et déterministe -> même orage pour tous. */
export function stormFlash(t: number, period = 420): number {
  const p = ((t % period) + period) % period
  if (p < 4) return 1 - p / 4          // premier éclat, vif
  if (p >= 9 && p < 16) return 0.7 * (1 - (p - 9) / 7) // réplique, plus douce
  return 0
}

// Enseigne néon : halo large translucide puis cœur vif (même idiome que les éclairs de tazer),
// luminosité pulsée au tick. `path` trace le tube ; on ouvre un tracé neuf avant chaque passe.
function neonStroke(ctx: CanvasRenderingContext2D, color: string, pulse: number, path: () => void): void {
  ctx.save()
  ctx.lineCap = 'round'; ctx.lineJoin = 'round'
  ctx.globalAlpha = 0.26 * pulse; ctx.strokeStyle = color; ctx.lineWidth = 9; ctx.beginPath(); path(); ctx.stroke() // halo
  ctx.globalAlpha = Math.min(1, 0.55 + 0.45 * pulse); ctx.strokeStyle = color; ctx.lineWidth = 3; ctx.beginPath(); path(); ctx.stroke() // cœur
  ctx.restore()
}

// Étoile filante déterministe : apparaît une fraction de chaque cycle de `period` ticks, file en
// diagonale et s'estompe. Pure (dérivée de `t`) ; la lane change à chaque cycle via le PRNG.
function shootStar(t: number, W: number, H: number, period: number, seed: number): { x: number; y: number; tx: number; ty: number; a: number } | null {
  const ph = wrap(t, period) / period
  if (ph > 0.16) return null
  const R = rng(seed + Math.floor(t / period) * 131)
  const ox = R() * W * 0.7, oy = R() * H * 0.3, dx = W * 0.32, dy = H * 0.16
  const k = ph / 0.16, x = ox + dx * k, y = oy + dy * k
  return { x, y, tx: x - dx * 0.3, ty: y - dy * 0.3, a: Math.sin(k * Math.PI) }
}

// Décor animé du monde, DERRIÈRE le terrain. `t` = temps décoratif continu en ticks (cur.tick +
// alpha), jamais l'horloge murale -> cohérent solo/rejeu. Chaque biome a son ambiance premium
// (particules + éléments qui vivent) ; les positions coïncident avec le fond STATIQUE (cristaux,
// chalet, soleil, roue). Purement cosmétique : aucun état ajouté, aucune lecture d'horloge.
function drawWorldDecor(rc: RenderCtx, world: WorldKey, t: number): void {
  const { ctx } = rc
  // PLAFOND DU DÉCOR (29/07, loi n° 2). Ces animations — nuages, flocons, néons,
  // bulles, éclairs — sont du FOND. Elles étaient réglées pour l'ancien style, où
  // le fond était déjà bavard ; par-dessus les découpes de papier, elles
  // redeviennent le bruit que toute la refonte cherche à supprimer. On ne les
  // redessine pas, on les remet à leur place.
  ctx.save()
  ctx.globalAlpha = 0.5
  const W = COLS * TS, H = ROWS * TS
  if (world === 'neige') {
    ctx.save() // aurore : miroitement (bandes qui ondulent + pulsent) par-dessus l'aurore statique
    for (const [i, [col, baseY, amp]] of ([['#7bf0c0', 0.2, 26], ['#7ad6f0', 0.26, 20]] as [string, number, number][]).entries()) {
      ctx.globalAlpha = 0.05 + 0.06 * neonPulse(t, i * 2); ctx.strokeStyle = col; ctx.lineWidth = 10; ctx.beginPath()
      for (let x = 0; x <= W; x += 12) { const y = H * baseY + Math.sin(x * 0.008 + t * 0.02) * amp; x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y) }
      ctx.stroke()
    }
    ctx.restore()
    ctx.save() // étoiles scintillantes (positions fixes, éclat pulsé)
    for (const [i, p] of drift(24, 0, W, H * 0.4, 0, 0, 21).entries()) { ctx.fillStyle = `rgba(255,255,255,${0.3 + 0.6 * neonPulse(t * 0.6, i)})`; ctx.fillRect(p.x, p.y, 2, 2) }
    ctx.restore()
    ctx.save() // neige à 2 profondeurs : gros flocons flous devant, petits derrière
    ctx.fillStyle = 'rgba(255,255,255,0.5)'; for (const f of snowflakes(40, t, W, H)) { ctx.beginPath(); ctx.arc(f.x, f.y, f.r * 1.4, 0, Math.PI * 2); ctx.fill() }
    ctx.fillStyle = 'rgba(255,255,255,0.85)'; for (const f of snowflakes(50, t * 1.3, W, H)) { ctx.beginPath(); ctx.arc(f.x, f.y, f.r * 0.7, 0, Math.PI * 2); ctx.fill() }
    ctx.restore()
    ctx.save() // fumée du chalet qui monte de la cheminée
    for (const [i, p] of drift(6, t, 44, 130, 0.06, -0.5, 8).entries()) { ctx.fillStyle = `rgba(220,225,235,${Math.max(0, 0.14 - i * 0.02)})`; ctx.beginPath(); ctx.arc(W * CABIN_X - 6 + p.x - 22 + Math.sin(t * 0.03 + i) * 6, H * CABIN_Y - 26 + p.y - 130, 6 + i, 0, Math.PI * 2); ctx.fill() }
    ctx.restore()
  } else if (world === 'abysses') {
    const top = H * SEA_TOP
    ctx.save() // RAYONS de surface : ils ondulent et respirent (le fond porte leur version statique)
    ctx.globalCompositeOperation = 'lighter'
    for (const [i, x0] of [0.18, 0.34, 0.58, 0.72, 0.9].entries()) {
      const sway = Math.sin(t * 0.012 + i * 1.7) * 26
      const grad = ctx.createLinearGradient(W * x0 + sway, top, W * x0 + 90 + sway, H * 0.85)
      grad.addColorStop(0, `rgba(190,240,255,${0.05 + 0.05 * neonPulse(t * 0.4, i)})`); grad.addColorStop(1, 'rgba(190,240,255,0)')
      ctx.fillStyle = grad
      ctx.beginPath(); ctx.moveTo(W * x0 - 40 + sway, top); ctx.lineTo(W * x0 + 40 + sway, top)
      ctx.lineTo(W * x0 + 160 + sway, H * 0.9); ctx.lineTo(W * x0 + 80 + sway, H * 0.9); ctx.closePath(); ctx.fill()
    }
    ctx.restore()
    ctx.save() // BANCS de poissons : 3 groupes qui traversent à des profondeurs et vitesses différentes
    for (const [gi, [depth, speed, col, n]] of ([[0.3, 0.5, 'rgba(120,200,225,0.5)', 9], [0.5, -0.34, 'rgba(90,170,200,0.42)', 12], [0.72, 0.24, 'rgba(70,140,170,0.34)', 7]] as [number, number, string, number][]).entries()) {
      ctx.fillStyle = col
      for (let i = 0; i < n; i++) {
        const k = i + gi * 13
        const x = wrap(k * 37.7 + t * speed, W + 80) - 40
        const y = H * depth + Math.sin(t * 0.03 + k * 0.9) * 12 + (i % 4) * 9
        const dir = speed > 0 ? 1 : -1
        ctx.beginPath(); ctx.ellipse(x, y, 6, 2.6, 0, 0, Math.PI * 2); ctx.fill()      // corps
        ctx.beginPath(); ctx.moveTo(x - dir * 6, y); ctx.lineTo(x - dir * 11, y - 3); ctx.lineTo(x - dir * 11, y + 3); ctx.closePath(); ctx.fill() // queue
      }
    }
    ctx.restore()
    ctx.save() // MÉDUSES : ombrelle qui pulse + tentacules qui traînent, montée très lente
    for (const [i, p] of drift(4, t, W, H * 1.2, 0.06, -0.16, 17).entries()) {
      const y = p.y - H * 0.1, pulse = 0.8 + 0.2 * Math.sin(t * 0.06 + i * 2)
      ctx.fillStyle = `rgba(190,150,255,${0.16 + 0.06 * pulse})`
      ctx.beginPath(); ctx.ellipse(p.x, y, 13 * pulse, 10 / pulse, 0, Math.PI, 0); ctx.fill()
      ctx.strokeStyle = `rgba(190,150,255,${0.12 + 0.05 * pulse})`; ctx.lineWidth = 1.5
      for (const dx of [-6, -2, 2, 6]) {
        ctx.beginPath(); ctx.moveTo(p.x + dx, y)
        for (let k = 4; k <= 26; k += 6) ctx.lineTo(p.x + dx + Math.sin(t * 0.05 + k * 0.3 + i) * 3, y + k)
        ctx.stroke()
      }
    }
    ctx.restore()
    ctx.save() // BULLES qui remontent vers la surface (fond de scène ; celles du nageur vivent ailleurs)
    for (const b of bubbleUp(26, t, W, H, 5)) {
      ctx.strokeStyle = 'rgba(200,245,255,0.3)'; ctx.lineWidth = 1
      ctx.beginPath(); ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2); ctx.stroke()
      ctx.fillStyle = 'rgba(255,255,255,0.25)'; ctx.fillRect(b.x - b.r * 0.4, b.y - b.r * 0.6, 1, 1) // reflet
    }
    ctx.restore()
    ctx.save() // ALGUES qui ondulent au courant (au-dessus des silhouettes statiques)
    ctx.strokeStyle = 'rgba(30,140,110,0.4)'; ctx.lineWidth = 5
    for (const [i, bx] of [W * 0.12, W * 0.46, W * 0.86].entries()) {
      ctx.beginPath(); ctx.moveTo(bx, H)
      for (let y = 0; y <= 90; y += 8) ctx.lineTo(bx + Math.sin(y * 0.06 + t * 0.04 + i) * (10 + y * 0.12), H - y)
      ctx.stroke()
    }
    ctx.restore()
  } else if (world === 'manoir') {
    const flash = stormFlash(t)
    ctx.save() // BRUME qui rampe au sol : nappes lentes, plus denses en bas
    ctx.fillStyle = 'rgba(180,170,210,0.05)'
    for (const p of drift(10, t, W, H * 0.3, 0.09, 0, 23)) { ctx.beginPath(); ctx.ellipse(p.x, H * 0.82 + p.y * 0.4, 70 + (p.x % 40), 12, 0, 0, Math.PI * 2); ctx.fill() }
    ctx.restore()
    ctx.save() // FENÊTRES : vacillement de bougie, PILE sur les carreaux peints dans le fond
    const hx = W * MANOR_X, hy = H * MANOR_Y
    for (const [i, [wx, wy]] of MANOR_WINDOWS.entries()) {
      const f = torchFlicker(t, i * 7) // même flamme vivante que les torches (pure, déphasée)
      ctx.globalCompositeOperation = 'lighter'
      ctx.fillStyle = `rgba(255,190,80,${0.1 + 0.12 * f})`
      ctx.fillRect(hx + wx - 2, hy + wy - 2, 20, 22)
      glow(ctx, hx + wx + 8, hy + wy + 9, 24 + 6 * f, `rgba(255,196,92,${0.12 * f})`, 'rgba(255,196,92,0)')
    }
    ctx.restore()
    ctx.save() // CHAUVES-SOURIS : silhouettes qui battent des ailes en traversant la lune
    ctx.fillStyle = 'rgba(10,6,16,0.85)'
    for (const [i, p] of drift(7, t, W + 60, H * 0.5, 0.55, 0.05, 11).entries()) {
      const x = p.x - 30, y = p.y + H * 0.06 + Math.sin(t * 0.05 + i * 1.3) * 18
      const w = 5 + Math.abs(Math.sin(t * 0.32 + i)) * 6 // battement d'ailes
      ctx.beginPath(); ctx.moveTo(x, y)
      ctx.quadraticCurveTo(x - 5, y - w, x - 11, y + 1); ctx.quadraticCurveTo(x - 5, y + 2, x, y + 3)
      ctx.quadraticCurveTo(x + 5, y + 2, x + 11, y + 1); ctx.quadraticCurveTo(x + 5, y - w, x, y)
      ctx.fill()
    }
    ctx.restore()
    if (flash > 0) { // ÉCLAIR : voile blanc-bleu sur toute la scène + contre-jour (déterministe, sans aléa)
      ctx.save()
      ctx.globalCompositeOperation = 'lighter'
      ctx.fillStyle = `rgba(150,170,230,${0.3 * flash})`
      ctx.fillRect(0, 0, W, H)
      ctx.restore()
    }
  } else if (world === 'usine') {
    ctx.save() // panaches de vapeur qui montent, derrière les néons
    ctx.fillStyle = 'rgba(150,160,180,0.09)'
    for (const p of drift(12, t, W, H, 0.05, -0.5, 5)) { ctx.beginPath(); ctx.arc(p.x, p.y, 12 + (p.x % 7), 0, Math.PI * 2); ctx.fill() }
    ctx.restore()
    ctx.save() // étincelles ambre qui montent
    for (const [i, f] of ember(16, t, W, H).entries()) { ctx.fillStyle = `rgba(255,180,90,${0.2 + 0.5 * neonPulse(t, i)})`; ctx.beginPath(); ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2); ctx.fill() }
    ctx.restore()
    neonStroke(ctx, '#3df0ff', neonPulse(t, 0), () => ctx.rect(W * 0.08, H * 0.1, W * 0.16, H * 0.1))                        // cadre cyan
    neonStroke(ctx, '#ff5ad6', neonPulse(t, 2.1), () => { ctx.moveTo(W * 0.88, H * 0.08); ctx.lineTo(W * 0.88, H * 0.3) })   // tube magenta
    neonStroke(ctx, '#ffd23f', neonPulse(t, 4.2), () => ctx.arc(W * 0.6, H * 0.14, 18, 0, Math.PI * 2))                      // anneau ambre
    neonStroke(ctx, '#5bff8f', neonPulse(t, 1.2), () => { ctx.moveTo(W * 0.3, H * 0.1); ctx.lineTo(W * 0.42, H * 0.1); ctx.lineTo(W * 0.36, H * 0.2); ctx.closePath() }) // flèche verte
    const bl = neonPulse(t, 0) > 0.7 ? 1 : 0.15 // voyant d'alerte clignotant
    glow(ctx, W * 0.5, H * 0.48, 16, `rgba(255,60,60,${0.5 * bl})`, 'rgba(255,60,60,0)')
    const ga = t * 0.05 // éclat qui tourne sur la roue dentée (même centre que le fond)
    ctx.save(); ctx.fillStyle = 'rgba(255,255,255,0.5)'; ctx.beginPath(); ctx.arc(W * 0.87 + Math.cos(ga) * 30, H * 0.6 + Math.sin(ga) * 30, 4, 0, Math.PI * 2); ctx.fill(); ctx.restore()
  } else if (world === 'desert') {
    ctx.save() // voile de poussière qui traverse lentement
    ctx.fillStyle = 'rgba(230,200,140,0.06)'
    for (const p of drift(8, t, W, H, 0.4, 0.02, 7)) { ctx.beginPath(); ctx.arc(p.x, p.y, 24, 0, Math.PI * 2); ctx.fill() }
    ctx.restore()
    const cx = W * SUN_X, cy = H * SUN_Y // halo du soleil qui respire (~4 s à 30 tps)
    const pulse = 0.5 + 0.5 * Math.sin(t * 0.05), rad = 120 + 40 * pulse
    const grad = ctx.createRadialGradient(cx, cy, 20, cx, cy, rad)
    grad.addColorStop(0, `rgba(255,236,170,${0.25 * pulse + 0.1})`); grad.addColorStop(1, 'rgba(255,236,170,0)')
    ctx.save(); ctx.fillStyle = grad; ctx.beginPath(); ctx.arc(cx, cy, rad, 0, Math.PI * 2); ctx.fill(); ctx.restore()
    ctx.save() // vautour qui tourne lentement dans le ciel
    ctx.strokeStyle = 'rgba(40,30,20,0.6)'; ctx.lineWidth = 2.5; ctx.lineCap = 'round'
    const va = t * 0.02, vx = W * 0.5 + Math.cos(va) * W * 0.18, vy = H * 0.26 + Math.sin(va) * H * 0.08, flap = Math.sin(t * 0.3) * 5
    ctx.beginPath(); ctx.moveTo(vx - 12, vy + flap); ctx.lineTo(vx, vy - 3); ctx.lineTo(vx + 12, vy + flap); ctx.stroke()
    ctx.restore()
  } else if (world === 'prairie') {
    ctx.save() // nuages qui dérivent lentement
    ctx.fillStyle = 'rgba(255,255,255,0.5)'
    for (const p of drift(4, t, W, H * 0.32, 0.12, 0, 2)) { const y = p.y + H * 0.06; ctx.beginPath(); ctx.arc(p.x, y, 24, 0, Math.PI * 2); ctx.arc(p.x + 22, y + 4, 16, 0, Math.PI * 2); ctx.arc(p.x - 20, y + 6, 15, 0, Math.PI * 2); ctx.fill() }
    ctx.restore()
    ctx.save() // oiseaux qui battent des ailes
    ctx.strokeStyle = 'rgba(60,70,90,0.55)'; ctx.lineWidth = 2; ctx.lineCap = 'round'
    for (const [i, p] of drift(4, t, W, H * 0.24, 0.2, 0, 9).entries()) { const y = p.y + H * 0.12, f = Math.sin(t * 0.25 + i) * 4; ctx.beginPath(); ctx.moveTo(p.x - 7, y + f); ctx.lineTo(p.x, y - 3); ctx.lineTo(p.x + 7, y + f); ctx.stroke() }
    ctx.restore()
    ctx.save() // papillons + pollen qui flottent
    for (const [i, p] of drift(10, t, W, H, 0.05, 0.03, 4).entries()) { const wob = Math.sin(t * 0.2 + i) * 8; ctx.fillStyle = i % 3 === 0 ? 'rgba(255,150,190,0.8)' : 'rgba(255,255,255,0.5)'; ctx.beginPath(); ctx.arc(p.x + wob, p.y + H * 0.45, i % 3 === 0 ? 3 : 1.6, 0, Math.PI * 2); ctx.fill() }
    ctx.restore()
  } else if (world === 'grotte') {
    ctx.save() // gouttes qui tombent
    ctx.strokeStyle = 'rgba(150,190,220,0.5)'; ctx.lineWidth = 1.5; ctx.lineCap = 'round'
    for (const p of drift(14, t, W, H, 0, 3.2, 4)) { ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(p.x, p.y + 7); ctx.stroke() }
    ctx.restore()
    for (const [fx, fy, rgb] of CAVE_GEMS) { const p = neonPulse(t, fx); glow(ctx, fx * W, fy * H, 30 + 12 * p, `rgba(${rgb},${0.1 + 0.16 * p})`, `rgba(${rgb},0)`) } // scintillement des cristaux
    ctx.save() // lucioles pulsées
    for (const [i, p] of drift(10, t, W, H, 0.15, 0.08, 6).entries()) { ctx.fillStyle = `rgba(180,255,140,${0.22 + 0.5 * neonPulse(t + i * 20, i)})`; ctx.beginPath(); ctx.arc(p.x, p.y, 1.8, 0, Math.PI * 2); ctx.fill() }
    ctx.restore()
  } else if (world === 'classique') {
    ctx.save() // voile atmosphérique bas : la ville se fond dans une brume légère (profondeur, relie fond et terrain)
    const haze = ctx.createLinearGradient(0, H * 0.52, 0, H)
    haze.addColorStop(0, 'rgba(40,28,64,0)'); haze.addColorStop(1, 'rgba(50,34,78,0.26)')
    ctx.fillStyle = haze; ctx.fillRect(0, H * 0.52, W, H * 0.48)
    ctx.restore()
    ctx.save() // étoiles scintillantes (positions fixes)
    for (const [i, p] of drift(28, 0, W, H * 0.6, 0, 0, 42).entries()) { ctx.fillStyle = `rgba(255,255,255,${0.25 + 0.6 * neonPulse(t * 0.5, i)})`; ctx.fillRect(p.x, p.y, 2, 2) }
    ctx.restore()
    const ss = shootStar(t, W, H, 260, 7) // étoile filante occasionnelle
    if (ss) { ctx.save(); ctx.globalAlpha = ss.a; ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.beginPath(); ctx.moveTo(ss.tx, ss.ty); ctx.lineTo(ss.x, ss.y); ctx.stroke(); ctx.restore() }
    ctx.save() // poussière ambre qui monte (présente sans couvrir le jeu)
    for (const [i, f] of ember(28, t, W, H).entries()) { ctx.fillStyle = `rgba(255,192,112,${0.12 + 0.16 * neonPulse(t, i)})`; ctx.beginPath(); ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2); ctx.fill() }
    ctx.globalCompositeOperation = 'lighter' // fines lucioles bleutées : petit cœur clair + halo doux, dérive lente (vie nocturne)
    for (const [i, p] of drift(9, t, W, H * 0.86, 0.05, -0.13, 61).entries()) {
      const y = p.y + H * 0.1, a = 0.32 + 0.5 * neonPulse(t * 0.6, i * 1.7)
      glow(ctx, p.x, y, 8, `rgba(196,228,255,${(0.16 * a).toFixed(3)})`, 'rgba(196,228,255,0)')
      ctx.fillStyle = `rgba(224,242,255,${a.toFixed(3)})`; ctx.beginPath(); ctx.arc(p.x, y, 1.4, 0, Math.PI * 2); ctx.fill()
    }
    ctx.restore()
  }
  ctx.restore()
}

/** Fond d'AMBIANCE pour les écrans hors jeu (accueil, connexion) : le fond statique d'un monde
 *  + son décor animé, à la taille du terrain (1344x768), sans terrain ni entités. `t` est un
 *  temps décoratif quelconque (l'accueil n'est pas simulé : l'horloge d'affichage convient). */
export function drawMenuBackdrop(rc: RenderCtx, world: WorldKey, t: number): void {
  rc.ctx.drawImage(worldBackground(world), 0, 0)
  drawWorldDecor(rc, world, t)
}

// Téléporteurs teintés : associe chaque case de téléporteur à l'index de teinte de sa paire
// (cyclé i % 3, cf. TELEPORTERS). Purement visuel : les deux cases d'une même paire partagent
// la teinte, ce qui montre au joueur quelle entrée relie quelle sortie. Fonction PURE (aucun
// DOM), donc testable hors navigateur ; reconstruite une fois par frame depuis cur.teleporters.
export function teleporterColorMap(teleporters: readonly [number, number][]): Map<number, number> {
  const m = new Map<number, number>()
  teleporters.forEach(([a, b], i) => {
    const c = i % 3
    m.set(a, c)
    m.set(b, c)
  })
  return m
}

// Overlays ANIMÉS des décos, dessinés au TICK (t = cur.tick + alpha), jamais l'horloge murale.
// Ne touche que les cases dont le code est animé (torche/bougie = flamme qui vacille ; valve =
// éclat qui tourne). Positions dérivées de `t` et de `cell` (déphasage par instance), déterministes.
// SOURCE DE BULLES (16/07 soir) : anim procédurale au tick — des bulles montent de chaque
// tuile T.Bubbles sur ~3 cases de haut (PUR VISUEL : seule la case SOURCE recharge, cf. sim).
// Positions dérivées du tick et de la cellule : déterministe, zéro état, zéro RNG runtime.
// Langage CARTOON POP (raccord bulles/eau 22/07) : GROSSES bulles à anneau blanc épais + cœur
// bleu clair + reflet, qui ÉCLATENT en 3 gouttelettes en fin de montée — même vocabulaire que
// l'eau (crêtes blanches), les cascades (mousse) et les dômes de lave qui popent.
function drawBubblesAnim(rc: RenderCtx, s: GameState, t: number): void {
  const { ctx } = rc
  ctx.save()
  ctx.lineCap = 'round'
  for (let cell = 0; cell < s.terrain.length; cell++) {
    if (s.terrain[cell] !== T.Bubbles) continue
    const bx = colOf(cell, s.cols) * TS, by = rowOf(cell, s.cols) * TS
    for (let i = 0; i < 4; i++) {
      const phase = ((cell * 7 + i * 23) % 48) / 48                 // décalage stable par bulle
      const prog = (t * 0.012 + phase) % 1                           // 0 -> 1 : du sol vers le haut
      const y = by + TS - 2 - prog * (TS * 3 - 6)                    // ~3 cases de montée
      const x = bx + 8 + ((cell + i * 5) % 3) * 12 + Math.sin(t * 0.09 + i * 2 + cell) * 3
      const r = (i % 2 ? 3.2 : 4.6) * (0.7 + prog * 0.45)            // gonfle en montant (pop)
      if (prog > 0.9) {                                              // ÉCLAT final : 3 gouttelettes qui s'écartent
        const burst = (prog - 0.9) / 0.1
        ctx.fillStyle = `rgba(255,255,255,${(0.9 * (1 - burst)).toFixed(3)})`
        for (let d = 0; d < 3; d++) {
          const a = -Math.PI / 2 + (d - 1) * 0.9
          ctx.beginPath()
          ctx.arc(x + Math.cos(a) * burst * 7, y + Math.sin(a) * burst * 7 + burst * 2, 1.4, 0, Math.PI * 2)
          ctx.fill()
        }
        continue
      }
      const a = 0.35 + 0.55 * (1 - prog)                             // s'allège en montant (l'éclat prend le relais)
      ctx.fillStyle = `rgba(124,208,255,${(a * 0.5).toFixed(3)})`    // cœur bleu clair translucide
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill()
      ctx.strokeStyle = `rgba(255,255,255,${a.toFixed(3)})`          // anneau blanc épais (langage pop)
      ctx.lineWidth = 2.4
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.stroke()
      ctx.beginPath(); ctx.arc(x - r * 0.35, y - r * 0.35, r * 0.28, 0, Math.PI * 2) // reflet haut-gauche
      ctx.fillStyle = `rgba(255,255,255,${(a * 0.9).toFixed(3)})`
      ctx.fill()
    }
  }
  ctx.restore()
}

// VER CHECKPOINT (16/07 soir) : chaque ancre T.Checkpoint = un gros ver de 5 cases centrées
// dessus (queue à gauche, tête à droite). ÉVEILLÉ (rose vif, yeux ouverts) = le ver du
// checkpoint ACTIF du coureur caméra (focusIdx) ; les autres dorment. Ondulation verticale
// par segment dérivée du tick et de la cellule : déterministe, pur rendu, zéro état.
function drawWorms(rc: RenderCtx, s: GameState, t: number, focusIdx: number): void {
  const active = s.runners[focusIdx]?.checkpoint ?? -1
  for (let cell = 0; cell < s.terrain.length; cell++) {
    if (s.terrain[cell] !== T.Checkpoint) continue
    const col = colOf(cell, s.cols), row = rowOf(cell, s.cols)
    const awake = cell === active
    const frames = awake ? WORM_WAKE : WORM_SLEEP
    for (let i = 0; i < 5; i++) {
      const c = col + i - 2
      if (c < 0 || c >= s.cols) continue
      const wob = Math.round(Math.sin(t * 0.06 + i * 1.1 + cell) * (awake ? 1.5 : 0.7)) // il respire (plus ample réveillé)
      blit(rc, frames[i]!, c * TS, row * TS + wob)
    }
  }
}

// ---- GRANDS DÉCORS (17/07, refaits 18/07 « fais mieux ») : blit depuis la feuille pixel art
// decors.png (gen-sprites.mjs) — 3 frames par décor, cadence par kind. 100 % cosmétiques,
// sous les entités. Exporté : l'ÉDITEUR dessine les mêmes ensembles depuis les ancres posées.
// Contrat d'échelle : dstCell px par case de l'emprise (16 par défaut = contrat historique de
// l'éditeur qui scale lui-même ; le JEU passe TS pour la pleine taille à l'écran).
const BIG_TICKS: Record<BigDecoKind, number> = { arbre: 12, epave: 11, baleine: 13, grue: 10, vitrail: 12, campement: 6 }
export function drawBigDeco(ctx: CanvasRenderingContext2D, kind: BigDecoKind, x0: number, y0: number, t: number, dstCell = 16): void {
  const img = decorSheet()
  if (!img) return // feuille pas encore chargée : on saute (chargée au boot par main.ts)
  const meta = BIG_DECOS[kind]
  // REFONTE (lot 6) : dessiné, pas blitté. Comme les 31 petits décors, il ne
  // porte AUCUNE ombre portée : c'est du décor, il reste collé au fond.
  blitGrandDeco(ctx, kind, meta.w * dstCell, meta.h * dstCell, x0, y0)
}
// Scan des ancres de grands décors dans la couche décor (codes 40+) et dessin depuis
// l'origine de l'emprise (col-ax, row-ay). Grille vide : zéro travail.
function drawBigDecos(rc: RenderCtx, decor: DecorGrid, t: number, cols: number): void {
  for (let cell = 0; cell < decor.length; cell++) {
    const kind = bigOfCode(decor[cell] ?? 0)
    if (!kind) continue
    const meta = BIG_DECOS[kind]
    drawBigDeco(rc.ctx, kind, (colOf(cell, cols) - meta.ax) * TS, (rowOf(cell, cols) - meta.ay) * TS, t, TS)
  }
}

function drawDecoAnim(rc: RenderCtx, decor: DecorGrid, t: number, cols: number): void {
  const { ctx } = rc
  // Même règle : ces overlays (flamme, éclat de valve, lucioles) se posent sur
  // des décos désormais en papier. À pleine intensité ils les recouvrent d'un
  // langage qui n'est plus le leur.
  ctx.save()
  ctx.globalAlpha = 0.62
  for (let cell = 0; cell < decor.length; cell++) {
    const code = decor[cell] ?? 0
    if (!DECO_ANIMATED_CODES.has(code) || DECO_FRONT_CODES.has(code)) continue // front = animées dans drawFrontDecos
    const x = colOf(cell, cols) * TS, y = rowOf(cell, cols) * TS
    ctx.save()
    if (code === DECOS.valve.code) {
      const cx = x + TS * 0.5, cy = y + TS * 7 / 16, a = t * 0.15 // éclat qui tourne sur le volant
      ctx.fillStyle = 'rgba(255,255,255,0.7)'
      ctx.beginPath(); ctx.arc(cx + Math.cos(a) * TS * 0.18, cy + Math.sin(a) * TS * 0.18, TS * 0.05, 0, Math.PI * 2); ctx.fill()
    } else if (code === DECOS.lanterne.code) {
      // verre qui pulse doucement (la lueur large vient de LIGHT_SOURCES)
      const g = 0.35 + 0.2 * Math.sin(t * 0.2 + cell)
      ctx.fillStyle = `rgba(255,220,120,${g.toFixed(3)})`
      ctx.fillRect(x + TS * 6 / 16, y + TS * 3 / 16, TS * 4 / 16, TS * 4 / 16)
    } else if (code === DECOS.fanion.code) {
      // pointe du fanion qui ondule (petit triangle redessiné au bout du tissu)
      const wob = Math.sin(t * 0.35 + cell) * TS * 0.06
      ctx.fillStyle = '#e05a5a'
      ctx.beginPath()
      ctx.moveTo(x + TS * 10 / 16, y + TS * 3 / 16)
      ctx.lineTo(x + TS * 12.5 / 16 + wob, y + TS * 4.5 / 16)
      ctx.lineTo(x + TS * 10 / 16, y + TS * 6 / 16)
      ctx.closePath(); ctx.fill()
    } else if (code === DECOS.lucioles.code) {
      // essaim : 3 points qui dansent en ellipses déphasées et clignotent
      for (let i = 0; i < 3; i++) {
        const a = t * (0.06 + i * 0.02) + cell * 1.7 + i * 2.1
        const fx = x + TS / 2 + Math.cos(a) * TS * (0.22 + i * 0.07)
        const fy = y + TS / 2 + Math.sin(a * 1.3) * TS * (0.18 + i * 0.06)
        const blink = 0.45 + 0.45 * Math.sin(t * 0.3 + i * 2.4 + cell)
        ctx.fillStyle = `rgba(255,240,150,${blink.toFixed(3)})`
        ctx.beginPath(); ctx.arc(fx, fy, 2.2, 0, Math.PI * 2); ctx.fill()
      }
    } else if (code >= TRESOR_CODE_MIN && DECO_BY_CODE[code]?.animated !== false) {
      // TRÉSORS (21/07) : étincelle en croix qui pulse, déphasée par cellule — même langage que le
      // scintillement de l'or (drawGoldSparkle), en plus discret (déco, pas ramassable).
      // `animated !== false` : les GRAVURES égyptiennes (codes hauts, animated: false) ne
      // scintillent pas — la plage ≥64 signifie « feuille HD », pas « trésor ».
      const ph = cell * 1.31
      const pulse = 0.5 + 0.5 * Math.sin(t * 0.13 + ph)
      if (pulse > 0.3) {
        const sx = x + TS * (0.28 + 0.44 * (((cell * 7919) % 97) / 97)) // position stable par cellule
        const sy = y + TS * (0.22 + 0.36 * (((cell * 104729) % 89) / 89))
        const r = 1 + pulse * 3
        ctx.globalCompositeOperation = 'lighter'
        ctx.fillStyle = `rgba(255,251,224,${(pulse * 0.7).toFixed(3)})`
        ctx.fillRect(sx - r, sy, 2 * r + 1, 1); ctx.fillRect(sx, sy - r, 1, 2 * r + 1)
        ctx.fillStyle = `rgba(255,255,255,${(pulse * 0.9).toFixed(3)})`
        ctx.fillRect(sx - 1, sy - 1, 3, 3)
      }
    } else {
      // flamme (torche : sommet ~row 2 ; bougie : ~row 3), vacillement latéral au tick
      const fx = x + TS * 7 / 16, fy = y + TS * (code === DECOS.bougie.code ? 3 : 2) / 16
      const flick = Math.sin(t * 0.4 + cell) * TS * 0.03
      ctx.fillStyle = 'rgba(255,150,60,0.9)'
      ctx.beginPath(); ctx.ellipse(fx + flick, fy, TS * 0.08, TS * 0.15, 0, 0, Math.PI * 2); ctx.fill()
      ctx.fillStyle = 'rgba(255,232,130,0.95)'
      ctx.beginPath(); ctx.ellipse(fx + flick, fy + TS * 0.03, TS * 0.045, TS * 0.08, 0, 0, Math.PI * 2); ctx.fill()
    }
    ctx.restore()
  }
  ctx.restore()
}

// Décos de PREMIER PLAN (cascades) : dessinées APRÈS les entités, en semi-transparence — le coureur
// passe DERRIÈRE le rideau et reste deviné au travers. Sprite de base + rideau ANIMÉ au tick
// (bandes qui défilent vers le bas + gouttes/braises), déphasé par colonne pour une chute continue
// sur des cascades empilées verticalement. Cosmétique pur, espace MONDE (sous la caméra).
// CASCADES langage CARTOON POP (choix Claude-Alain 22/07, maquette /pages/eau-lave.html modèle 3),
// désormais 100 % procédurales : ruban semi-transparent à bords épais (blanc pour l'eau, jaune pour
// la lave), écailles d'écoulement défilantes (offset en Y monde -> continu sur une colonne de
// cascades), évasement à la case du BAS, et RACCORD D'IMPACT selon la tuile dessous : couronne de
// gouttes + remous concentriques sur une nappe (eau/lave), éclaboussures simples sur un sol.
// La lave coule à MOITIÉ de la vitesse de l'eau (matière visqueuse).
export function drawFrontDecos(rc: RenderCtx, decor: DecorGrid, t: number, cols: number, terrain?: Uint8Array): void {
  const { ctx } = rc
  // PLAFOND DU DÉCOR (28/07, loi n° 2 de l'audit). C'est ICI que se jouait le
  // défaut le plus grave relevé : une carte de la famille portait 65 cascades,
  // dessinées PAR-DESSUS le jeu, plus visibles que le héros — l'œil devait
  // trier. On ne les redessine pas, on les REMET À LEUR PLACE : un rideau reste
  // un rideau, il ne se dispute pas le premier plan avec ce qui se joue.
  ctx.save()
  ctx.globalAlpha = 0.42
  for (let cell = 0; cell < decor.length; cell++) {
    const code = decor[cell] ?? 0
    if (!DECO_FRONT_CODES.has(code)) continue
    const col = colOf(cell, cols), row = rowOf(cell, cols)
    const x = col * TS, y = row * TS
    const lava = code === DECOS.cascadeLave.code
    const above = row > 0 ? (decor[cell - cols] ?? 0) : 0
    const below = decor[cell + cols] ?? 0 // hors grille -> 0 (fin de colonne = pied)
    const isBottom = below !== code
    // la colonne plonge-t-elle dans une nappe ? immergée, elle s'efface presque
    const tHere = terrain ? ((terrain[cell] ?? -1) as T) : (-1 as T)
    const inLiquid = tHere === T.Water || tHere === T.Lava
    const dim = inLiquid ? 0.34 : 1
    // REFONTE « papier découpé » (29/07). Le rideau était dessiné dans le langage
    // cartoon de juillet : bord blanc épais, écailles, écume à dômes, et une
    // BOUCHE DE PIERRE en pixel-art au sommet de chaque colonne. Trois langages
    // sur une carte qui en porte soixante-cinq. Ici, une cascade est ce qu'elle
    // doit être en papier : des BANDES de teintes voisines qui glissent, sans
    // contour, sans écume. Le plafond de la loi n° 2 (42 %) reste au-dessus.
    // ⚠️ LE DESSIN VIT DANS client/cascade-papier.ts DEPUIS LE 31/07. Il était écrit ici en
    // toutes lettres ; Claude-Alain veut comparer trois façons de mieux cacher l'arrière-plan, et
    // une planche qui repeindrait « à peu près » ferait valider autre chose que ce qu'il verra.
    // Le jeu et la planche appellent donc la MÊME fonction — même discipline que les compagnons.
    // Le rang depuis le déversoir : on remonte la colonne tant que c'est la même cascade.
    let depuisSommet = 0
    while (row - depuisSommet > 0 && (decor[cell - (depuisSommet + 1) * cols] ?? 0) === code) depuisSommet++
    peindreCascade(ctx, x, y, TS, {
      lave: lava, bas: isBottom, sommet: above !== code && row > 0 && !inLiquid, depuisSommet,
      flow: lava ? t * 1.4 : t * 2.6,          // la lave rampe deux fois moins vite
      alpha: 0.42 * dim,                        // le plafond de la loi n° 2
    })
    // 🚨 IL Y AVAIT ICI UN ctx.restore() ORPHELIN (31/07). Avant l'extraction du dessin vers
    // cascade-papier.ts, la boucle ouvrait son propre ctx.save() ; l'extraction l'a retiré — puisque
    // peindreCascade équilibre le sien — mais a laissé le restore. Résultat : la PREMIÈRE cascade
    // dépilait le save de cette fonction, et la DEUXIÈME celui de la COUCHE MONDE, qui porte la
    // translation caméra. À partir de la deuxième, les cascades étaient donc peintes en
    // coordonnées ÉCRAN : elles SUIVAIENT LA CAMÉRA au lieu de rester sur la carte.
    // Ne rien remettre ici : peindreCascade gère son état, et l'alpha du parent est intact au retour.
  }
  ctx.restore()
}

// EAU vivante (16/07, « il manque les visuels ») : par-dessus le sprite — ligne de SURFACE qui
// ondule (seulement si la case au-dessus n'est pas de l'eau) + reflets internes qui scintillent.
// Piloté au tick (déphasé par colonne), cosmétique pur.
// EAU langage CARTOON POP (choix Claude-Alain 22/07, maquette /pages/eau-lave.html modèle 3) :
// grosses vagues rondes à bord blanc épais + bande claire sous la crête + croissants de lumière
// qui scintillent dans le corps. La phase dépend de x MONDE -> continue de case en case.
function drawWaterAnim(ctx: CanvasRenderingContext2D, x: number, y: number, t: number, isSurface: boolean): void {
  // REFONTE « papier découpé » (29/07). L'eau était un ruban cartoon à grosse
  // ligne blanche roulante : un troisième langage par-dessus la tuile de papier.
  // Elle devient ce qu'elle doit être ici — une DÉCOUPE dont le bord ondule,
  // et des plis qui glissent. Le dessin de la tuile (terrain-papier) fait le
  // corps, cette passe ne fait que le rendre vivant.
  ctx.save()
  if (isSurface) {
    const houle = (px: number): number => y + 5 + Math.sin((x + px) * 0.035 + t * 0.07) * 3.2
    // le bord de la découpe : une frange dentelée qui suit la houle
    ctx.fillStyle = 'rgba(160,214,240,0.85)'
    ctx.beginPath(); ctx.moveTo(x, houle(0))
    for (let px = 3; px <= TS; px += 3) ctx.lineTo(x + px, houle(px))
    ctx.lineTo(x + TS, y + 11); ctx.lineTo(x, y + 11); ctx.closePath(); ctx.fill()
    // l'éclat du pli, plus clair, décalé d'un cran : c'est lui qui bouge à l'œil
    ctx.fillStyle = 'rgba(232,246,252,0.6)'
    ctx.beginPath(); ctx.moveTo(x, houle(0) - 2)
    for (let px = 3; px <= TS; px += 3) ctx.lineTo(x + px, houle(px) - 2)
    ctx.lineTo(x + TS, houle(TS)); ctx.lineTo(x, houle(0)); ctx.closePath(); ctx.fill()
  } else {
    // Sous la surface : des plis qui glissent. Leur PRÉSENCE et leur phase
    // dépendent de la case dans le MONDE — sans ça, chaque case dessinait le
    // même trait au même endroit au même moment, et un grand bassin affichait
    // une grille de traits parfaitement alignés : exactement le défaut que le
    // motif de la tuile venait de perdre. Un pli sur deux cases environ suffit.
    const cx = Math.round(x / TS), cy = Math.round(y / TS)
    const n = bruit(cx * 3, cy * 7)
    if (n > 0.42) {
      ctx.fillStyle = 'rgba(255,255,255,0.10)'
      const d = ((t * 0.35 + cx * 19 + cy * 31) % (TS * 2)) - TS
      ctx.fillRect(x + 2 + n * 9, y + ((d + TS) % TS), TS * (0.35 + n * 0.5), 2)
    }
  }
  ctx.restore()
}

// LAVE pop, ligne de SURFACE (même langage) : bande claire + crête jaune épaisse + dôme de bulle
// qui gonfle puis éclate en gouttelettes (cycle déterministe déphasé par case).
function drawLavaSurfaceAnim(ctx: CanvasRenderingContext2D, x: number, y: number, t: number): void {
  // REFONTE (29/07) : la surface portait une crête jaune épaisse et un dôme de
  // bulle qui éclatait — le langage cartoon pop de juillet, posé sur la frange
  // DENTELÉE que la tuile de papier dessine déjà. Deux surfaces sur la même case.
  // Il ne reste que ce que le papier ne peut pas faire, parce qu'il est figé dans
  // le cache : la respiration. Les dents avancent et reculent, rien d'autre.
  ctx.save()
  const sein = Math.sin(t * 0.045) * 1.6
  ctx.fillStyle = 'rgba(255,154,46,0.5)'
  for (let i = 0; i < TS - 1; i += 7) {
    const h = 5 + Math.sin((x + i) * 0.05 + t * 0.06) * 2.2
    ctx.beginPath()
    ctx.moveTo(x + i, y + 2 + sein); ctx.lineTo(x + i + 3.5, y + 2 + sein - h); ctx.lineTo(x + i + 7, y + 2 + sein)
    ctx.closePath(); ctx.fill()
  }
  ctx.restore()
}

// Vacillement « feu vivant » d'une torche décorative : intensité dans ~[0.39, 0.93], IRRÉGULIÈRE
// (somme de 3 sinus à fréquences ~2.6 / 6.2 / 12.9 Hz à 30 ticks/s, la plage d'une vraie flamme) et
// DÉPHASÉE par instance (`cell`) pour que deux torches ne battent pas à l'unisson. Pure et déterministe
// (t = tick + alpha, jamais l'horloge murale) -> testable, jamais dans la simulation ni le hash.
export function torchFlicker(t: number, cell: number): number {
  const s = cell * 1.7
  return 0.66 + 0.13 * Math.sin(t * 0.55 + s) + 0.08 * Math.sin(t * 1.3 + s * 2.3) + 0.06 * Math.sin(t * 2.7 + s * 0.7)
}

// Luminance (0..1) du bas du ciel d'un biome, dérivée de WORLDS (aucune table à maintenir si un biome
// est ajouté). Sert à doser le halo des torches : un feu éclaire peu au grand jour, fort dans le noir.
export function skyLuma(world: WorldKey): number {
  const h = WORLDS[world].sky[1].replace('#', '')
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16)
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255
}

// Sources de lumière décoratives : rayon (en blocs) et facteur d'intensité par type de déco. La torche
// éclaire large et fort (3 blocs) ; la bougie, flamme plus petite, éclaire plus court et plus doux
// (2 blocs). Étendre cette table pour toute future déco lumineuse (clé = code de la grille `decor`).
const LIGHT_SOURCES: Record<number, { blocks: number; mul: number }> = {
  [DECOS.torche.code]: { blocks: 3, mul: 1 },
  [DECOS.bougie.code]: { blocks: 2, mul: 0.82 },
  [DECOS.lanterne.code]: { blocks: 2, mul: 0.9 },      // lanterne sur poteau (vague 2)
  [DECOS.cascadeLave.code]: { blocks: 2, mul: 0.75 },  // la lave en fusion rougeoie
}
const LIGHT_MARGIN = 3 * TS // culling : le plus grand rayon de LIGHT_SOURCES (aucun halo hors écran omis)

// --- PÉNOMBRE des zones abritées (chantier Pharaon premium, 22/07) ---------------------------
// Voile sombre drapé sur les cases qu'aucune lumière du ciel n'atteint (masque pur shelter.ts),
// bords fondus par UNE passe de blur au bake. Canvas hors-écran à l'échelle de la carte (+ marges :
// le blur ne doit pas « éclaircir » les bords), re-baké SEULEMENT si le masque change — creuser
// dans un tunnel déjà sombre ne re-bake rien ; 1 drawImage par frame ensuite. L'alpha dérive de la
// clarté du ciel (skyLuma) : fort en plein désert/prairie, quasi nul en grotte/classique (les
// cartes sombres historiques restent INTACTES : sous 0.03 le voile ne s'active pas du tout).
const VEIL_PAD = 40
const VEIL_BLUR = 14
let veilSig = ''
let veilMask: Uint8Array | null = null
let veilCanvas: HTMLCanvasElement | null = null

function veilAlpha(world: WorldKey): number {
  return 0.24 * skyLuma(world)
}

// Met à jour masque + canvas du voile pour ce state ; retourne le masque courant (null = voile
// inactif pour ce biome). Appelé une fois par frame depuis render(), coût nul si rien n'a changé.
function updateShelter(s: GameState, world: WorldKey): Uint8Array | null {
  if (veilAlpha(world) < 0.03) return null
  const sig = `${world}|${s.cols}x${s.rows}|${terrainHash(s)}`
  if (sig === veilSig && veilCanvas) return veilMask
  veilSig = sig
  const mask = shelterMask(s.terrain, s.cols)
  const w = s.cols * TS + 2 * VEIL_PAD, h = s.rows * TS + 2 * VEIL_PAD
  if (veilCanvas && veilCanvas.width === w && veilCanvas.height === h && sameMask(veilMask, mask)) { veilMask = mask; return veilMask }
  veilMask = mask
  // 1) masque net : spans horizontaux par rangée, prolongés dans les marges aux bords de carte
  //    (sinon le blur moyenne avec le vide et l'ombre s'estompe au ras des murs d'enceinte).
  const raw = document.createElement('canvas')
  raw.width = w
  raw.height = h
  const rctx = raw.getContext('2d')!
  rctx.fillStyle = '#0f0c20'
  for (let r = 0; r < s.rows; r++) {
    let c = 0
    while (c < s.cols) {
      if (!mask[r * s.cols + c]) { c++; continue }
      let c2 = c
      while (c2 < s.cols && mask[r * s.cols + c2]) c2++
      const extL = c === 0 ? VEIL_PAD : 0
      const extR = c2 === s.cols ? VEIL_PAD : 0
      const extB = r === s.rows - 1 ? VEIL_PAD : 0
      rctx.fillRect(VEIL_PAD + c * TS - extL, VEIL_PAD + r * TS, (c2 - c) * TS + extL + extR, TS + extB)
      c = c2
    }
  }
  // 2) bords fondus : blur unique au bake, jamais par frame
  const cv = veilCanvas && veilCanvas.width === w && veilCanvas.height === h ? veilCanvas : document.createElement('canvas')
  cv.width = w // resize = clear (même canvas réutilisé quand les dims ne bougent pas)
  cv.height = h
  const cctx = cv.getContext('2d')!
  cctx.filter = `blur(${VEIL_BLUR}px)`
  cctx.drawImage(raw, 0, 0)
  cctx.filter = 'none'
  veilCanvas = cv
  return veilMask
}

// Halo de lumière des décos à flamme (torche, bougie) : disque ROND, chaud, ADDITIF (« lighter »),
// centré sur chaque source placée. La forme reste un cercle parfait (gradient radial) ; seuls l'intensité
// (flicker) et le rayon « respirent » au tick (feu vivant), JAMAIS la forme. L'intensité est dosée par le
// biome (`skyLuma`) ET par le type (`mul`) : la flamme FLAMBOIE dans une grotte sombre, reste discrète en
// plein désert (comme un vrai feu). Dessiné SOUS les entités (le coureur reste net) et culé au champ caméra
// + marge du plus grand rayon (aucun gradient hors écran, même sur une grande carte). Cosmétique pur.
function drawLightHalos(rc: RenderCtx, decor: DecorGrid, t: number, cols: number, cam: { x: number; y: number }, world: WorldKey, shelter: Uint8Array | null = null): void {
  if (decor.length === 0) return
  const boost = 1 + 1.4 * (1 - skyLuma(world)) // ~2.3 en grotte/usine/classique, ~1.2 en désert/neige/prairie
  const BOOST_DARK = 2.3 // source en zone ABRITÉE (pénombre 22/07) : elle flamboie comme dans une grotte
  const vx0 = cam.x - LIGHT_MARGIN, vy0 = cam.y - LIGHT_MARGIN
  const vx1 = cam.x + COLS * TS + LIGHT_MARGIN, vy1 = cam.y + ROWS * TS + LIGHT_MARGIN
  const { ctx } = rc
  for (let cell = 0; cell < decor.length; cell++) {
    const src = LIGHT_SOURCES[decor[cell] ?? 0]
    if (!src) continue
    const cx = colOf(cell, cols) * TS + TS / 2, cy = rowOf(cell, cols) * TS + TS / 2
    if (cx < vx0 || cx > vx1 || cy < vy0 || cy > vy1) continue // hors champ (marge = plus grand rayon)
    const R = src.blocks * TS
    const b = shelter && shelter[cell] ? Math.max(boost, BOOST_DARK) : boost
    const k = Math.min(1.7, torchFlicker(t, cell) * b * src.mul) // dosé par biome ET type (et pénombre), borné : le cœur ne « crame » pas
    const rr = R * (0.94 + 0.06 * Math.sin(t * 0.9 + cell * 1.7))     // « respire » ±6 %, reste un disque
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, rr)
    g.addColorStop(0, `rgba(255,245,208,${(0.46 * k).toFixed(3)})`)    // cœur blanc-chaud
    g.addColorStop(0.16, `rgba(255,201,116,${(0.34 * k).toFixed(3)})`) // jaune-orangé
    g.addColorStop(0.42, `rgba(255,126,50,${(0.18 * k).toFixed(3)})`)  // orange
    g.addColorStop(1, 'rgba(255,80,30,0)')                             // rouge -> transparent
    ctx.fillStyle = g
    ctx.beginPath(); ctx.arc(cx, cy, rr, 0, Math.PI * 2); ctx.fill()
    ctx.restore()
  }
}

// --- Premier plan (polish cosmétique, hors simulation ; dessiné en espace MONDE, sous la caméra).
// Relief des plateformes, ombres portées, bloom des collectibles. Aucun état ajouté ; le léger
// battement du bloom suit le tick (cur.tick + alpha), jamais l'horloge murale. ---
const RELIEF_TILES = new Set<number>([T.Brick, T.Concrete, T.FalseBrick, T.Sand])
// Tuiles à animer au tick (cf. drawTileAnim) : lave, tapis (H/V), glace, trampolines, porte, station bombe.
const ANIM_TILES = new Set<number>([T.Lava, T.Fire, T.ConveyorL, T.ConveyorR, T.ConveyorUp, T.ConveyorDown, T.Ice, T.Trampoline, T.TrampolineL, T.TrampolineR, T.TrampolineStrong, T.TrampolineStrongL, T.TrampolineStrongR, T.DoorClosed, T.BombRefill, T.Button, T.Barrier, T.BarrierOpen, T.DashCrystal])
// Fin de grâce du circuit (fix 18/07) : posé par render() avant le paint du terrain — quand la
// temporisation de relâchement touche à sa fin (buttonLinger dans <= 45 ticks SANS bouton re-pressé,
// qui re-armerait à +90), les barrières OUVERTES clignotent : le couperet s'annonce.
let barrierCloseWarn = false
// Relief d'une tuile solide : liseré clair (haut + arête gauche) + ombre basse -> volume.
function drawTileRelief(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  ctx.fillStyle = 'rgba(255,255,255,0.13)'; ctx.fillRect(x, y, TS, 3)
  ctx.fillStyle = 'rgba(255,255,255,0.06)'; ctx.fillRect(x, y, 3, TS)
  ctx.fillStyle = 'rgba(0,0,0,0.22)'; ctx.fillRect(x, y + TS - 4, TS, 4)
}
// Une case est-elle un MUR PLEIN (pour l'auto-tuilage des bords HD) ? Hors carte = plein (le mur
// continue au bord du monde -> pas d'arête parasite sur le cadre). brique/béton/friable/fausse brique.
function isWallAt(s: GameState, col: number, row: number): boolean {
  if (col < 0 || row < 0 || col >= s.cols || row >= s.rows) return true
  const t = s.terrain[row * s.cols + col] ?? 0
  return t === T.Brick || t === T.FalseBrick || t === T.Concrete || t === T.Crumble
}
// BORDS INTELLIGENTS (refonte HD, auto-tuilage) : remplace le relief UNIFORME par-case pour les murs
// HD classiques. On ne peint QUE sur les faces EXPOSÉES (voisine non-mur) -> l'intérieur d'un mur reste
// une texture continue, la grille de 48px disparaît. Lumière (haut-gauche) sur dessus/gauche exposés,
// ombre sur bas/droite, mousse/lichen sur le dessus exposé (le "sol"), petite occlusion aux coins.
function drawWallEdges(ctx: CanvasRenderingContext2D, s: GameState, col: number, row: number, style: HdStyle): void {
  const x = col * TS, y = row * TS
  const up = isWallAt(s, col, row - 1), down = isWallAt(s, col, row + 1)
  const left = isWallAt(s, col - 1, row), right = isWallAt(s, col + 1, row)
  const veg = `rgb(${style.veg[0]},${style.veg[1]},${style.veg[2]})`, vegHi = `rgb(${style.vegHi[0]},${style.vegHi[1]},${style.vegHi[2]})`
  if (!up) { // dessus exposé = arête vive + frange (mousse/herbe/givre/sable/rouille selon le biome)
    ctx.fillStyle = 'rgba(255,238,196,0.26)'; ctx.fillRect(x, y, TS, 3)
    ctx.fillStyle = 'rgba(0,0,0,0.14)'; ctx.fillRect(x, y + 3, TS, 1)
    const g = hashCell(col * 3 + 1, row * 7 + 5)
    if (g % 5 < 3) for (let i = 0; i < 7; i++) { // ~60 % des rebords portent un peu de dépôt
      const gx = x + 2 + ((g >> (i * 3)) & 7) + i * 6
      if (gx >= x + TS - 1) continue
      const th = 2 + ((g >> (i * 2)) & 3)
      ctx.fillStyle = ((g >> i) & 1) ? veg : vegHi
      ctx.fillRect(gx, y - th, 1, th)
      if (((g >> (i + 4)) & 3) === 0) { ctx.fillStyle = vegHi; ctx.fillRect(gx, y - th - 1, 1, 1) }
    }
  }
  if (!left) { ctx.fillStyle = 'rgba(255,238,196,0.12)'; ctx.fillRect(x, y, 2, TS) }        // gauche exposé = liseré clair
  if (!right) { ctx.fillStyle = 'rgba(0,0,0,0.20)'; ctx.fillRect(x + TS - 2, y, 2, TS) }     // droite = ombre
  if (!down) { ctx.fillStyle = 'rgba(0,0,0,0.30)'; ctx.fillRect(x, y + TS - 4, TS, 4) }      // sous-face = ombre portée
  if (up && !left) { ctx.fillStyle = 'rgba(0,0,0,0.12)'; ctx.fillRect(x, y, 5, 5) }          // occlusion coin concave
  if (up && !right) { ctx.fillStyle = 'rgba(0,0,0,0.12)'; ctx.fillRect(x + TS - 5, y, 5, 5) }
}
// Altérations de surface des murs HD (toutes APÉRIODIQUES, dosées pour rester lisibles) : taches
// d'humidité LARGES (2e champ de bruit cohérent, indépendant de l'usure -> débordent les cases en
// douceur) + coulures verticales sombres et dépôts de salpêtre (hash global, ~13 % des cases).
function drawWallOverlay(ctx: CanvasRenderingContext2D, col: number, row: number, style: HdStyle): void {
  const x = col * TS, y = row * TS
  const damp = worldNoise(col + 41, row + 17)
  if (damp > 0.6) { ctx.fillStyle = `rgba(${style.damp[0]},${style.damp[1]},${style.damp[2]},${Math.min(0.26, (damp - 0.6) * 0.8).toFixed(3)})`; ctx.fillRect(x, y, TS, TS) }
  const s = hashCell(col * 5 + 3, row * 9 + 1)
  if (s % 100 < 13) for (let i = 0, n = 1 + (s & 1); i < n; i++) {
    const cx = x + 5 + ((s >> (i * 4)) % (TS - 10)), len = 12 + ((s >> (i * 3)) % (TS - 16))
    ctx.fillStyle = 'rgba(18,14,10,0.15)'; ctx.fillRect(cx, y, 2, len)
    ctx.fillStyle = 'rgba(18,14,10,0.09)'; ctx.fillRect(cx - 1, y, 1, len - 4); ctx.fillRect(cx + 2, y, 1, len - 6)
    if (s % 3 === 0) { ctx.fillStyle = 'rgba(226,224,208,0.30)'; ctx.fillRect(cx, y + 1, 2, 2) } // salpêtre
  }
}
// GRANDES LÉZARDES multi-cases (passe SÉPARÉE, après le terrain -> par-dessus les briques). ~3 % des
// cases mur HD sont un départ ; le tracé zigzague vers le bas sur plusieurs cases et S'ARRÊTE dès qu'il
// sort du mur (garde anti-débordement dans le vide). Apériodique (hash global, aucun modulo de position).
function drawBigCracks(rc: RenderCtx, s: GameState, world: WorldKey, col0: number, row0: number, col1: number, row1: number): void {
  if (!HD_BIOMES[world] || !worldTilesSheet()) return
  const ctx = rc.ctx
  for (let row = row0; row < row1; row++) for (let col = col0; col < col1; col++) {
    if (!isWallAt(s, col, row)) continue
    const h = hashCell(col * 11 + 2, row * 13 + 6)
    if (h % 100 >= 3) continue
    let cx = col * TS + TS / 2 + (h % 10) - 5, cy = row * TS + 5
    ctx.strokeStyle = 'rgba(15,9,7,0.6)'; ctx.lineWidth = 2
    ctx.beginPath(); ctx.moveTo(cx, cy)
    for (let k = 0, steps = (3 + (h % 4)) * 8; k < steps; k++) {
      const hh = hashCell(Math.floor(cx), Math.floor(cy))
      cx += (hh % 3) - 1; cy += 5 + (hh % 2)
      if (!isWallAt(s, Math.floor(cx / TS), Math.floor(cy / TS))) break
      ctx.lineTo(cx, cy)
    }
    ctx.stroke()
  }
}
// Ombre portée au sol : ellipse douce sous les pieds d'une entité dessinée en (sx,sy), taille TS.
function drawGroundShadow(ctx: CanvasRenderingContext2D, sx: number, sy: number): void {
  ctx.save(); ctx.fillStyle = 'rgba(0,0,0,0.26)'
  ctx.beginPath(); ctx.ellipse(sx + TS / 2, sy + TS - 3, TS * 0.33, TS * 0.1, 0, 0, Math.PI * 2); ctx.fill()
  ctx.restore()
}
// Bloom : halo ADDITIF d'un collectible/danger centré sur sa case (x,y = coin), battement au tick.
function drawBloom(ctx: CanvasRenderingContext2D, x: number, y: number, rgb: string, r: number, t: number): void {
  const cx = x + TS / 2, cy = y + TS / 2, rr = r * (0.86 + 0.14 * Math.sin(t * 0.15 + cx * 0.05))
  ctx.save(); ctx.globalCompositeOperation = 'lighter'
  const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, rr)
  g.addColorStop(0, `rgba(${rgb},0.5)`); g.addColorStop(1, `rgba(${rgb},0)`)
  ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, rr, 0, Math.PI * 2); ctx.fill()
  ctx.restore()
}
// TÉLÉPORTEUR : état de la paire, PAR-DESSUS la tuile du cache terrain (demande Claude-Alain 30/07,
// « le TP brille quand il est fonctionnel, et grisé s'il a été utilisé, avec un décompte visuel »).
// Prêt   : halo violet qui respire + anneau plein — on voit de loin qu'il est ouvert.
// Chargé : voile gris qui éteint la tuile + anneau de décompte qui SE REMPLIT jusqu'au retour du
//          halo, et une lueur qui grandit sur la dernière demi-seconde (le « c'est bientôt bon »).
// L'état vient de la SIMULATION (teleCooldownRatio), jamais d'une horloge locale : les 3 écrans
// d'une partie en ligne affichent le même décompte, à la frame près.
export function drawTeleporterState(ctx: CanvasRenderingContext2D, s: GameState, col: number, row: number, t: number): void {
  const x = col * TS, y = row * TS
  const cx = x + TS / 2, cy = y + TS / 2, r = TS * 0.34
  const ratio = teleCooldownRatio(s, row * s.cols + col)
  if (ratio >= 1) {
    drawBloom(ctx, x, y, '190,130,255', 22, t)
    ctx.save(); ctx.globalCompositeOperation = 'lighter'
    ctx.strokeStyle = `rgba(214,170,255,${0.5 + 0.25 * Math.sin(t * 0.16)})`; ctx.lineWidth = 2
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke(); ctx.restore()
    return
  }
  ctx.save()
  ctx.fillStyle = 'rgba(24,22,34,0.62)'   // la bouche s'éteint : grisée, clairement hors service
  ctx.fillRect(x, y, TS, TS)
  ctx.strokeStyle = 'rgba(255,255,255,0.16)'; ctx.lineWidth = 2   // rail du décompte
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke()
  ctx.strokeStyle = 'rgba(190,150,255,0.9)'; ctx.lineWidth = 3    // aiguille : se remplit dans le sens horaire
  ctx.beginPath(); ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + ratio * Math.PI * 2); ctx.stroke()
  ctx.restore()
  if (ratio > 0.82) drawBloom(ctx, x, y, '190,130,255', 10 + (ratio - 0.82) * 66, t) // il se rallume
}
// Scintillement « bling » de l'or : 2 éclats en croix de lumière qui pulsent en déphasage (au tick),
// par-dessus la pièce. Additif (lighter) + pixels nets pour rester dans le style. Chaque pièce déphasée par sa position.
function drawGoldSparkle(ctx: CanvasRenderingContext2D, x: number, y: number, t: number): void {
  const cx = x + TS / 2, cy = y + TS / 2
  const spots: Array<[number, number, number]> = [[cx + 9, cy - 9, 0], [cx - 8, cy + 8, 2.4]]
  ctx.save(); ctx.globalCompositeOperation = 'lighter'
  for (const [sx, sy, ph] of spots) {
    const p = 0.5 + 0.5 * Math.sin(t * 0.16 + ph + cx * 0.03)
    if (p < 0.22) continue
    const r = Math.round(2 + p * 5)
    ctx.fillStyle = `rgba(255,251,224,${(p * 0.8).toFixed(3)})`
    ctx.fillRect(sx - r, sy, 2 * r + 1, 1)       // branche horizontale
    ctx.fillRect(sx, sy - r, 1, 2 * r + 1)       // branche verticale
    ctx.fillStyle = `rgba(255,255,255,${p.toFixed(3)})`
    ctx.fillRect(sx - 1, sy - 1, 3, 3)           // cœur brillant
  }
  ctx.restore()
}
// Tuiles animées (cosmétique, au tick) : bulles de lave, flèches de tapis, scintillement de glace,
// vibration de trampoline, serrure de porte, halo de station de bombes. PAR-DESSUS le sprite, sous les entités.
function drawTileAnim(ctx: CanvasRenderingContext2D, tile: number, x: number, y: number, t: number): void {
  if (tile === T.Fire) { // langues de flammes qui lèchent vers le haut (déphasées par colonne)
    ctx.save()
    for (let i = 0; i < 3; i++) {
      const ph = (t * 0.07 + i * 0.33 + x * 0.01) % 1
      const fx = x + TS * (0.2 + 0.3 * i), fh = TS * (0.25 + 0.35 * Math.sin(ph * Math.PI))
      ctx.fillStyle = i === 1 ? 'rgba(255,220,90,0.75)' : 'rgba(255,140,50,0.7)'
      ctx.beginPath()
      ctx.moveTo(fx, y + TS - 2); ctx.quadraticCurveTo(fx + 3, y + TS - 2 - fh, fx + 1.5, y + TS - 2 - fh * 1.25)
      ctx.quadraticCurveTo(fx + 5, y + TS - 2 - fh * 0.6, fx + 6, y + TS - 2); ctx.closePath(); ctx.fill()
    }
    ctx.restore()
  } else if (tile === T.Lava) { // bulles rondes vives qui montent (langage pop 22/07)
    ctx.save()
    for (let i = 0; i < 3; i++) {
      const ph = (t * 0.05 + i * 0.37) % 1, bx = x + TS * (0.22 + 0.28 * ((i * 7) % 3)), by = y + TS * (1 - ph)
      if (ph < 0.9) {
        ctx.globalAlpha = 0.85 * (1 - ph * 0.55)
        ctx.fillStyle = '#ff9430'
        ctx.beginPath(); ctx.arc(bx, by, 3.4 * (1 - ph * 0.35), 0, Math.PI * 2); ctx.fill()
        ctx.strokeStyle = '#ffc84a'; ctx.lineWidth = 1.5; ctx.stroke()
      }
    }
    ctx.restore()
    // REFONTE (29/07) : ces chevrons étaient des traits BLANCS épais du langage
    // cartoon, posés sur la flèche de papier déjà dessinée par la tuile — deux
    // flèches à la fois sur la même case. Ils passent à l'encre et s'effacent :
    // la flèche papier dit la DIRECTION, eux ne disent plus que le MOUVEMENT.
  } else if (tile === T.ConveyorL || tile === T.ConveyorR) { // chevrons qui défilent horizontalement
    const dir = tile === T.ConveyorR ? 1 : -1
    // clip sur la case : un chevron pris à cheval sur le bord bavait sur la case
    // voisine, qui n'est pas forcément un tapis (ici de l'eau).
    ctx.save(); ctx.beginPath(); ctx.rect(x, y, TS, TS); ctx.clip()
    ctx.strokeStyle = 'rgba(58,44,30,0.3)'; ctx.lineWidth = 1.6; ctx.lineCap = 'round'
    for (let k = 0; k < 3; k++) { const off = (((t * 0.6 * dir + k * TS / 3) % TS) + TS) % TS, cxp = x + off, cy = y + TS - 7; ctx.beginPath(); ctx.moveTo(cxp - dir * 4, cy - 3.5); ctx.lineTo(cxp, cy); ctx.lineTo(cxp - dir * 4, cy + 3.5); ctx.stroke() }
    ctx.restore()
  } else if (tile === T.ConveyorUp || tile === T.ConveyorDown) { // chevrons verticaux
    const dir = tile === T.ConveyorDown ? 1 : -1
    ctx.save(); ctx.beginPath(); ctx.rect(x, y, TS, TS); ctx.clip()
    ctx.strokeStyle = 'rgba(58,44,30,0.3)'; ctx.lineWidth = 1.6; ctx.lineCap = 'round'
    for (let k = 0; k < 3; k++) { const off = (((t * 0.6 * dir + k * TS / 3) % TS) + TS) % TS, cyp = y + off, cx = x + TS - 7; ctx.beginPath(); ctx.moveTo(cx - 3.5, cyp - dir * 4); ctx.lineTo(cx, cyp); ctx.lineTo(cx + 3.5, cyp - dir * 4); ctx.stroke() }
    ctx.restore()
  } else if (tile === T.Ice) { // éclat qui traverse
    const ph = (t * 0.04) % 1, gx = x + TS * ph
    ctx.save(); ctx.globalAlpha = 0.5 * Math.sin(ph * Math.PI); ctx.fillStyle = 'rgba(255,255,255,0.9)'
    ctx.beginPath(); ctx.moveTo(gx, y + 4); ctx.lineTo(gx + 4, y + TS / 2); ctx.lineTo(gx, y + TS - 4); ctx.lineTo(gx - 4, y + TS / 2); ctx.closePath(); ctx.fill()
    ctx.restore()
  } else if (tile === T.Trampoline || tile === T.TrampolineL || tile === T.TrampolineR || tile === T.TrampolineStrong || tile === T.TrampolineStrongL || tile === T.TrampolineStrongR) { // ressort qui vibre
    // AUDIT 16/07 : le trampoline PUISSANT vibrait pas (absent d'ANIM_TILES) alors qu'il propulse le
    // plus fort. Il vibre donc PLUS AMPLE et PLUS VIF que les autres : l'oeil lit sa puissance.
    // REFONTE (29/07) : cette barre vibrait à 28 % de la hauteur, c'est-à-dire
    // à VINGT PIXELS AU-DESSUS de la toile — un trait vert qui flottait dans le
    // vide. Elle vient se poser sur le bord de la toile de papier, qu'elle fait
    // respirer au lieu de la doubler.
    const strong = tile === T.TrampolineStrong || tile === T.TrampolineStrongL || tile === T.TrampolineStrongR
    const sp = strong ? 0.45 : 0.3, amp = strong ? 2.5 : 1.6
    const yy = y + TS - (strong ? 17 : 13) + Math.sin(t * sp) * amp
    ctx.save(); ctx.strokeStyle = `rgba(232,255,244,${0.4 + 0.3 * Math.sin(t * sp)})`; ctx.lineWidth = strong ? 2.6 : 2
    ctx.beginPath(); ctx.moveTo(x + 4, yy); ctx.lineTo(x + TS - 4, yy); ctx.stroke()
    ctx.restore()
  } else if (tile === T.Button) { // dome qui PULSE doucement : « presse-moi »
    ctx.save(); ctx.fillStyle = `rgba(255,163,158,${0.25 + 0.2 * Math.sin(t * 0.12)})`
    ctx.beginPath(); ctx.arc(x + TS / 2, y + TS * 0.55, 5, 0, Math.PI * 2); ctx.fill(); ctx.restore()
  } else if (tile === T.Barrier) { // barreaux d'energie : ondes qui descendent
    ctx.save(); ctx.strokeStyle = `rgba(255,210,63,${0.3 + 0.25 * Math.sin(t * 0.2)})`; ctx.lineWidth = 2
    const yy = y + ((t * 0.8) % TS)
    ctx.beginPath(); ctx.moveTo(x + 8, yy); ctx.lineTo(x + TS - 8, yy); ctx.stroke(); ctx.restore()
  } else if (tile === T.BarrierOpen) { // fin de grâce : barreaux FANTÔMES qui clignotent (ça va se refermer)
    if (barrierCloseWarn && Math.floor(t / 5) % 2 === 0) {
      ctx.save(); ctx.strokeStyle = 'rgba(255,120,100,0.55)'; ctx.lineWidth = 2
      for (const bx of [10, 24, 38]) { ctx.beginPath(); ctx.moveTo(x + bx, y + 4); ctx.lineTo(x + bx, y + TS - 4); ctx.stroke() }
      ctx.restore()
    }
  } else if (tile === T.DashCrystal) { // scintillement cyan
    ctx.save(); ctx.globalAlpha = 0.3 + 0.3 * Math.sin(t * 0.18)
    ctx.fillStyle = '#c8feff'
    ctx.fillRect(x + TS / 2 - 1, y + 4, 2, 2); ctx.fillRect(x + 6, y + TS / 2, 2, 2)
    ctx.restore()
  } else if (tile === T.DoorClosed) { // serrure qui luit
    ctx.save(); ctx.fillStyle = `rgba(255,210,80,${0.4 + 0.3 * Math.sin(t * 0.15)})`
    ctx.beginPath(); ctx.arc(x + TS / 2, y + TS / 2, 3, 0, Math.PI * 2); ctx.fill()
    ctx.restore()
  } else if (tile === T.BombRefill) { // station de bombes : halo rouge qui pulse
    drawBloom(ctx, x, y, '255,80,80', 20, t)
  }
}
// Torche (option, opt-in) : pénombre ambiante (multiply) + halo chaud (lighter) centrés sur le
// coureur suivi, limités à l'aire de jeu (le HUD reste net). Assombrit fort -> défaut OFF (prefs).
function drawTorchLight(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  const W = COLS * TS, H = ROWS * TS
  ctx.save(); ctx.beginPath(); ctx.rect(0, 0, W, H); ctx.clip()
  ctx.globalCompositeOperation = 'multiply'
  const g = ctx.createRadialGradient(cx, cy, 84, cx, cy, 380)
  g.addColorStop(0, '#ffffff'); g.addColorStop(1, '#241d38')
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H)
  ctx.globalCompositeOperation = 'lighter'
  const w = ctx.createRadialGradient(cx, cy, 0, cx, cy, 150)
  w.addColorStop(0, 'rgba(255,220,150,0.22)'); w.addColorStop(1, 'rgba(255,220,150,0)')
  ctx.fillStyle = w; ctx.fillRect(0, 0, W, H)
  ctx.restore()
}
// Filtre arcade CRT (option, opt-in) : scanlines douces + vignette, sur l'aire de jeu (HUD épargné).
function drawCrt(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  ctx.save()
  ctx.fillStyle = 'rgba(0,0,0,0.1)'
  for (let y = 0; y < h; y += 3) ctx.fillRect(0, y, w, 1)
  const v = ctx.createRadialGradient(w / 2, h / 2, h * 0.34, w / 2, h / 2, h * 0.9)
  v.addColorStop(0, 'rgba(0,0,0,0)'); v.addColorStop(1, 'rgba(0,0,0,0.38)')
  ctx.fillStyle = v; ctx.fillRect(0, 0, w, h)
  ctx.restore()
}

// Tremblé d'écran (option feel, cosmétique) : déclenché sur mort / explosion de bombe / tase, décroît
// sur ~8 ticks. État module (comme les particules) ; lu par render() qui décale la couche MONDE (pas
// le fond -> aucun bord noir). Purement visuel : jamais dans la simulation, le hash ou le rejeu.
let shakeSeenTick = -1, shakeEnd = 0, shakeAmp = 0
// ÉCHANGES D'OUTIL en cours (06/08) : un horodatage par coureur, alimenté par les événements
// de la sim (malletPris / malletRendu). Même motif que la traînée de dash : le rendu se souvient,
// la simulation ne porte rien de cosmétique. Purgé quand le tick recule (nouvelle partie).
const swapAt: ({ at: number; versMaillet: boolean } | undefined)[] = []
let swapSeenTick = -1

function suitEchanges(cur: GameState): void {
  if (cur.tick < swapSeenTick) swapAt.length = 0
  if (cur.tick === swapSeenTick) return
  swapSeenTick = cur.tick
  for (const e of cur.events) {
    if (e.type === 'malletPris') swapAt[e.runner] = { at: cur.tick, versMaillet: true }
    else if (e.type === 'malletRendu') swapAt[e.runner] = { at: cur.tick, versMaillet: false }
  }
}

function updateShake(prev: GameState | null, cur: GameState): void {
  // Nouvel état (tick RÉINITIALISÉ : respawn / niveau suivant / nouvelle partie) : purge le tremblé
  // périmé. Sans ça, shakeEnd restait grand et l'amplitude explosait sur le nouveau state à tick 0
  // (cause racine du « tout l'écran vibre » à chaque mort). Détecté par le tick qui recule.
  if (cur.tick < shakeSeenTick) { shakeAmp = 0; shakeEnd = 0 }
  if (cur.tick === shakeSeenTick) return // une seule évaluation par tick (pas à chaque frame)
  shakeSeenTick = cur.tick
  if (!prev) return
  let amp = 0
  cur.runners.forEach((r, i) => { const p = prev.runners[i]; if (p && p.alive && !r.alive) amp = Math.max(amp, 6) }) // mort d'un coureur
  // ONDE TONNERRE (dash v2, 22/07) : petit tremblé au BOOM du départ (début du burst : fin de charge)
  cur.runners.forEach((r, i) => {
    const p = prev.runners[i]
    if (p && r.dashUntil > cur.tick && p.dashChargeUntil > prev.tick && r.dashChargeUntil <= cur.tick && dashVariant(r.dashUntil, i) === 2) amp = Math.max(amp, 3)
  })
  const bombs = new Set(cur.bombs.map((b) => b.cell))
  for (const b of prev.bombs) if (!bombs.has(b.cell)) amp = Math.max(amp, 8) // bombe disparue (explosion)
  cur.monks.forEach((m, i) => { const p = prev.monks[i]; if (p && m.stunUntil > cur.tick && p.stunUntil <= cur.tick - 1) amp = Math.max(amp, 3) }) // tase (moine nouvellement étourdi)
  if (cur.events.some((e) => e.type === 'malletHit')) amp = Math.max(amp, 5) // COUP DE MAILLET (05/08) : l'impact secoue l'écran, solo comme multi
  if (amp > 0) { shakeAmp = amp; shakeEnd = cur.tick + SHAKE_TICKS }
}
/**
 * Déclenche une secousse depuis le CONTRÔLEUR, sur un événement que updateShake ne peut pas voir en
 * comparant deux états (26/07, chantier « jus ») : le coup de pioche et le moine qui tombe dans le
 * trou. Purement visuel — même contrat que updateShake, jamais dans la simulation ni le hash.
 *
 * ⚠️ La secousse se cale sur le tick COURANT du jeu, pas sur l'horloge : sinon elle survivrait à un
 * changement de niveau (le tick repart à 0) et le nouveau niveau démarrerait en tremblant.
 */
export function bumpShake(amp: number, tick: number): void {
  if (amp <= 0) return
  shakeAmp = Math.max(shakeAmp, amp)
  shakeEnd = Math.max(shakeEnd, tick + SHAKE_TICKS)
}

function shakeOffset(t: number): { x: number; y: number } {
  if (t >= shakeEnd || shakeAmp <= 0) return { x: 0, y: 0 }
  const a = shakeAmplitudeAt(shakeAmp, shakeEnd, t) // BORNÉE (anti-explosion sur reset de tick)
  return { x: a * Math.sin(t * 3.7), y: a * Math.cos(t * 4.3) }
}

// Lissage d'affichage de la poussée des caisses (Phase 4, cosmétique PUR — hors sim/hash). La
// poussée déplace la caisse d'une CASE d'un coup côté moteur (saut-de-case déterministe) ; ici on
// glisse la X affichée vers la X moteur en ease-out -> rendu fluide au rythme du coureur. État
// module (comme le tremblé) : `crateDispX` par index, resync sur reset de tick (nouveau niveau/
// partie) ou changement de nombre de caisses (casse). La Y reste interpolée (chute nette).
let crateDispX: number[] = []
let crateDispTick = -1
const CRATE_EASE = 0.3 // fraction de rapprochement par frame (ease-out ; « un peu plus vif », réglage 16/07)

// Blit d'une TUILE DE TERRAIN depuis la feuille HD world-tiles.png (48 px source, refonte 20/07,
// terminal DÉCOR). world-tiles a la MÊME grille que spritesheet mais en cellules 48 : la cellule d'un
// SpriteRef (coords 16) s'y trouve à (sx*3, sy*3). Blit 1:1 (48->48) = net, fini l'upscale flou.
// Fallback tant que la feuille n'est pas chargée : blit historique 16->48 depuis spritesheet -> jamais
// de case vide. N'est appelée QUE pour les tuiles de TERRAIN ; objets/décor/moines/HUD gardent blit().
function blitWorld(rc: RenderCtx, sr: SpriteRef, x: number, y: number): void {
  const ws = worldTilesSheet()
  if (ws) rc.ctx.drawImage(ws, sr.sx * 3, sr.sy * 3, 48, 48, x, y, TS, TS)
  else rc.ctx.drawImage(rc.sheet, sr.sx, sr.sy, 16, 16, x, y, TS, TS)
}

// Déco de SOL « intégrée » (21/07, « les décos ne s'intègrent pas comme élément de décor ») : un blit
// plein cadre rend un objet-tuile net et saturé qui saute aux yeux. Ici on la dessine RÉDUITE (de l'air
// autour, ancrée au bas de la case) + FONDUE dans l'ambiance (teinte/assombrissement des SEULS pixels
// de la déco, dosé par l'obscurité du ciel via skyLuma) — via un buffer offscreen (JAMAIS drawTinted,
// frontière perso). Buffer réutilisé, cosmétique pur.
// Décos LUMINEUSES (cristal/champignon/lucioles) : gardent leur éclat -> PAS de fondu (juste réduites + ancrées).
const GLOW_DECOS = new Set([
  DECOS.cristal.code, DECOS.champignon.code, DECOS.lucioles.code,
  // Trésors (21/07) : le bling ne doit pas être éteint par l'assombrissement d'ambiance.
  ...(Object.keys(DECOS) as DecoKind[]).filter((k) => k.startsWith('tresor')).map((k) => DECOS[k].code),
])
let decoBuf: HTMLCanvasElement | null = null
function drawDecoGround(rc: RenderCtx, sr: SpriteRef, x: number, y: number, world: WorldKey, code: number): void {
  if (!decoBuf) { decoBuf = document.createElement('canvas'); decoBuf.width = TS; decoBuf.height = TS }
  const bctx = decoBuf.getContext('2d')!
  bctx.imageSmoothingEnabled = false
  bctx.clearRect(0, 0, TS, TS)
  const ws = worldTilesSheet()
  if (ws) bctx.drawImage(ws, sr.sx * 3, sr.sy * 3, 48, 48, 0, 0, TS, TS)
  else bctx.drawImage(rc.sheet, sr.sx, sr.sy, 16, 16, 0, 0, TS, TS)
  if (!GLOW_DECOS.has(code)) { // fondu ambiant (sauf lumineuses) : nuit -> ~0.40, plein jour -> ~0.13
    const dark = 0.44 * (1 - skyLuma(world))
    bctx.save(); bctx.globalCompositeOperation = 'source-atop'
    bctx.fillStyle = `rgba(10,8,24,${dark.toFixed(3)})`
    bctx.fillRect(0, 0, TS, TS); bctx.restore()
  }
  const s = 0.74, w = TS * s // réduite (plus d'air) + ancrée au bas-centre : un accent, pas un objet pleine case
  rc.ctx.drawImage(decoBuf, 0, 0, TS, TS, x + (TS - w) / 2, y + (TS - w), w, w)
}
// Hash déterministe d'une case (col,row) -> entier : choisit la VARIANTE anti-clonage d'une tuile HD.
// Stable à chaque visite (aucun Math.random), donc le mur a toujours la même « personnalité ».
function hashCell(x: number, y: number): number {
  let h = (x * 374761393 + y * 668265263) >>> 0
  h = ((h ^ (h >>> 13)) * 1274126177) >>> 0
  return h >>> 0
}
// Bruit cohérent basse fréquence (value noise 2D) sur la grille de cases : interpolation lissée entre
// des valeurs de hash aux coins d'une maille PLUS GRANDE qu'une case -> des RÉGIONS multi-cases (zones
// humides, zones claires) au lieu d'un damier case-par-case. Déterministe, apériodique sur un niveau.
function worldNoise(col: number, row: number): number {
  const S = 5.5 // taille d'une maille de bruit, en cases (~5-6 cases par région tonale)
  const x = col / S, y = row / S
  const x0 = Math.floor(x), y0 = Math.floor(y), fx = x - x0, fy = y - y0
  const corner = (a: number, b: number): number => (hashCell(a, b) % 100000) / 100000
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy) // smoothstep
  const top = corner(x0, y0) * (1 - sx) + corner(x0 + 1, y0) * sx
  const bot = corner(x0, y0 + 1) * (1 - sx) + corner(x0 + 1, y0 + 1) * sx
  return top * (1 - sy) + bot * sy
}
// Style d'ALTÉRATION HD par biome pour les OVERLAYS au rendu : frange sur les rebords exposés (veg =
// mousse/herbe/givre/sable/rouille selon le biome) + voile d'humidité large (damp). Recopié de HD_STYLE
// de gen-sprites.mjs (mêmes teintes veg, qui pilotent aussi le dépôt incorporé dans la feuille).
interface HdStyle { veg: readonly [number, number, number]; vegHi: readonly [number, number, number]; damp: readonly [number, number, number] }
const HD_STYLE: Record<WorldKey, HdStyle> = {
  classique: { veg: [95, 138, 58], vegHi: [122, 166, 74], damp: [30, 46, 32] },
  grotte: { veg: [78, 116, 80], vegHi: [110, 152, 100], damp: [26, 42, 36] },
  prairie: { veg: [104, 162, 62], vegHi: [150, 200, 86], damp: [40, 62, 36] },
  desert: { veg: [176, 150, 96], vegHi: [214, 188, 132], damp: [86, 68, 42] },
  neige: { veg: [214, 228, 238], vegHi: [255, 255, 255], damp: [120, 140, 162] },
  usine: { veg: [150, 100, 62], vegHi: [186, 128, 78], damp: [46, 38, 32] },
  abysses: { veg: [62, 146, 132], vegHi: [96, 190, 168], damp: [24, 48, 48] },
  manoir: { veg: [92, 112, 60], vegHi: [126, 150, 84], damp: [36, 34, 44] },
}
// Blit d'une CELLULE HD arbitraire de world-tiles.png (coords 48 DIRECTES) : sert aux variantes
// anti-clonage (hors grille socle). Rien si la feuille n'est pas encore chargée (l'appelant retombe
// alors sur blitWorld/ref). 48->48 net, comme blitWorld.
function blitWorldCell(rc: RenderCtx, sx: number, sy: number, x: number, y: number): void {
  const ws = worldTilesSheet()
  if (ws) rc.ctx.drawImage(ws, sx, sy, 48, 48, x, y, TS, TS)
}

// --- CACHE DU TERRAIN (Phase 4, perf mobile) : la couche STATIQUE du terrain (murs HD, bords
// intelligents, taches d'humidité, reliefs, grandes lézardes) est peinte UNE fois dans un canvas
// hors-écran, puis re-blittée chaque frame. Elle ne se repeint QUE lorsque le terrain change
// (signature ci-dessous). Les couches ANIMÉES (lave/feu, tuiles mécaniques, eau) restent dessinées au
// tick, par-dessus, dans render(). Gain : des centaines de dessins procéduraux par frame en moins. ---
let terrainCanvas: HTMLCanvasElement | null = null
let terrainCacheSig = ''
// Signature d'invalidation : contenu du terrain (hash) + monde + téléporteurs (leur teinte dépend des
// paires) + présence de la feuille HD (chargée en asynchrone -> repeindre quand elle arrive). Bon
// marché à recalculer chaque frame ; tant qu'elle ne bouge pas, le cache est réutilisé tel quel.
function terrainSig(s: GameState, world: WorldKey): string {
  let tele = 0
  for (const [a, b] of s.teleporters) tele = Math.imul(tele ^ (a * 65537 + b), 16777619)
  return `${world}|${s.cols}x${s.rows}|${terrainHash(s)}|${tele >>> 0}|${worldTilesSheet() ? 1 : 0}`
}
// Peint TOUTES les cases (pas seulement les visibles) — couche STATIQUE seule. Reprend à l'IDENTIQUE
// le rendu par-case de render() SANS les couches animées (bloom lave/feu, tuiles animées, eau).
// rc.ctx = le contexte du canvas hors-écran.
function paintTerrainStatic(rc: RenderCtx, s: GameState, world: WorldKey, wbg: Uint8Array | null): void {
  // REFONTE « papier découpé » (28/07, lot 3) : les tuiles ne sont plus blittées
  // depuis world-tiles.png mais dessinées, biome par biome, par client/terrain-papier.
  // On peut se le permettre ICI et nulle part ailleurs : ce canvas n'est repeint
  // que quand la signature du terrain change, donc une fois par creusage, pas
  // soixante fois par seconde.
  //
  // Les overlays de l'ancien style (usure HD, bords de mur, relief, grandes
  // fissures) disparaissent : en papier, la frange de découpe fait le bord et le
  // motif imprimé fait la matière. Les empiler produirait deux langages à la fois,
  // ce que toute la refonte cherche justement à supprimer.
  const ctx = rc.ctx
  const P = BIOMES[world]
  // ⚠️ DETAIL est GLOBAL au module papier, et le héros l'ÉTEINT à chaque image
  // (il est dessiné à 44 px, sous le seuil). Ce canvas n'étant repeint qu'au
  // creusage, il héritait donc du réglage du dernier qui avait peint : les murs
  // repoussaient sans grain après le premier trou, alors que la première image
  // en avait. Invisible sur une capture de départ — d'où la faute. On pose.
  setDetail(1)
  const teleColor = teleporterColorMap(s.teleporters)
  for (let row = 0; row < s.rows; row++) {
    for (let col = 0; col < s.cols; col++) {
      const cell = row * s.cols + col
      const tile = s.terrain[cell] ?? 0
      if (tile === T.Empty) continue
      // ⚠️ La ZONE MORTELLE ne se dessine PAS en jeu : c'est un piège invisible,
      // l'ancien rendu l'écartait explicitement (`tile === T.DeathZone ? undefined`).
      // Son dessin violet n'existe QUE pour le mode création, qui doit montrer au
      // créateur où il en a posé. Même faute que la fausse brique, même correctif.
      if (tile === T.DeathZone) continue
      const nom = T[tile]
      if (!nom) continue
      // le téléporteur porte la couleur de sa paire : on la passe en variante
      const kind = tile === T.Teleporter ? `Teleporter${teleColor.get(cell) ?? 0}` : nom
      // IMMERGÉE = la case du dessus est le même liquide. C'est ici, et nulle part
      // ailleurs, qu'on connaît le voisinage : sans cette information l'eau posait
      // une ligne d'horizon à CHAQUE rangée et le bassin se lisait en damier.
      // La case du dessus compte comme liquide si elle EST de l'eau, mais aussi si
      // elle appartient à la NAPPE (une échelle ou une corde posée dans le bassin
      // est peinte en eau au rendu) : sinon l'eau d'en dessous se croit en surface
      // et pose une ligne d'horizon sous chaque barreau.
      const immerge = (tile === T.Water || tile === T.Bubbles)
        ? row > 0 && (s.terrain[cell - s.cols] === T.Water || s.terrain[cell - s.cols] === T.Bubbles
          || (wbg ? wbg[cell - s.cols] === 1 : false))
        // la lave ET le feu passent par `mortel()` : les deux portent la frange
        // dentelée, les deux doivent la réserver à leur première rangée.
        : (tile === T.Lava || tile === T.Fire)
          ? row > 0 && s.terrain[cell - s.cols] === tile
          : false
      tuilePapier(ctx, col * TS, row * TS, TS, kind, P, immerge)
    }
  }
}

// Renvoie le canvas du terrain à jour ; repeint uniquement si la signature (donc le terrain) a changé.
// FOND D'EAU (21/07, « les pièces d'or, les échelles et les téléporteurs ne doivent pas effacer
// l'eau ») : masque de la nappe complète (client/water-bg, pur). L'eau ne changeant JAMAIS en
// cours de partie, l'empreinte des cases d'eau sert de clé : le masque se calcule une fois par
// niveau. Aucune eau sur la carte -> null (zéro coût pour les cartes sèches).
let wbgCache: Uint8Array | null = null
let wbgKey = ''
function waterBgMask(s: GameState): Uint8Array | null {
  let count = 0, sum = 0
  for (let i = 0; i < s.terrain.length; i++) if (s.terrain[i] === T.Water) { count++; sum += i }
  const key = `${s.cols}x${s.rows}:${count}:${sum}`
  if (key !== wbgKey) { wbgKey = key; wbgCache = count ? computeWaterBg(s.terrain, s.cols, s.rows) : null }
  return wbgCache
}

function terrainLayer(rc: RenderCtx, s: GameState, world: WorldKey): HTMLCanvasElement {
  const sig = terrainSig(s, world)
  const w = s.cols * TS, h = s.rows * TS
  if (terrainCanvas && terrainCacheSig === sig && terrainCanvas.width === w && terrainCanvas.height === h) return terrainCanvas
  if (!terrainCanvas) terrainCanvas = document.createElement('canvas')
  if (terrainCanvas.width !== w || terrainCanvas.height !== h) { terrainCanvas.width = w; terrainCanvas.height = h }
  const cctx = terrainCanvas.getContext('2d')!
  cctx.imageSmoothingEnabled = false // pixel-art net, comme le contexte principal (main.ts) : aucun lissage des tuiles HD
  cctx.clearRect(0, 0, w, h)
  paintTerrainStatic({ ctx: cctx, sheet: rc.sheet }, s, world, waterBgMask(s))
  terrainCacheSig = sig
  return terrainCanvas
}

export function render(
  rc: RenderCtx,
  prev: GameState | null,
  cur: GameState,
  alpha: number,
  world: WorldKey = DEFAULT_WORLD,
  decor: DecorGrid = EMPTY_DECOR_GRID, // couche de décorations (cosmétique, hors sim) ; vide par défaut
  digUntil: readonly number[] = [], // par coureur : tick jusqu'auquel jouer la frame `dig` (transitoire client, cosmétique)
  focusIdx = 0, // Lot E : index du coureur suivi par la caméra (solo = 0, online = youAre)
  bounces: readonly (readonly [number, number] | undefined)[] = [], // par coureur : [tick du rebond, ampleur en su] (habillage trampoline 18/07, transitoire client)
  ghost: { prev: Runner | null; cur: Runner | null } | null = null, // FANTÔME du recordman (25/07) : coureur translucide rejoué, hors sim
  // ENTRÉE EN SCÈNE (01/08) : pendant les trois secondes d'arrivée, les coureurs et leurs
  // compagnons ne sont PAS dessinés — c'est l'animation qui peint le héros, et elle le place
  // ailleurs qu'à son spawn. Sans ce drapeau on en verrait deux. Rien d'autre ne change : le
  // terrain, le décor, l'or et les moines sont peints normalement, et l'état n'est pas touché.
  masqueCoureurs = false,
): { camX: number; camY: number } {
  const { ctx } = rc
  const ZOOM = cameraZoom() // pref « Zoom caméra » (Réglages), lue chaque frame : 1.0 recul max -> 1.5 zoom max
  // Fond du monde + décor animé : FIXES au viewport (parallaxe ; ne scrollent PAS avec la carte,
  // et évitent un canvas hors-écran géant sur une grande carte). Le reste (couche monde) scrolle.
  ctx.drawImage(worldBackground(world), 0, 0)
  drawWorldDecor(rc, world, cur.tick + alpha)

  // Caméra (Lot E) : suit le coureur local (position écran interpolée), clampée aux bords de la
  // carte. Carte <= viewport (28x16) -> (0,0) => couche monde dessinée EXACTEMENT comme avant.
  const fr = cur.runners[focusIdx] ?? cur.runners[0]
  const fp = fr ? lerpPos(prev?.runners[focusIdx] ?? prev?.runners[0] ?? null, fr, alpha) : { px: 0, py: 0 }
  const cam = cameraOffset(fp.px + TS / 2, fp.py + TS / 2, cur.cols * TS, cur.rows * TS, COLS * TS / ZOOM, ROWS * TS / ZOOM)
  updateShake(prev, cur)
  suitEchanges(cur) // ÉCHANGES D'OUTIL (06/08) : mémorise les tick de malletPris/malletRendu
  const sh = shakeOffset(cur.tick + alpha) // tremblé d'écran (feel) : décale la couche monde, pas le fond
  ctx.save()
  ctx.beginPath(); ctx.rect(0, 0, COLS * TS, ROWS * TS); ctx.clip() // couche monde bornée à l'aire de jeu (HUD net sous le zoom)
  ctx.scale(ZOOM, ZOOM) // zoom caméra : agrandit la couche monde autour du coureur suivi (cam ci-dessus)
  ctx.translate(-cam.x + sh.x, -cam.y + sh.y) // couche MONDE : caméra (Lot E) + tremblé (feel)

  // 1. TERRAIN — la couche STATIQUE (murs HD, bords intelligents, taches, reliefs, lézardes) vient
  //    d'un canvas hors-écran (cache) peint 1x et re-blitté à la portion VISIBLE ; il ne se repeint que
  //    quand le terrain change (signature = hash du terrain + monde + téléporteurs + feuille HD). On
  //    économise ainsi des centaines de dessins procéduraux par frame (gain mobile). Les couches
  //    ANIMÉES (lave/feu, tuiles mécaniques, eau) restent dessinées au tick, par-dessus, juste après.
  const col0 = Math.max(0, Math.floor(cam.x / TS)), row0 = Math.max(0, Math.floor(cam.y / TS))
  const col1 = Math.min(cur.cols, Math.ceil((cam.x + COLS * TS) / TS) + 1)
  const row1 = Math.min(cur.rows, Math.ceil((cam.y + ROWS * TS) / TS) + 1)
  const layer = terrainLayer(rc, cur, world)
  if (col1 > col0 && row1 > row0) {
    const sx = col0 * TS, sy = row0 * TS, sw = (col1 - col0) * TS, sh = (row1 - row0) * TS
    // blit de la seule fenêtre visible du cache (net : imageSmoothing off). NB : à zoom != 100 %, les
    // traits fins des bords sont rééchantillonnés comme tout le reste du rendu — imperceptible, assumé.
    ctx.drawImage(layer, sx, sy, sw, sh, sx, sy, sw, sh)
  }
  // Couches ANIMÉES du terrain (hors cache) : lave/feu (bloom), tuiles mécaniques, eau — culées visible.
  const wbg = waterBgMask(cur)
  const teleC = wbg ? teleporterColorMap(cur.teleporters) : null
  const PB = BIOMES[world]                       // la palette de papier du monde
  for (let row = row0; row < row1; row++) {
    for (let col = col0; col < col1; col++) {
      const cell = row * cur.cols + col
      const tile = cur.terrain[cell] ?? 0
      // FOND D'EAU : la nappe se dessine sous les tuiles non-solides posées dans un bassin
      // (pièce = case vide, échelle, téléporteur, corde...), puis la tuile statique est
      // redessinée PAR-DESSUS l'eau (le cache terrain est déjà blitté dessous, l'eau l'a
      // recouverte). Les tuiles animées (cristal de dash) se redessinent d'elles-mêmes après.
      if (wbg && wbg[cell] && tile !== T.Water && !isSolid(tile)) {
        // REFONTE (29/07) : la nappe et la tuile qu'elle porte étaient les deux
        // derniers blits de l'ancienne feuille SUR l'aire de jeu — d'où l'échelle
        // jaune pixel posée dans un carré de bleu uni au milieu du papier.
        const surface = row === 0 || !wbg[cell - cur.cols]
        tuilePapier(ctx, col * TS, row * TS, TS, 'Water', PB, !surface)
        drawWaterAnim(ctx, col * TS, row * TS, cur.tick + alpha, surface)
        const nomT = tile === T.Teleporter ? `Teleporter${teleC!.get(cell) ?? 0}` : T[tile]
        if (nomT && tile !== T.Empty && !ANIM_TILES.has(tile)) tuilePapier(ctx, col * TS, row * TS, TS, nomT, PB)
      }
      if (tile === T.Lava) drawBloom(ctx, col * TS, row * TS, '255,90,40', 30, cur.tick + alpha) // lave qui rougeoie
      else if (tile === T.Fire) drawBloom(ctx, col * TS, row * TS, '255,130,40', 26, cur.tick + alpha) // feu qui rougeoie
      if (ANIM_TILES.has(tile)) {
        barrierCloseWarn = cur.buttonLinger > cur.tick && cur.buttonLinger - cur.tick <= 45 // fin de grâce du circuit
        drawTileAnim(ctx, tile, col * TS, row * TS, cur.tick + alpha) // tuiles animées (mécanique/danger)
        // surface de LAVE (pop 22/07) : crête jaune + dômes de bulles, seulement à la première case
        if (tile === T.Lava && (row === 0 || (cur.terrain[cell - cur.cols] ?? 0) !== T.Lava)) drawLavaSurfaceAnim(ctx, col * TS, row * TS, cur.tick + alpha)
      }
      else if (tile === T.Water) drawWaterAnim(ctx, col * TS, row * TS, cur.tick + alpha, row === 0 || !(wbg && wbg[cell - cur.cols])) // eau vivante (surface + reflets alignés sur la nappe complète)
      // Téléporteur : halo/décompte par-dessus la tuile. Hors ANIM_TILES à dessein — l'appartenance
      // à ce set fait sauter le re-blit de la tuile statique dans un bassin (cf. la branche wbg).
      if (tile === T.Teleporter) drawTeleporterState(ctx, cur, col, row, cur.tick + alpha)
    }
  }
  // 1bis. couche DÉCO (cosmétique) : PAR-DESSUS le terrain, SOUS les entités. Code 0 = vide.
  //       Les décos de PREMIER PLAN (cascades) sont dessinées plus tard (drawFrontDecos, après
  //       les entités) : on les saute ici pour ne pas doubler le rideau.
  //       Une grille vide n'exécute aucun blit -> rendu identique aux cartes sans décoration.
  for (let cell = 0; cell < decor.length; cell++) {
    const code = decor[cell] ?? 0
    if (code === 0 || DECO_FRONT_CODES.has(code)) continue // cascades : rendues APRÈS les entités (drawFrontDecos)
    const dcol0 = colOf(cell, cur.cols), drow0 = rowOf(cell, cur.cols)
    // REFONTE « papier découpé » (lot 5b) : la déco est dessinée, pas blittée.
    // Elle ne porte AUCUNE ombre portée — c'est ce qui la colle au fond pendant
    // que le terrain et les personnages s'en décollent, et c'est la règle qui
    // empêche une cascade de redevenir plus visible que le héros.
    if (blitDeco(ctx, code, TS, dcol0 * TS, drow0 * TS)) continue
    // Variantes désert (audit 22/07) : chaîne bronze, stalagmite grès, cristal ambre
    const ref = (world === 'desert' ? DESERT_DECO_BY_CODE[code] : undefined) ?? DECO_SPRITES_BY_CODE[code]
    if (!ref) continue
    const dcol = dcol0, drow = drow0
    // OMBRE DE CONTACT (21/07, « les décos ne s'intègrent pas ») : ellipse sombre douce à la base,
    // UNIQUEMENT si la déco REPOSE sur un solide -> ancre les décos de sol, épargne les murales
    // (torche/bannière/lanterne accrochées : rien de solide dessous). Cosmétique, sous la déco.
    const onGround = isSolid(cur.terrain[(drow + 1) * cur.cols + dcol] ?? T.Empty)
    if (onGround) {
      const scx = dcol * TS + TS / 2, scy = drow * TS + TS - 3
      ctx.save()
      const sg = ctx.createRadialGradient(scx, scy, 0, scx, scy, TS * 0.4)
      sg.addColorStop(0, 'rgba(0,0,0,0.5)'); sg.addColorStop(0.5, 'rgba(0,0,0,0.28)'); sg.addColorStop(1, 'rgba(0,0,0,0)')
      ctx.fillStyle = sg; ctx.beginPath(); ctx.ellipse(scx, scy, TS * 0.4, TS * 0.12, 0, 0, Math.PI * 2); ctx.fill()
      ctx.restore()
      drawDecoGround(rc, ref, dcol * TS, drow * TS, world, code) // sol : réduite + fondue dans l'ambiance
    } else {
      blitWorld(rc, ref, dcol * TS, drow * TS) // murale (torche/bannière) : plein cadre, pas de fondu
    }
  }
  drawDecoAnim(rc, decor, cur.tick + alpha, cur.cols) // overlays animés (flamme torche/bougie, éclat valve), au tick
  drawBubblesAnim(rc, cur, cur.tick + alpha) // bulles montantes des sources (EAU 16/07 soir), au tick
  drawBigDecos(rc, decor, cur.tick + alpha, cur.cols) // grands décors (17/07) : ensembles multi-cases, sous les entités
  drawWorms(rc, cur, cur.tick + alpha, focusIdx) // vers checkpoint (16/07 soir) : 5 cases par ancre, sous les entités
  // 2. trous creusés — stage 1 (repousse imminente) clignote ouvert <-> repousse.
  //    AU BIOME (audit 16/07) : un trou est une brique éventrée, il prend donc les couleurs des
  //    briques du monde (avant : rouge classique dans la neige comme dans l'usine).
  for (const h of cur.holes) {
    const stage = regrowStage(cur, h)
    // REFONTE (29/07) : le trou se DESSINE. En pixel il venait de la feuille HD et
    // ressortait comme un bloc de brique rouge posé au milieu du papier — c'est le
    // défaut que Claude-Alain a entouré deux fois sur sa capture.
    const nomH = stage === 0 ? 'Hole'
      : stage === 1 ? (cur.tick % 8 < 4 ? 'Hole' : 'HoleRegrow')
        : 'HoleRegrow'
    const hx = colOf(h.cell, cur.cols) * TS, hy = rowOf(h.cell, cur.cols) * TS
    tuilePapier(ctx, hx, hy, TS, nomH, PB)
    // ÉCLATEMENT : les ~8 premiers ticks portent des éclats de papier qui s'écartent
    // du point d'impact — pur habillage, le trou est déjà ouvert dessous.
    const age = cur.tick - (h.regrowAt - REGROW_TICKS)
    if (age >= 0 && age < 8) {
      const k = age / 8
      ctx.save()
      ctx.globalAlpha = 1 - k
      ctx.fillStyle = PB.briqueHaut
      for (let e = 0; e < 7; e++) {
        const a = e * 0.9, d = 6 + k * 22
        ctx.fillRect(hx + TS / 2 + Math.cos(a) * d, hy + TS / 2 + Math.sin(a) * d * .7, 4 - k * 2, 4 - k * 2)
      }
      if (age < 2) { ctx.fillStyle = '#fff6d8'
        ctx.fillRect(hx + TS / 2 - 2, hy + 2, 4, 18); ctx.fillRect(hx + TS / 2 - 9, hy + 9, 18, 4) }
      ctx.restore()
    }
    // REBOUCHAGE EN COURS (31/07) : la terre remonte DEPUIS LE BAS du trou à mesure que le joueur
    // cogne. On le dessine par-dessus le trou, sous les entités : le captif s'enfonce visiblement.
    // C'est le seul retour qui dit « continue, tu y es presque » — sans lui, quarante-huit ticks
    // de touche maintenue ne montrent rien du tout.
    if (h.fill > 0) drawRebouchage(ctx, hx, hy, h.fill / FILL_TICKS, cur.tick + alpha)
  }
  // 3. or au sol — halo doux (bloom) puis la pièce par-dessus.
  for (const cell of cur.golds) { const gx = colOf(cell, cur.cols) * TS, gy = rowOf(cell, cur.cols) * TS; drawBloom(ctx, gx, gy, '255,205,70', 24, cur.tick + alpha); blitObjet(ctx, 'or', TS, gx, gy); drawGoldSparkle(ctx, gx, gy, cur.tick + alpha) }
  // 4. objets ramassables (sprite par nature : bombe / marteau / tazer) — léger halo pour les repérer.
  for (const it of cur.items) { const ix = colOf(it.cell, cur.cols) * TS, iy = rowOf(it.cell, cur.cols) * TS
    drawBloom(ctx, ix, iy, '255,220,130', 22, cur.tick + alpha)
    // REFONTE (lot 6) : ce qui se ramasse porte une ombre portée franche et une
    // teinte vive — ce sont les objets les plus importants après le héros.
    blitObjet(ctx, it.kind, TS, ix, iy) }
  // 4bis. APPÂTS POSÉS (31/07) : ils ne sont plus ramassables, mais ils doivent se voir de loin —
  // c'est autour d'eux que se joue le piège. Halo chaud plus appuyé que les objets, et un
  // battement lent : ça respire, donc ça attire l'oeil comme ça attire les moines.
  for (const cell of cur.appats) {
    const ax = colOf(cell, cur.cols) * TS, ay = rowOf(cell, cur.cols) * TS
    drawBloom(ctx, ax, ay, '255,150,60', 26, cur.tick + alpha)
    const bob = Math.sin((cur.tick + alpha) * 0.12) * 2
    blitObjet(ctx, 'appat', TS, ax, ay + bob)
  }
  // 5. bombes amorcées — clignotent (mèche allumée) au même rythme que les trous.
  // REFONTE (29/07) : la bombe papier existait déjà (lot 6, `blitObjet`) mais seuls
  // les objets AU SOL y étaient branchés ; la bombe AMORCÉE était restée en pixel.
  // La mèche allumée se dit maintenant par un halo qui bat, pas par un second sprite.
  for (const b of cur.bombs) {
    const bx = colOf(b.cell, cur.cols) * TS, by = rowOf(b.cell, cur.cols) * TS
    if (cur.tick % 8 < 4) drawBloom(ctx, bx, by, '255,120,40', 26, cur.tick + alpha)
    blitObjet(ctx, 'bomb', TS, bx, by)
  }
  // 5ter. caisses poussables (Phase 4, #2), SOUS les entités. Contenu CACHÉ en jeu (bois/métal).
  //        X LISSÉE (glisse en ease-out vers la position moteur -> poussée fluide, plus de saut-de-case
  //        visible) ; Y interpolée (chute nette). Resync au reset de tick (nouveau niveau/partie) ou
  //        au changement de nombre (casse) -> pas de glissement fantôme entre deux états.
  if (cur.tick < crateDispTick || crateDispX.length !== cur.crates.length) crateDispX = cur.crates.map((c) => c.x)
  crateDispTick = cur.tick
  for (let i = 0; i < cur.crates.length; i++) {
    const c = cur.crates[i]!
    const dx = c.x - crateDispX[i]!
    // saut > 1 case (resync/anomalie) OU < 0.5 sous-unité (anti-résidu) : snap ; sinon glisse (ease-out).
    crateDispX[i] = (Math.abs(dx) > SUB * 1.5 || Math.abs(dx) < 0.5) ? c.x : crateDispX[i]! + dx * CRATE_EASE
    const pc = prev && prev.crates.length === cur.crates.length ? prev.crates[i]! : null
    const cy = pc && Math.abs(c.y - pc.y) <= SUB ? pc.y * (1 - alpha) + c.y * alpha : c.y
    // ROCHER (17/07) : frame par DISTANCE parcourue (x>>2 : change toutes les 4 su) — il
    // tourne en roulant, immobile au repos. Bois/métal : sprite fixe comme avant.
    // REFONTE (29/07) : caisses et rocher en papier (lot 6, jamais branché ici).
    // Le rocher garde sa rotation par distance : elle est portée par le dessin
    // (un quartier plus clair qui tourne), donc une seule pièce suffit.
    blitObjet(ctx, c.kind === 'boulder' ? 'rocher' : c.kind === 'wood' ? 'caisse-bois' : 'caisse-metal',
      TS, toPx(crateDispX[i]!), toPx(cy))
  }
  // 5bis-a. PÉNOMBRE (22/07) : voile sombre sur les zones abritées, AVANT les halos — les flammes
  //         rallument par-dessus. Entités dessinées APRÈS : le coureur reste net même dans l'ombre.
  const shMask = updateShelter(cur, world)
  if (shMask && veilCanvas) {
    ctx.save()
    ctx.globalAlpha = veilAlpha(world)
    ctx.drawImage(veilCanvas, VEIL_PAD, VEIL_PAD, cur.cols * TS, cur.rows * TS, 0, 0, cur.cols * TS, cur.rows * TS)
    ctx.restore()
  }
  // 5bis. torches décoratives : halo de lumière ROND (3 blocs) façon feu vivant, PAR-DESSUS le sol/or
  //       mais SOUS les entités (elles restent nettes). Cosmétique, au tick, culé au champ caméra.
  drawLightHalos(rc, decor, cur.tick + alpha, cur.cols, cam, world, shMask)
  // 6. entités : moines d'abord, coureurs au-dessus (le joueur reste visible). `near` (le moine
  //    nargue) = un coureur VIVANT est à ≤ 2 cases (Chebyshev) du moine, dérivé des positions
  //    courantes en lecture seule (cosmétique, zéro impact simulation ; aucun champ ajouté).
  const TAUNT_SUB = 2 * SUB // portée de narquerie : 2 cases exprimées en sous-unités
  cur.monks.forEach((m, i) => {
    const pm = prev?.monks[i]
    const near = m.respawnAt === 0 && cur.runners.some(
      (r) => r.alive && Math.abs(m.x - r.x) <= TAUNT_SUB && Math.abs(m.y - r.y) <= TAUNT_SUB,
    )
    // ÉMOTIONS (17/07) : réservées à l'IA MALIGNE (sans elle, ni ligne de vue ni mémoire : un
    // signe mentirait) et aux moines « disponibles » (ni piégé, ni étourdi, ni statue, ni en
    // chantier — ces états ont déjà leur langage visuel). monkMood est PUR : même affichage
    // sur les 3 écrans d'un multi.
    const mood = cur.smartMonks && m.respawnAt === 0 && !m.dormant && m.trappedCell < 0
      && m.stunUntil <= cur.tick && m.work === 0 ? monkMood(cur, m) : null
    // Le captif PANIQUE quand le trou qui le retient est en cours de rebouchage : c'est le seul
    // signe, côté moine, que les coups de pioche du joueur portent.
    const panique = m.trappedCell >= 0 && cur.holes.some((h) => h.cell === m.trappedCell && h.fill > 0)
    drawMonk(rc, pm && pm.respawnAt === 0 ? pm : null, m, alpha, cur.tick, near, mood, panique)
  })
  // TOILE DU TRAMPOLINE (18/07, variantes du saut) : 6 ticks de déformation après un rebond —
  // V enfoncé (ticks 0-2, au plus creux à 1) puis bombé catapulte (3-5). Tracé procédural neutre
  // (marche sur les 6 tuiles trampoline, normales/directionnelles/puissantes). Le pad se retrouve
  // en remontant la position du coureur de ce qu'il a gagné depuis l'événement (2 su/tick).
  cur.runners.forEach((r, i) => {
    const [tAt, tTotal] = bounces[i] ?? [0, 0]
    const el = cur.tick - tAt
    if (tAt <= 0 || tTotal < 24 || el < 0 || el >= 6 || !r.alive) return
    const x = Math.round(r.x / SUB) * TS
    const y = (Math.floor((r.y + 2 * el) / SUB) + 1) * TS
    ctx.save()
    ctx.lineWidth = 3
    if (el < 3) {
      const d = el === 1 ? 11 : 7
      ctx.strokeStyle = 'rgba(13, 11, 26, 0.55)'
      ctx.beginPath(); ctx.moveTo(x + 3, y + 4); ctx.lineTo(x + TS / 2, y + 4 + d); ctx.lineTo(x + TS - 3, y + 4); ctx.stroke()
    } else {
      const d = el === 3 ? 7 : 3
      ctx.strokeStyle = 'rgba(255, 214, 224, 0.85)'
      ctx.beginPath(); ctx.moveTo(x + 3, y + 4); ctx.quadraticCurveTo(x + TS / 2, y + 4 - d * 2, x + TS - 3, y + 4); ctx.stroke()
    }
    ctx.restore()
  })
  cur.fishes.forEach((f, i) => drawFish(rc, prev?.fishes[i] ?? null, f, alpha, cur.tick)) // ennemis aquatiques (16/07 soir)
  cur.drapeaux.forEach((d, i) => drawDrapeau(rc, prev?.drapeaux[i] ?? null, d, alpha, cur.tick)) // DRAPEAU-CRAPAUD (05/08)
  // MAILLETS AU SOL + PIOCHES PLANTÉES (05/08, objet versus n°4). Le maillet brille comme un
  // armement à prendre ; chaque pioche plantée porte le foulard de son propriétaire — on sait
  // toujours où est le maillet, et à qui est la pioche qui l'attend.
  for (const cell of cur.maillets) {
    const x = colOf(cell, cur.cols) * TS, y = rowOf(cell, cur.cols) * TS
    drawBloom(ctx, x, y, '255,178,61', TS * 0.55, cur.tick)
    blitObjet(ctx, 'maillet', TS, x, y)
  }
  for (const p of cur.pioches) {
    const look = runnerLooks?.[p.owner]
    const hueP = look ? look.hue : p.owner === focusIdx ? hueIndex(avatarHue()) : 0
    drawPiochePlantee(ctx, colOf(p.cell, cur.cols) * TS, rowOf(p.cell, cur.cols) * TS, hueP, cur.tick)
  }
  // TOILES DE BANDELETTES (06/08) : deux cases, tendues entre leurs ancrages. Le poseur la voit
  // pleine (il sait où il l'a mise), les autres à demi-teinte — l'œil attentif est récompensé.
  // Tant qu'elle n'est pas ARMÉE, elle scintille : son poseur peut encore s'écarter.
  for (const b of cur.bandelettes) {
    const c1 = Math.min(b.cell, b.cell2), c2 = Math.max(b.cell, b.cell2)
    const x = colOf(c1, cur.cols) * TS, y = rowOf(c1, cur.cols) * TS
    const w = (colOf(c2, cur.cols) - colOf(c1, cur.cols) + 1) * TS
    ctx.save()
    if (b.owner !== focusIdx) ctx.globalAlpha = 0.5
    toilePapier(ctx, x, y, w, TS, cur.tick + alpha, TOILE_CHOISIE, cur.tick >= b.armeAt)
    ctx.restore()
  }
  // COURONNES AU SOL (05/08) : elles brillent, on sait toujours où est la tentation.
  for (const cell of cur.couronnes) {
    const x = colOf(cell, cur.cols) * TS, y = rowOf(cell, cur.cols) * TS
    drawBloom(ctx, x, y, '255,198,31', TS * 0.7, cur.tick)
    blitObjet(ctx, 'couronne', TS, x, y)
  }
  // PATATES CHAUDES (05/08) : au sol ou sur le dos de leur porteur (la sim recopie sa position).
  for (let i = 0; i < cur.patates.length; i++) {
    const p = cur.patates[i]!
    const pp = lerpPos(prev?.patates[i] ?? null, p, alpha)
    const depuis = p.allumee ? cur.tick - (p.boomAt - PATATE_MIN - PATATE_JIT) : 0
    drawPatate(ctx, pp.px, p.porteur >= 0 ? pp.py - TS * 0.42 : pp.py, cur.tick + alpha, p.allumee, depuis)
  }
  drawDashFxBack(ctx, cur.tick + alpha) // dash 3 variantes : queue de comète, échos, ondes de départ (SOUS les coureurs)
  // FANTÔME DU RECORDMAN (25/07) : dessiné AVANT les vrais coureurs (donc dessous : toi d'abord,
  // lui en repère), translucide et dans une teinte à part. Il vient d'une SECONDE partie rejouée
  // (client/ghost.ts) : rien ici ne lit ni n'écrit l'état de la partie en cours. L'index de FX
  // GHOST_FX_IDX lui donne son propre emplacement dans le magasin d'effets de dash — sans quoi il
  // écraserait la traînée du joueur, qui occupe les index 0 à 2.
  if (ghost?.cur) {
    ctx.save()
    ctx.globalAlpha = GHOST_ALPHA
    drawRunner(rc, ghost.prev, ghost.cur, GHOST_FX_IDX, alpha, cur.tick, 0, GHOST_HUE_IDX, undefined, '', 0, '', estImmerge(cur, ghost.cur))
    ctx.restore()
  }
  cur.runners.forEach((r, i) => {
    if (!r.alive || masqueCoureurs) return
    const pr = prev?.runners[i]
    const digLeft = Math.max(0, (digUntil[i] ?? 0) - cur.tick) // ticks restants du geste de creusage (0 = aucun)
    // Bouclier de réanimation (16/07 soir) : le ressuscité CLIGNOTE tant qu'il est intouchable
    // (lisible : « profites-en pour t'écarter ») — pur rendu, dérivé du tick.
    const invuln = r.invulnUntil > cur.tick
    if (invuln) ctx.globalAlpha = 0.45 + 0.35 * Math.sin((cur.tick + alpha) * 0.7)
    // LE FANTÔME DE PAPIER (05/08, objet versus n°9) : rendu LOCAL pur — la simulation ne change
    // pas d'un bit (les moines te voient et te tuent, le tazer te touche). Chez les ADVERSAIRES,
    // le corps disparaît ; chez soi, on se voit translucide à 12 % pour garder le contrôle.
    // Fin de l'effet : réapparition en FONDU d'une demi-seconde, jamais un pouf — on ne punit
    // pas la position de celui qui n'a rien demandé.
    // ⚠️ Cette boucle ne s'appuie PAS sur save/restore pour l'alpha : elle l'écrit et le remet à
    // 1 à la fin (cf. `invuln`). On suit la même convention — un restore() sans save() dépilerait
    // une couche du canvas (la leçon des cascades, 04/08).
    const fEcart = (cur.tick + alpha) - r.fantomeUntil
    const fPlancher = i === focusIdx ? 0.12 : 0               // chez soi : une ombre de soi-même ; ailleurs : rien
    let fAlpha = 1
    if (r.fantomeUntil > 0 && fEcart < FANTOME_FONDU) {
      fAlpha = fEcart < 0 ? fPlancher : fPlancher + (1 - fPlancher) * (fEcart / FANTOME_FONDU)
    }
    if (fAlpha <= 0) { ctx.globalAlpha = 1; return }           // invisible chez les adversaires
    if (fAlpha < 1) ctx.globalAlpha = (invuln ? ctx.globalAlpha : 1) * fAlpha
    // TEINTE de tenue PAR coureur (multi) : Le Baroudeur recolore la SEULE veste ocre (tête/casque/
    // peau/écharpe intacts), donc des joueurs de couleurs différentes restent distincts sans casser le
    // perso — ce qui avait justifié le gel de l'ocre. En ligne : la teinte (déjà dédoublonnée anti-
    // jumeaux par dedupLooks) vient de runnerLooks[i].hue. En solo : le coureur suivi prend la
    // préférence locale (avatarHue), les autres gardent l'ocre d'origine (index 0).
    const look = runnerLooks?.[i]
    const hueIdx = look ? look.hue : i === focusIdx ? hueIndex(avatarHue()) : 0
    // TENUE (26/07) : même règle que la teinte et le familier — en ligne elle vient du serveur
    // (apparence du joueur), en solo c'est la tenue équipée du coureur suivi. Les autres coureurs
    // sans apparence gardent Le Baroudeur d'origine.
    const outfitId = look ? look.outfit ?? '' : i === focusIdx ? equipped('outfit') ?? '' : ''
    const hatId = look ? look.hat ?? '' : i === focusIdx ? equipped('hat') ?? '' : ''
    // EMOTE (26/07) : elle n'appartient qu'au coureur LOCAL (elle est déclenchée par ses touches et
    // ne voyage pas sur le réseau). Le corps penche autour de ses PIEDS et rebondit ; l'animation
    // étant une fonction pure du temps, elle revient d'elle-même au repos.
    const emo = i === focusIdx ? currentEmote(nowMsForEmote()) : null
    if (emo && emo.frame.tilt !== 0) {
      const { px: epx, py: epy } = lerpPos(pr && pr.alive ? pr : null, r, alpha)
      ctx.save()
      ctx.translate(epx + TS / 2, epy + TS)
      ctx.rotate(emo.frame.tilt)
      ctx.translate(-(epx + TS / 2), -(epy + TS))
    }
    drawRunner(rc, pr && pr.alive ? pr : null, r, i, alpha, cur.tick, digLeft, hueIdx, bounces[i], outfitId, emo ? emo.frame.bob * TS : 0, hatId, estImmerge(cur, r))
    // LE MODE D'EMPLOI (06/08) : seulement au-dessus de SON cocon — un adversaire n'a pas
    // besoin qu'on lui explique comment TU t'en sors. Dessiné ici, où l'on sait qui est local.
    if (r.momieUntil > cur.tick && i === focusIdx) {
      const pm = lerpPos(pr ?? null, r, alpha)
      drawMessageMomie(ctx, pm.px, pm.py, cur.tick + alpha, r.momieCoups, r.momieCote, MOMIE_COUPS)
    }
    // L'ÉCHANGE D'OUTIL (06/08) : une seconde de cérémonie au-dessus du coureur concerné, sur
    // TOUS les écrans (voir un adversaire prendre le maillet est une information capitale).
    const sw = swapAt[i]
    if (sw && cur.tick - sw.at < SWAP_TICKS) {
      const ps = lerpPos(pr ?? null, r, alpha)
      drawEchangeOutil(ctx, ps.px, ps.py, cur.tick + alpha - sw.at, sw.versMaillet)
    }
    if (emo && emo.frame.tilt !== 0) ctx.restore()
    if (invuln || fAlpha < 1) ctx.globalAlpha = 1
  })
  // La bulle de l'emote : au-dessus de la tête du coureur local, jamais penchée avec le corps.
  {
    const moi = cur.runners[focusIdx]
    if (moi && moi.alive && !masqueCoureurs) {
      const p = lerpPos(prev?.runners[focusIdx] ?? null, moi, alpha)
      drawEmoteBubble(ctx, p.px, p.py, TS, nowMsForEmote())
    }
  }
  drawDashFxFront(ctx, cur.tick + alpha) // dash 3 variantes : foudre + runes, arcs, étoiles, ondes d'arrivée (PAR-DESSUS)
  // FAMILIER (Lot C : T6 solo -> C7 multi) : chat / canari cosmétique qui TROTTE derrière CHAQUE
  // coureur vivant. SOLO (runnerLooks === null) : seul le coureur suivi (focusIdx) a le pet des prefs
  // locales — comportement fonctionnellement INCHANGÉ. EN LIGNE : le pet de chaque joueur vient de
  // son runnerLooks[i].pet (équipé, transmis par le serveur). Hors sim/hash — lecture seule des
  // positions écran, aucun champ ajouté à l'état. Le pet rejoue la trace du coureur avec un retard EN
  // TICKS (pushPetHistory/petLaggedPos PAR index) -> il trotte derrière ; l'historique se resette seul
  // sur nouveau niveau / veille / téléport, et le reset explicite (prev === null) couvre le 1er frame
  // et le changement de contexte (solo↔multi). Dessiné À CÔTÉ de drawRunner (jamais dedans).
  if (prev === null) resetPetHistory()
  cur.runners.forEach((r, i) => {
    if (masqueCoureurs) return
    if (r.deguiseUntil > cur.tick) return // DÉGUISEMENT (05/08) : le pet disparaît avec son maître — un compagnon trahirait le faux moine
    if (r.momieUntil > cur.tick) return // MOMIFIÉ (05/08) : le compagnon se cache aussi, il n'y a plus qu'un cocon
    const petId = runnerLooks ? runnerLooks[i]?.pet ?? null : i === focusIdx ? equipped('pet') : null
    if (!petDessinable(petId)) return // les treize du catalogue (cf. petDessinable : papier, HD, ou 16 px)
    // coureur MORT : le pet reste dessiné (il accourt et VEILLE le corps — updateAndDrawPet gère)
    const pr = prev?.runners[i]
    const p = lerpPos(pr && pr.alive && r.alive ? pr : null, r, alpha)
    // la chouette « utile » : direction (px signés) de l'or le plus proche, qu'elle guette en oisiveté
    let goldDx: number | null = null
    if (petId === 'pet-chouette' && cur.golds.length > 0) {
      let best = Infinity
      for (const cell of cur.golds) {
        const gx = colOf(cell, cur.cols) * TS, gy = rowOf(cell, cur.cols) * TS
        const dd = Math.abs(gx - p.px) + Math.abs(gy - p.py)
        if (dd < best) { best = dd; goldDx = gx - p.px }
      }
    }
    // moine ACTIF (ni piégé ni en réapparition) à moins de 4 cases : le compagnon se met en alerte
    let monkDx: number | null = null
    if (r.alive) {
      let bestM = 4 * TS
      for (const m of cur.monks) {
        if (m.respawnAt !== 0 || m.trappedCell >= 0) continue
        const mx = m.x * (TS / 8), my = m.y * (TS / 8)
        const dd = Math.hypot(mx - p.px, my - p.py)
        if (dd < bestM) { bestM = dd; monkDx = mx - p.px }
      }
    }
    const moving = pr ? pr.x !== r.x || pr.y !== r.y : false
    updateAndDrawPet(rc, i, petId, p.px, p.py, r.face, cur.tick, alpha, {
      alive: r.alive,
      events: cur.events,
      goldDx, monkDx,
      runnerMoving: moving,
      cellNear: (cell) => Math.abs(colOf(cell, cur.cols) * TS - p.px) + Math.abs(rowOf(cell, cur.cols) * TS - p.py) < 1.3 * TS,
      solidAt: (wx, wy) => {
        const c = Math.floor(wx / TS), rw = Math.floor(wy / TS)
        return c >= 0 && rw >= 0 && c < cur.cols && rw < cur.rows && isSolid(cur.terrain[rw * cur.cols + c] ?? T.Empty)
      },
    })
  })
  // Bulles de nage (EAU 16/07) : un coureur immergé (breath < plein ⟺ sous l'eau, même astuce que
  // la jauge) émet un chapelet de bulles qui montent — cosmétique, piloté au tick, déphasé par coureur.
  // Langage CARTOON POP (raccord bulles/eau 22/07) : anneau blanc franc + cœur bleu clair, comme
  // les bulles des sources (drawBubblesAnim) et les crêtes blanches de l'eau.
  cur.runners.forEach((r, i) => {
    if (!r.alive || r.breath >= BREATH_TICKS) return
    const p = lerpPos(prev?.runners[i] ?? null, r, alpha)
    ctx.save()
    ctx.lineWidth = 1.8
    for (let k = 0; k < 3; k++) {
      const ph = (cur.tick + alpha) * (0.025 + k * 0.008) + i * 0.7 + k * 0.37
      const frac = ph % 1
      const bx = p.px + TS * 0.62 + Math.sin(ph * 9) * 3
      const by = p.py + TS * 0.25 - frac * TS * 0.9
      const rr = 2.0 + k * 0.9
      ctx.globalAlpha = 0.9 * (1 - frac)
      ctx.fillStyle = 'rgba(124,208,255,0.45)'
      ctx.beginPath(); ctx.arc(bx, by, rr, 0, Math.PI * 2); ctx.fill()
      ctx.strokeStyle = 'rgba(255,255,255,0.95)'
      ctx.beginPath(); ctx.arc(bx, by, rr, 0, Math.PI * 2); ctx.stroke()
    }
    ctx.restore()
  })
  drawParticles(ctx, prev, cur, cur.tick + alpha, focusIdx) // particules d'action (poussière/étincelles/gravats), espace monde ; focusIdx = coureur local (traînée cosmétique)
  drawFrontDecos(rc, decor, cur.tick + alpha, cur.cols, cur.terrain) // cascades : ruban pop DEVANT le coureur, raccord d'impact sur la tuile dessous
  ctx.restore() // fin de la couche monde : le HUD (renderHud) se dessine ensuite SANS décalage caméra
  // Options client (opt-in, lues dans localStorage) : torche puis filtre CRT, limitées à l'aire de
  // jeu (le HUD garde sa netteté). fp/cam sont les position/décalage caméra déjà calculés ci-dessus.
  if (torchPref()) drawTorchLight(ctx, (fp.px + TS / 2 - cam.x) * ZOOM, (fp.py + TS / 2 - cam.y) * ZOOM)
  if (crtPref()) drawCrt(ctx, COLS * TS, ROWS * TS)
  return { camX: cam.x, camY: cam.y }
}

/** Un éclair de tir tazer en attente d'affichage (transitoire). `from`/`to` = cases (index). */
export interface TazeFlash { from: number; to: number; bornAt: number }
/** Durée de vie d'un éclair de tazer (ms) : au-delà, l'appelant le purge de sa liste. */
export const FLASH_MS = 200

// Éclairs de tir (event 'taze') : segment vif du centre de `from` (tireur) au centre de
// `to` (impact), qui s'estompe sur FLASH_MS. Double trait : halo cyan glace dessous, cœur
// jaune vif dessus. L'appelant maintient la liste (remplie sur l'event, purgée > FLASH_MS)
// et l'affiche APRÈS le rendu du terrain et des entités.
/**
 * La position ÉCRAN (pixels du canvas, zoom et caméra compris) des pieds d'un coureur.
 * Sert à l'entrée en scène : l'animation doit arriver là où le héros va démarrer, sur
 * n'importe quelle carte. Le calcul est le même que celui de la couche monde — zoom d'abord,
 * caméra ensuite — et il est ici pour n'exister qu'à un seul endroit.
 */
export function posEcranCoureur(
  r: { x: number; y: number }, cam: { camX: number; camY: number },
): { cx: number; ySol: number } {
  const Z = cameraZoom()
  return { cx: (toPx(r.x) - cam.camX + TS / 2) * Z, ySol: (toPx(r.y) - cam.camY + TS) * Z }
}

export function drawTazeFlashes(
  rc: RenderCtx, flashes: readonly TazeFlash[], now: number,
  cols: number = COLS, cam: { camX: number; camY: number } = { camX: 0, camY: 0 },
): void {
  const { ctx } = rc
  const ZOOM = cameraZoom() // même pref « Zoom caméra » que la couche monde
  ctx.save()
  ctx.scale(ZOOM, ZOOM) // même zoom que la couche monde (sinon éclairs décalés / mauvaise échelle)
  ctx.translate(-cam.camX, -cam.camY) // même décalage caméra que la couche monde (Lot E)
  for (const f of flashes) {
    const age = now - f.bornAt
    if (age < 0 || age > FLASH_MS) continue
    const k = 1 - age / FLASH_MS // 1 (frais) -> 0 (éteint)
    const x0 = colOf(f.from, cols) * TS + TS / 2, y0 = rowOf(f.from, cols) * TS + TS / 2
    const x1 = colOf(f.to, cols) * TS + TS / 2, y1 = rowOf(f.to, cols) * TS + TS / 2
    ctx.save()
    ctx.globalAlpha = k
    ctx.lineCap = 'round'
    ctx.strokeStyle = '#9fd8f5'          // halo cyan glace
    ctx.lineWidth = 3 + 7 * k
    ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke()
    ctx.strokeStyle = '#ffe36b'          // cœur jaune vif
    ctx.lineWidth = 1 + 3 * k
    ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke()
    ctx.restore()
  }
  ctx.restore()
}

export function renderHud(rc: RenderCtx, s: GameState, hud: HudInfo): void {
  const { ctx } = rc
  const top = ROWS * TS
  ctx.fillStyle = HUD_BG
  ctx.fillRect(0, top, COLS * TS, HUD_H)

  // ⚠️ `orRamasse`, PAS `totalGold - golds.length` : `golds` ne contient que l'or AU SOL, donc une
  // pièce volée par un moine (ou encore enfermée dans une caisse) était comptée comme ramassée.
  // Signalé par Claude-Alain. La règle vit dans shared/state.ts, avec celle qui révèle la sortie.
  const collected = orRamasse(s)
  const timeStr = formatTime(hud.timeMs) // chrono mm:ss.cc (Lot A), partage avec les classements
  const hearts = '♥'.repeat(Math.max(0, hud.lives))
  // Cles du coureur local (compteur, tuiles P3 T2) : n'apparait que s'il en porte -> zero impact
  // sur les cartes sans cle et sur le layout historique du bandeau.
  const keys0 = s.runners[0]?.keys ?? 0
  const keyTxt = keys0 > 0 ? `     CLÉ ${keys0}` : ''

  ctx.fillStyle = HUD_FG
  ctx.font = '24px ui-monospace, Menlo, monospace'
  ctx.textBaseline = 'middle'
  const midY = top + HUD_H / 2
  ctx.textAlign = 'left'
  const leftTxt = `OR ${collected}/${s.totalGold}     VIES ${hearts}     ${timeStr}${keyTxt}`
  ctx.fillText(leftTxt, 16, midY)

  // JAUGE DE DASH (31/07, demande Claude-Alain : « une jauge qui affiche quand le CD du dash est
  // ok, au lieu du texte vert ou gris »). Le texte ne disait que prêt/pas prêt ; la jauge montre
  // COMBIEN il reste, donc on sait s'il faut attendre une seconde ou sept.
  //
  // ⚠️ LE PIÈGE DU BANDEAU, déjà payé une fois (« Phare à on! », 22/07, cf. plus bas) : tout se
  // suit sur un seul curseur x. L'ancien bloc avançait de la largeur de son TEXTE ; une jauge n'en
  // a pas. Il faut donc avancer de sa largeur en PIXELS, sinon OBJET, checkpoint et « [M] son »
  // se peignent par-dessus.
  let cursorX = 16 + ctx.measureText(leftTxt).width
  if (s.dashEnabled) {
    const reste = Math.max(0, (s.runners[0]?.dashReadyAt ?? 0) - s.tick)
    const p = 1 - Math.min(1, reste / DASH_CD_TICKS)   // 0 = vide, 1 = prêt
    const pret = reste === 0
    const lab = '   DASH '
    ctx.fillStyle = pret ? '#5bd75b' : '#7a7599'
    ctx.fillText(lab, cursorX, midY)
    const gx = cursorX + ctx.measureText(lab).width
    const GW = 92, GH = 14, gy = midY - GH / 2
    // Le fossé, puis le remplissage, puis le cadre : le cadre passe en dernier pour rester net.
    ctx.fillStyle = '#100d22'
    ctx.fillRect(gx, gy, GW, GH)
    ctx.fillStyle = pret ? '#5bd75b' : '#c9a227'       // ocre pendant la charge, vert une fois prêt
    ctx.fillRect(gx + 2, gy + 2, Math.round((GW - 4) * p), GH - 4)
    // Prêt : un liseré clair qui respire, pour que l'œil l'attrape sans lire.
    if (pret) {
      ctx.strokeStyle = rgbaHex('#b6ffb6', 0.35 + ((Math.sin(s.tick * 0.2) + 1) / 2) * 0.45)
      ctx.lineWidth = 2
      ctx.strokeRect(gx + 1, gy + 1, GW - 2, GH - 2)
    }
    ctx.strokeStyle = pret ? '#5bd75b' : '#4a4470'
    ctx.lineWidth = 1
    ctx.strokeRect(gx + 0.5, gy + 0.5, GW - 1, GH - 1)
    cursorX = gx + GW                                   // ⚠️ en PIXELS, pas en largeur de texte
    ctx.fillStyle = HUD_FG
  }
  // Objet porté du coureur local (16/07 soir) : affiché ICI, dans le bandeau — l'icône
  // au-dessus de la tête est retirée (le bouton tactile OBJET la porte déjà ; le bandeau
  // sert d'affichage clavier + rappel). Compteur = charges restantes (tazer/corde).
  if (hud.item) {
    const label = '   OBJET '
    ctx.fillText(label, cursorX, midY)
    cursorX += ctx.measureText(label).width
    blitObjet(ctx, hud.item.kind, 24, cursorX, midY - 12)
    if (hud.item.count > 0) {
      ctx.fillStyle = ACCENT
      ctx.fillText(` ${hud.item.count}`, cursorX + 24, midY)
      ctx.fillStyle = HUD_FG
    }
    cursorX += 24 + (hud.item.count > 0 ? ctx.measureText(` ${hud.item.count}`).width : 0)
  }
  // Checkpoint actif (16/07 soir) : la TÊTE du ver éveillé dans le bandeau — rappel discret
  // « tu as un point de réanimation ». Absent tant qu'aucun ver n'a été touché (layout intact).
  if (hud.checkpoint) {
    const w = WORM_WAKE[4]!
    ctx.drawImage(rc.sheet, w.sx, w.sy, 16, 16, cursorX + 12, midY - 12, 24, 24)
    cursorX += 12 + 24
  }

  // Indicateur de son : DANS le flux gauche (après dash/objet/checkpoint). Il vivait centré à
  // x fixe (COLS*TS/2) et le bloc gauche, allongé par DASH/OBJET, finissait dessus — textes
  // superposés illisibles (bug vu sur « Phare à on! », 22/07). En flux, la collision est
  // impossible par construction. Masqué en mode tactile (pas de clavier, [M] sans objet).
  if (!hud.touchMode) {
    ctx.fillStyle = hud.muted ? '#ffd93d' : '#7a7599'
    const sonTxt = hud.muted ? '   [M] son coupé' : '   [M] son actif'
    ctx.fillText(sonTxt, cursorX, midY)
    cursorX += ctx.measureText(sonTxt).width
  }
  // LE FANTÔME EN PISTE (26/07) : son pseudo et son temps, dans la teinte qui le dessine. On voyait
  // un coureur translucide sans savoir qui c'était — donc sans savoir ce qu'on était en train de
  // courir. Le temps affiché est CELUI DU FANTÔME (sa course réelle), pas le record du classement.
  // Le libellé se RÉDUIT selon la place qui reste avant le nom du niveau (collé à droite) : sans ça
  // il s'écrit dessus, constaté en capture le jour même. Voir client/hud-ghost.ts pour la règle.
  if (hud.ghost) {
    const dispo = COLS * TS - 16 - ctx.measureText(hud.levelName).width - HUD_GAP - cursorX
    const txt = ghostHudLabel(hud.ghost.pseudo, hud.ghost.timeMs, dispo, (t) => ctx.measureText(t).width)
    if (txt) {
      ctx.fillStyle = GHOST_HUD_COLOR
      ctx.fillText(txt, cursorX, midY)
    }
  }

  ctx.fillStyle = HUD_FG
  ctx.textAlign = 'right'
  ctx.fillText(hud.levelName, COLS * TS - 16, midY)

  // L'objet porté (tazer + ses charges) s'affiche désormais AU-DESSUS DE LA TÊTE du coureur, pour
  // TOUS les joueurs et tous les modes (cf. drawCarriedItem). L'ancien « TAZER n » du HUD, réservé
  // au joueur 0, est retiré : l'icône le remplace sans faire doublon.
  ctx.textAlign = 'left'
}
